import * as SIP from "sip.js";
import { MediaFlowDiagnostics } from "./media-flow-diagnostics";
import { observeSessionBye } from "./session-diagnostics";
import { RUNTIME_VERSION } from "./runtime-version";
import { outgoingFailureMessage, sipFailureEvidence, audioFailureMessage } from "./call-failure";
import { callIdentity, callEventIdentity, updateCallIdentity, incomingCustomerPhone, incomingCallContext } from "./call-identity";
import type { CallIdentity, SoftphoneCallOptions } from "./call-identity";
import { TabRpc, TAB_RPC_VERSION, OWNER_UNRESPONSIVE_MESSAGE } from "./tab-rpc";
import { CallSetup, setupTimeoutMessage, setupTimerTiming } from "./call-setup";
import { MediaCapture } from "./media-capture";
import { IncomingFavicon } from "./incoming-favicon";
import { CallControl } from "./call-control";
import { DiagnosticQueue, TransportDiagnostics, audioStats, diagnosticScope, peerDiagnosticState, playbackDiagnosticState } from "./diagnostics";
import { WidgetCredentialSync, newerCredentialVersion, newerWidgetCredential, widgetCredentialVersion, widgetCredentialBinding } from "./widget-credentials";

declare global {
  interface Window {
    DoCRMSoftphone?: Record<string, unknown>;
    webkitAudioContext?: typeof AudioContext;
    __DOCRMSoftphoneEmbed?: boolean;
    __DOCRMSoftphoneHeadless?: boolean;
  }
}

type AnyRecord = Record<string, any>;
type SoftphoneCall = AnyRecord & CallIdentity & { id: string };
type RuntimeConfig = AnyRecord & {
  testMode: boolean;
  authMode: string;
};

const headlessElements = new Map<string, HTMLElement>();
const headlessInputIds = new Set(["extension", "password", "number", "saveUser", "transferSearch"]);
const headlessSelectIds = new Set(["transferType", "callOutcomeMode", "audioInput", "audioOutput"]);

const $ = (id: string): any => window.__DOCRMSoftphoneHeadless
  ? headlessElement(id)
  : document.getElementById(id);

function headlessElement(id: string) {
  if (!window.__DOCRMSoftphoneHeadless) return null;

  const existing = headlessElements.get(id);
  if (existing) return existing;

  const element = id === "remoteAudio"
    ? document.createElement("audio")
    : headlessSelectIds.has(id)
      ? document.createElement("select")
      : headlessInputIds.has(id)
        ? document.createElement("input")
        : document.createElement("div");
  if (id === "remoteAudio") (element as HTMLAudioElement).autoplay = true;
  element.id = id;
  headlessElements.set(id, element);
  return element;
}

const runtimeQuerySelector = (selector: string) =>
  window.__DOCRMSoftphoneHeadless ? null : document.querySelector(selector);

const runtimeQuerySelectorAll = (selector: string) =>
  window.__DOCRMSoftphoneHeadless ? [] : Array.from(document.querySelectorAll(selector));

const callStateEl = $("callState");
const callStateTextEl = $("callStateText");
const headerStatusDotEl = $("headerStatusDot");
const accountLabelEl = $("accountLabel");
const extensionEl = $("extension");
const passwordEl = $("password");
const numberEl = $("number");
const remoteAudio = $("remoteAudio");
const widgetEl = $("softphoneWidget");
const eventLogEl = $("eventLog");
const toastEl = $("toast");
const saveUserEl = $("saveUser");
const transferTypeEl = $("transferType");
const callOutcomeModeEl = $("callOutcomeMode");
const audioInputEl = $("audioInput");
const audioOutputEl = $("audioOutput");
const audioInputButtonEl = $("audioInputButton");
const audioOutputButtonEl = $("audioOutputButton");
const audioInputTextEl = $("audioInputText");
const audioOutputTextEl = $("audioOutputText");
const audioInputOptionsEl = $("audioInputOptions");
const audioOutputOptionsEl = $("audioOutputOptions");
const outcomeModalEl = $("outcomeModal");
const outcomeSelectButtonEl = $("outcomeSelectButton");
const outcomeOptionsEl = $("outcomeOptions");
const outcomeSubmitEl = $("outcomeSubmit");

let userAgent;
let registerer;
let connectPromise;
let disconnectPromise;
let connectionGeneration = 0;
let registrationPromise;
let reconnectTimer;
let stopTransportMonitor: (() => void) | undefined;
let probeTransportNow: (() => void) | undefined;
let reconnectAttempt = 0;
let registrationConfigurationRestartPending = false;
let registrationConfigurationRestartTask;
let transportConnected = false;
let registrationReady = false;
let desiredConnection = false;
let registrationStandby = false;
let registrationLeaseName = "";
let registrationLeaseOwned = false;
let registrationLeaseRelease;
let registrationLeaseRetryTimer;
let ownershipYield: CallSetup | undefined;
let ownershipReservation: { target: string; until: number } | undefined;
let ownerRefreshTask: Promise<void> | undefined;
let resolveOwnerRefresh: (() => void) | undefined;
let controlClaimTask: Promise<void> | undefined;
let availabilityTask: Promise<void> | undefined;
let availabilityError = "";
let pageWasConnected = false;
let pageSuspended = false;
let pageExitTask: Promise<void> | undefined;
let tabCoordinationChannel: BroadcastChannel | undefined;
let tabCoordinationHeartbeatTimer;
let mirroredOwnerState;
let mirroredOwnerStateAt = 0;
let mirroredOwnerId = "";
let mirroredOwnerProtocol = 0;
const callSetups = new WeakMap<object, CallSetup>();
let session;
let consultSession;
let remoteAudioSession;
let remoteAudioPlaybackGeneration = 0;
let remoteAudioPlaybackTask: Promise<void> | undefined;
let audioOutputTask: Promise<void> = Promise.resolve();
let muted = false;
let held = false;
let toastTimer;
let ringtoneAudio;
let ringtoneContext;
let ringbackOscillator;
let ringbackGain;
let outgoingTryingTimer;
let callFailureTimer;
let callPhase = "idle";
let activePhone = "";
let activeCall;
let callStartedAt = 0;
let callTimerInterval;
let transferTargets = [];
let transferSearchQuery = "";
let runtimeConfig: RuntimeConfig = { testMode: false, authMode: "external" };
let acceptedParentOrigin = "";
let transferInProgress = false;
let transferFlow;
let targetPanelMode = "transfer";
let conferenceStatusTimer;
let callStatusTimer;
let callActionLock;
const calls = new Map<string, SoftphoneCall>();
const callControl = new CallControl();
let callStatusRequest: CallSetup | undefined;
let conferenceStatusRequest: CallSetup | undefined;
let selectedCallId = "";
let latestCallId = "";
let activeMediaCallId = "";
let waitingToneOscillator;
let waitingToneGain;
let waitingToneTimer;
let ringingCallId = "";
const pendingOutcomeCalls = [];
let activeOutcomeCall;
let selectedOutcome;
let notificationsEnabled = localStorage.getItem("softphoneNotifications") !== "off";
let microphonePermission: PermissionStatus | undefined;
const incomingAttentionPhones = new Map<string, string>();
const incomingFavicon = new IncomingFavicon();
const incomingNotifications = new Map<string, Notification>();
const notifiedIncomingCallIds = new Set<string>();
const INCOMING_DOCUMENT_TITLE = "🔔 Входящий звонок!";
let titleBeforeIncoming = "";
let lastAttentionTitle = "";
let titleFlashTimer;
let titleFlashActive = false;
const SAVED_USER_KEY = "softphoneSavedUser";
const KEYPAD_COLLAPSED_KEY = "softphoneKeypadCollapsed";
const TRANSFER_TYPE_KEY = "softphoneTransferType";
const AUDIO_INPUT_KEY = "softphoneAudioInput";
const AUDIO_OUTPUT_KEY = "softphoneAudioOutput";
const MAX_RECONNECT_DELAY_MS = 30_000;
const REGISTRATION_LEASE_PREFIX = "docrm-softphone:registration";
const REGISTRATION_LEASE_TTL_MS = 20_000;
const REGISTRATION_LEASE_RETRY_MS = 2_000;
const TAB_COORDINATION_CHANNEL = "docrm-softphone:tabs:v1";
const TAB_COORDINATION_HEARTBEAT_MS = 2_000;
const CALL_SETUP_TIMEOUT_MS = 30_000;
// Asterisk Dial uses 45/60 seconds. This is only a last-resort browser guard
// when its final response is lost, not a replacement for the PBX ring timeout.
const CALL_RINGING_TIMEOUT_MS = 90_000;
const TAB_INSTANCE_ID = crypto.randomUUID?.() || `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const tabRpc = new TabRpc({
  self: TAB_INSTANCE_ID,
  send: postTabCoordinationMessage,
  observe: (detail) => {
    const knownOwner = detail.ownerTabId === mirroredOwnerId && mirroredOwnerStateAt > 0;
    diagnosticQueue.record("tab_command", { ...browserDiagnosticContext(), ...detail,
      ownerHeartbeatAgeMs: knownOwner ? Math.max(0, Date.now() - mirroredOwnerStateAt) : undefined,
      ownerLiveCallCount: knownOwner ? mirroredOwnerState?.calls?.length : undefined });
  },
  execute: async (command, args, signal, requester) => {
    try {
      if (command === "yieldIdleOwnership") return await yieldIdleOwnership(requester, signal);
      const result = await executeLocalTabCommand(command, args, signal);
      return command === "getAudioDevices" ? result : sharedOwnerState();
    } finally { broadcastOwnerState(); }
  }
});

const diagnosticQueue = new DiagnosticQueue((events, signal) => fetch(apiUrl("/api/widget/diagnostics"), {
  method: "POST", headers: widgetApiHeaders({ json: true }), body: JSON.stringify({ events }), signal
}));
let credentialAdvertisement: { binding: string; issuedAt: number; expiresAt: number } | undefined;
let credentialAdvertisementToken = "";
let lastCredentialOffer: { owner: string; token: string; at: number } | undefined;
const credentialSync = new WidgetCredentialSync({
  context: () => ownsRegistrationLease() ? {
    token: widgetApiToken(), key: credentialContextKey(), url: credentialVerificationUrl()
  } : undefined,
  verify: async (url, token, signal) => {
    const response = await fetch(url, { headers: { accept: "application/json", authorization: `Bearer ${token}` },
      cache: "no-store", credentials: "omit", redirect: "error", signal });
    if (!response.ok) throw new Error("Widget credential verification rejected");
    return response.json();
  },
  apply: (token, config, expiresAt) => {
    // Refresh API/TURN credentials only. Existing SIP sessions and peer
    // connections retain their configuration; new peers get the fresh ICE set.
    Object.assign(runtimeConfig, { widgetToken: token, widgetConfigToken: token, widgetTokenExpiresAtUnix: expiresAt,
      iceServers: config.iceServers, iceServersExpiresAtUnix: config.iceServersExpiresAtUnix });
    configureDiagnosticQueue(true);
    updateCredentialAdvertisement();
  },
  observe: credentialsPhase => diagnosticQueue.record("widget_credentials", { ...browserDiagnosticContext(), credentialsPhase })
});
const transportDiagnostics = new TransportDiagnostics((type, detail) => diagnosticQueue.record(type, {
  ...browserDiagnosticContext(), ...detail, desiredConnection, liveCallCount: liveCalls().length,
  callId: activeCall?.id, linkedId: activeCall?.linkedId, callPhase
}));

const mediaCapture = new MediaCapture({
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  visible: () => document.visibilityState === "visible"
});
function browserDiagnosticContext() {
  return { runtimeVersion: RUNTIME_VERSION, tabProtocol: TAB_RPC_VERSION,
    tabId: TAB_INSTANCE_ID, visibility: document.visibilityState, focused: document.hasFocus(),
    tabRole: registrationStandby ? "standby" : ownsRegistrationLease() ? "owner" : "unregistered",
    microphonePermission: microphonePermission?.state || "unknown", selectedInput: Boolean(runtimeConfig.audioInputDeviceId || localStorage.getItem(AUDIO_INPUT_KEY)) };
}
function callMediaDiagnosticContext(activeSession) {
  try {
    return { ...browserDiagnosticContext(), sipCallId: activeSession?.request?.callId,
      ...peerDiagnosticState(activeSession?.sessionDescriptionHandler?.peerConnection),
      ...playbackDiagnosticState(remoteAudio, Boolean(activeSession && remoteAudioSession === activeSession)) };
  } catch { return {}; } // Unavailable browser context must not interrupt cleanup.
}
async function captureMicrophone(constraints, signal?: AbortSignal, activeSession?) {
  // Permission inspection is best effort; it must not delay the actual capture.
  void getBrowserPermissions().catch(() => {});
  const started = performance.now();
  const detail = () => ({ ...browserDiagnosticContext(), callId: callBySession(activeSession)?.id,
    elapsedMs: Math.round(performance.now() - started) });
  diagnosticQueue.record("microphone_requested", detail());
  try {
    const stream = await mediaCapture.acquire(constraints, signal);
    diagnosticQueue.record("microphone_acquired", detail());
    return stream;
  } catch (error) {
    diagnosticQueue.record("microphone_failed", { ...detail(), code: error?.code, errorName: error?.name });
    throw error;
  }
}

function callSetup(activeSession): CallSetup {
  let setup = callSetups.get(activeSession);
  if (!setup) { setup = new CallSetup(); callSetups.set(activeSession, setup); }
  return setup;
}

const DEFAULT_CALL_OUTCOMES = {
  mode: "off",
  enabled: false,
  required: false,
  options: []
};

const STATE_LABELS = {
  Initial: "Подготовка",
  Establishing: "Соединение",
  Established: "Разговор",
  Terminating: "Завершение",
  Terminated: "Завершен"
};

const SIP_ERROR_LABELS = {
  400: "Некорректный запрос",
  401: "Нужна авторизация",
  403: "Доступ запрещен",
  404: "Номер не найден",
  408: "Время ожидания истекло",
  480: "Абонент недоступен",
  486: "Абонент занят",
  487: "Вызов отменен",
  488: "Несовместимые параметры звонка",
  500: "Ошибка телефонии",
  503: "Телефония недоступна",
  603: "Вызов отклонен"
};

function assetUrl(path: string) {
  return new URL(path.replace(/^\/+/, ""), import.meta.url).toString();
}

const EVENT_LABELS = {
  ready: "готов",
  state: "состояние",
  registered: "подключен",
  unregistered: "отключен",
  incoming: "входящий",
  outgoing: "исходящий",
  outgoing_progress: "дозвон",
  answering: "соединяем",
  answered: "ответ",
  ended: "завершен",
  transfer_consult_started: "консультация",
  transfer_consult_established: "консультация",
  transfer_started: "перевод",
  transfer_success: "перевод",
  transfer_completed: "перевод",
  transfer_canceled: "перевод",
  transfer_failed: "перевод",
  conference_ended: "конференция",
  call_selected: "линия",
  conference_requested: "конференция",
  conference_invite_started: "конференция",
  conference_started: "конференция",
  conference_state_changed: "конференция",
  conference_participant_left: "конференция",
  conference_status_failed: "конференция",
  conference_failed: "конференция",
  audio_devices_changed: "аудио",
  audio_device_selected: "аудио",
  audio_device_failed: "аудио",
  call_outcome_required: "результат",
  call_outcome_selected: "результат",
  call_outcome_skipped: "результат",
  call_outcome_failed: "результат",
  muted: "микрофон выкл.",
  unmuted: "микрофон вкл.",
  held: "удержание",
  resumed: "возобновлен",
  dtmf: "тон",
  session: "звонок"
};

function stateLabel(state) {
  return STATE_LABELS[state] || state;
}

function logEvent(type, detail = "") {
  if (!eventLogEl) return;
  const time = new Date().toLocaleTimeString();
  const label = EVENT_LABELS[type] || type;
  const value = STATE_LABELS[detail] || detail;
  const line = `[${time}] ${label}${value ? `: ${value}` : ""}`;
  eventLogEl.textContent = [line, ...eventLogEl.textContent.split("\n").filter(Boolean)].slice(0, 6).join("\n");
}

function newCallId() {
  return crypto.randomUUID?.() || `call-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function liveCalls() {
  return Array.from(calls.values()).filter((call) => call.phase !== "ended");
}

function hasCallCapacity(direction = "outgoing") {
  const count = liveCalls().filter(call => (call.direction === "consult") === (direction === "consult")).length;
  return count < (direction === "consult" ? 1 : 2);
}

function requireCallCapacity(direction = "outgoing") {
  if (!hasCallCapacity(direction)) throw new Error("Заняты обе линии. Завершите один из звонков");
}

function selectedCall() {
  return calls.get(selectedCallId) || activeCall || null;
}

function callBySession(activeSession) {
  if (!activeSession) return null;
  const id = activeSession.__softphoneCallId;
  if (id && calls.has(id)) return calls.get(id);
  return Array.from(calls.values()).find((call) => call.session === activeSession) || null;
}

function isLiveSession(activeSession) {
  return activeSession && activeSession.state !== SIP.SessionState.Terminated;
}

function durationSec(call = activeCall) {
  if (!call?.answeredAt) return 0;
  const end = call.endedAt || new Date().toISOString();
  return Math.max(0, Math.floor((Date.parse(end) - Date.parse(call.answeredAt)) / 1000));
}

function phaseLabel(phase, call = {} as any) {
  if (call.held) return "Удержание";
  if (phase === "incoming") return "Входящий";
  if (phase === "answering") return "Соединяем";
  if (phase === "calling") return "Исходящий";
  if (phase === "talking") return "Разговор";
  if (phase === "terminating") return "Завершение";
  return "Ожидание";
}

function syncGlobalsFromCall(call) {
  if (!call) {
    session = undefined;
    activeCall = undefined;
    callPhase = "idle";
    activePhone = "";
    muted = false;
    held = false;
    return;
  }
  session = call.session;
  activeCall = call;
  callPhase = call.phase || "idle";
  activePhone = call.phone || "";
  muted = Boolean(call.muted);
  held = Boolean(call.held);
}

function renderCallStack() {
  const stack = $("callStack");
  if (!stack) return;
  const items = liveCalls();
  stack.hidden = items.length < 2;
  stack.replaceChildren();
  items.forEach((call) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "call-line";
    button.dataset.phase = call.phase || "idle";
    button.dataset.held = String(Boolean(call.held));
    button.classList.toggle("is-selected", call.id === selectedCallId);

    const main = document.createElement("span");
    main.className = "call-line-main";
    const title = document.createElement("span");
    title.className = "call-line-title";
    title.textContent = call.customerPhone || "Неизвестный номер";
    const meta = document.createElement("span");
    meta.className = "call-line-meta";
    meta.textContent = `${phaseLabel(call.phase, call)} * ${call.direction === "incoming" ? "входящий" : call.direction === "consult" ? "консультация" : "исходящий"}`;
    main.append(title, meta);

    const badge = document.createElement("span");
    badge.className = "call-line-badge";
    button.append(main, badge);
    button.addEventListener("click", () => {
      const action = call.phase === "incoming" ? selectCall(call.id, { attach: true }) : activateCall(call.id);
      action.catch((error) => setStatus(error.message));
    });
    stack.append(button);
  });
}

function isCurrentCall(call) {
  return Boolean(call?.session && calls.get(call.id) === call && !call.endedAt &&
    call.phase !== "ended" && call.session.state !== SIP.SessionState.Terminated &&
    !callSetups.get(call.session)?.controller.signal.aborted);
}

function requireCurrentCall(call) {
  if (!isCurrentCall(call)) throw new Error("Этот звонок уже завершён");
  return call;
}

function rememberCall(call) {
  // Creation belongs exclusively to createCallRecord. An async callback is not
  // allowed to resurrect a removed/terminated record, even with the same id.
  if (!isCurrentCall(call)) return null;
  startCallStatusMonitor();
  return call;
}

function createCallRecord({ activeSession = session, direction, phone = "", customerPhone = undefined, dialNumber = undefined,
  phase = "idle", transfer = null, select = true, id = newCallId(), linkedId = "", asteriskChannelId = "" }: AnyRecord & Partial<CallIdentity>) {
  const existing = callBySession(activeSession);
  const call: SoftphoneCall = existing || {
    id,
    linkedId,
    asteriskChannelId,
    session: activeSession,
    direction,
    phone,
    ...callIdentity({ customerPhone, dialNumber }),
    phase,
    transfer,
    muted: false,
    held: false,
    createdAt: new Date().toISOString(),
    progressAt: null,
    answeredAt: null,
    endedAt: null,
    sipStatusCode: 0,
    sipReason: ""
  };
  if (!existing) latestCallId = call.id;
  call.direction = direction || call.direction;
  call.phone = phone || call.phone || "";
  updateCallIdentity(call, { customerPhone, dialNumber });
  call.phase = phase || call.phase;
  call.transfer = transfer ?? call.transfer;
  if (!existing && activeSession) {
    activeSession.__softphoneCallId = call.id;
    calls.set(call.id, call);
  }
  rememberCall(call);
  if (select || !selectedCallId) {
    selectedCallId = call.id;
    syncGlobalsFromCall(call);
  }
  renderCallStack();
  reconcileRinging();
  return call;
}

async function selectCall(callId, options = {} as any) {
  const call = calls.get(callId);
  if (!isCurrentCall(call)) return null;
  selectedCallId = call.id;
  syncGlobalsFromCall(call);
  if (options.attach && call.session?.state === SIP.SessionState.Established && !call.held) {
    attachRemoteAudio(call.session);
  }
  if (call.phase === "talking" && call.answeredAt) startCallTimer(call);
  else stopCallTimer();
  updateCallDisplay();
  updatePrimaryButtons();
  updateControlButtons();
  renderCallStack();
  return call;
}

async function activateCall(callId) {
  return callControl.run(() => activateCallInternal(callId));
}

async function holdOtherCalls(target?) {
  const others = liveCalls().filter(call => call !== target && isCurrentCall(call));
  if (others.some(call => call.holdOperation || ["calling", "answering", "terminating"].includes(call.phase))) {
    throw new Error("Дождитесь завершения текущего действия со звонком");
  }
  for (const call of others.filter(call => call.session.state === SIP.SessionState.Established && !call.held)) {
    await setSipHold(true, { call, emit: true });
  }
}

async function activateCallInternal(callId) {
  const target = calls.get(callId);
  if (!target) return null;
  requireCurrentCall(target);
  if (target.holdOperation) throw new Error("Ожидается подтверждение удержания звонка");
  await holdOtherCalls(target);
  requireCurrentCall(target);
  await selectCall(target.id, { attach: false });
  if (target.session?.state === SIP.SessionState.Established && target.held) {
    await setSipHold(false, { call: target, emit: true });
  }
  requireCurrentCall(target);
  if (target.session?.state === SIP.SessionState.Established && !target.held) attachRemoteAudio(target.session);
  await selectCall(target.id, { attach: false });
  emitWidgetEvent("call_selected", { callId: target.id, phone: target.phone, phase: target.phase });
  return getSoftphoneState();
}

function selectNextLiveCall() {
  const next = liveCalls()[0];
  if (next) {
    selectedCallId = next.id;
    syncGlobalsFromCall(next);
  } else {
    selectedCallId = "";
    syncGlobalsFromCall(null);
  }
  renderCallStack();
}

function removeCall(call) {
  if (!call || calls.get(call.id) !== call) return;
  clearTimeout(call.mediaRecoveryTimer);
  if (call.session) callSetup(call.session).abort();
  stopCallSetupTimer(call);
  stopIncomingAttention(call.id);
  call.phase = "ended";
  call.endedAt = call.endedAt || new Date().toISOString();
  enqueueCallOutcome(call);
  calls.delete(call.id);
  reconcileRinging();
  if (selectedCallId === call.id) selectNextLiveCall();
  else renderCallStack();
  showNextOutcomePrompt();
  startCallStatusMonitor();
  if (!liveCalls().length) void restartRegistrationForConfigurationWhenIdle();
}

async function closeCallLocally(call, reason = "ended") {
  if (!call || calls.get(call.id) !== call) return;
  stopIncomingAttention(call.id);
  const activeSession = call.session;
  if (activeSession) callSetup(activeSession).abort();
  try {
    if (activeSession?.state === SIP.SessionState.Established) {
      await activeSession.bye?.();
    } else if (call.phase === "incoming") {
      await activeSession?.reject?.();
    } else if (["calling", "answering"].includes(call.phase)) {
      if (call.direction === "outgoing") {
        call.localCancelRequested = true;
        call.localCancelRequestedAt = new Date().toISOString();
      }
      if (call.direction === "incoming") await activeSession?.reject?.();
      else await activeSession?.cancel?.();
    }
  } catch (error) {
    if (call.localCancelRequested && isExpectedLocalCancelError(error)) {
      emitOutgoingCanceled(call, call.sipStatusCode || 487);
    } else {
      emitWidgetEvent("call_cleanup_failed", {
        callId: call.id,
        reason,
        message: error.message,
        recoverable: true
      });
    }
  }
  if (calls.get(call.id) !== call) return; // SIP may have completed cleanup while awaiting BYE/CANCEL.
  if (consultSession === activeSession) consultSession = undefined;
  if (remoteAudioSession === activeSession || activeMediaCallId === call.id) {
    detachRemoteAudio(activeSession);
  }
  if (selectedCallId === call.id || !liveCalls().length) stopRingback();
  if (call.localCancelRequested) emitOutgoingCanceled(call);
  removeCall(call);
  if (!liveCalls().length) {
    setCallPhase("idle");
    setCallUiState("idle", "Ожидание");
  }
  updateControlButtons();
  updatePrimaryButtons();
}

function beginCall(direction, phone, options = {} as any) {
  activeCall = createCallRecord({
    activeSession: options.activeSession || session,
    direction,
    phone,
    customerPhone: options.customerPhone,
    dialNumber: options.dialNumber,
    phase: options.phase || callPhase || (direction === "incoming" ? "incoming" : "calling"),
    transfer: options.transfer || null,
    select: options.select !== false
  });
  return activeCall;
}

function finishCall(call = activeCall) {
  if (!call) return;
  stopCallSetupTimer(call);
  call.endedAt = new Date().toISOString();
  call.phase = "ended";
}

function outcomeConfig() {
  return normalizeCallOutcomeConfig(runtimeConfig);
}

function outcomeBlockingStrict() {
  return !outcomeModalEl.hidden && Boolean(activeOutcomeCall) && outcomeConfig().required;
}

function outcomePromptDeferredByCall() {
  return liveCalls().some((call) => ["incoming", "answering", "calling", "talking", "held"].includes(call.phase));
}

function callSnapshot(call) {
  return {
    id: call.id,
    callId: call.id,
    linkedId: call.linkedId || "",
    direction: call.direction,
    phone: call.phone,
    ...callIdentity(call),
    extension: extensionEl.value.trim(),
    userId: runtimeConfig.userId || "",
    provider: runtimeConfig.provider || "",
    startedAt: call.createdAt || null,
    answeredAt: call.answeredAt || null,
    endedAt: call.endedAt || new Date().toISOString(),
    durationSec: durationSec(call),
    sipStatusCode: call.sipStatusCode || 0,
    sipReason: call.sipReason || "",
    transfer: call.transfer || null
  };
}

function shouldRequestCallOutcome(call) {
  if (!call || call.outcomeHandled) return false;
  if (call.skipOutcomeReason) return false;
  if (call.direction === "consult") return false;
  if (!["incoming", "outgoing"].includes(call.direction)) return false;
  if (!call.answeredAt) return false;
  return true;
}

function stopCallStatusMonitor() {
  callStatusRequest?.abort();
  callStatusRequest = undefined;
  if (!callStatusTimer) return;
  clearInterval(callStatusTimer);
  callStatusTimer = undefined;
}

function startCallStatusMonitor() {
  if (!userAgent || !liveCalls().some((call) => call.phase === "incoming" || call.session?.state === SIP.SessionState.Established)) {
    stopCallStatusMonitor();
    return;
  }
  if (callStatusTimer) return;
  callStatusTimer = setInterval(checkActiveCallStatus, 3500);
}

const CALL_STATUS_UNAVAILABLE_MESSAGE = "Не удалось проверить состояние звонков на шлюзе. Повторите действие";

async function readActiveCallStatus(request: CallSetup, extension: string) {
  const response = await request.run(() => fetch(apiUrl(`/api/widget/calls/active?extension=${encodeURIComponent(extension)}`), {
    headers: widgetApiHeaders(), signal: request.controller.signal
  }));
  const status = await request.run(() => response.json());
  if (!response.ok || status.ok === false) throw new Error(CALL_STATUS_UNAVAILABLE_MESSAGE);
  return status;
}

async function requireKnownServerCalls(signal?: AbortSignal) {
  signal?.throwIfAborted();
  const extension = extensionEl.value.trim(), identity = credentialContextKey();
  const agent = userAgent, generation = connectionGeneration;
  const request = new CallSetup();
  const abort = () => request.controller.abort(signal?.reason);
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => request.abort(), 8000);
  let status;
  try {
    status = await readActiveCallStatus(request, extension);
  } catch (error) {
    signal?.throwIfAborted();
    throw new Error(CALL_STATUS_UNAVAILABLE_MESSAGE, { cause: error });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
  signal?.throwIfAborted();
  if (extension !== extensionEl.value.trim() || identity !== credentialContextKey() ||
    agent !== userAgent || generation !== connectionGeneration || pageSuspended) {
    throw new Error("Подключение телефонии изменилось. Повторите действие после регистрации");
  }
  if (status.callIdentityVersion !== 1 || !Array.isArray(status.channels)) {
    throw new Error(CALL_STATUS_UNAVAILABLE_MESSAGE);
  }
  // Only this WebRTC extension, not the shared provider login. Known held and
  // consultation dialogs remain usable; never adopt or hang up unknown ones.
  const known = new Set(liveCalls().filter(call => isLiveSession(call.session)).map(call => call.session.request?.callId).filter(Boolean));
  if (status.channels.some(channel => !channel.sipCallId || !known.has(channel.sipCallId))) {
    throw new Error("На вашей линии ещё есть звонок из другой вкладки или устройства. Завершите его там и повторите действие");
  }
}

async function checkActiveCallStatus() {
  if (callStatusRequest) return;
  const trackedCalls = liveCalls().filter((call) => call.phase === "incoming" || call.session?.state === SIP.SessionState.Established);
  if (!trackedCalls.length) {
    stopCallStatusMonitor();
    return;
  }
  const extension = extensionEl.value.trim();
  if (!extension) return;
  const request = new CallSetup();
  callStatusRequest = request;
  const timeout = setTimeout(() => request.abort(), 8000);
  try {
    const status = await readActiveCallStatus(request, extension);
    if (callStatusRequest !== request || request.controller.signal.aborted || extensionEl.value.trim() !== extension) return;
    const currentCalls = trackedCalls.filter(isCurrentCall);
    if (!currentCalls.length) return;
    callActionLock = status.actionLocked ? status.actionLock || { reason: "call_action_locked" } : null;
    updateControlButtons();
    if (status.callIdentityVersion !== 1 || !Array.isArray(status.channels)) return;
    let identityChanged = false;
    currentCalls.forEach((call) => {
      const sipCallId = call.session.request?.callId;
      if (!sipCallId) return;
      const channel = status.channels.find(item => item.sipCallId === sipCallId);
      if (channel && Object.prototype.hasOwnProperty.call(channel, "customerPhone")) {
        identityChanged ||= call.customerPhone !== channel.customerPhone;
        updateCallIdentity(call, { customerPhone: channel.customerPhone });
        identityChanged ||= call.transfer?.type !== channel.transfer?.type || call.transfer?.sourceExtension !== channel.transfer?.sourceExtension;
        call.transfer = channel.transfer || null;
        if (channel.transfer?.type === "conference_invite") {
          call.skipOutcomeReason = "conference_helper";
          call.outcomeHandled = true;
        }
      }
      if (channel?.linkedid && call.linkedId !== channel.linkedid) {
        call.linkedId = channel.linkedid;
        emitWidgetEvent("call_linked", {
          callId: call.id,
          linkedId: call.linkedId,
          phone: call.phone || "",
          direction: call.direction || null
        });
      }
      call.missedServerChecks = channel ? 0 : Number(call.missedServerChecks || 0) + 1;
      rememberCall(call);
    });
    if (identityChanged) {
      updateCallDisplay();
      renderCallStack();
      emitWidgetEvent("state", getSoftphoneState());
    }
    const staleCalls = currentCalls.filter((call) =>
      call.session?.state === SIP.SessionState.Established &&
      Number(call.missedServerChecks || 0) >= 2 &&
      Date.now() - Date.parse(call.answeredAt || call.createdAt || new Date().toISOString()) > 5000
    );
    for (const call of staleCalls) {
      if (!isCurrentCall(call)) continue;
      emitWidgetEvent("ended", {
        callId: call.id,
        phone: call.phone || "",
        direction: call.direction || null,
        reason: "server_channel_closed"
      });
      await closeCallLocally(call, "server_channel_closed");
    }
  } catch {
    // Network/AMI status is a safety net only; SIP state remains the source of truth on errors.
  } finally {
    clearTimeout(timeout);
    if (callStatusRequest === request) callStatusRequest = undefined;
  }
}

function enqueueCallOutcome(call) {
  const config = outcomeConfig();
  if (!config.enabled || !shouldRequestCallOutcome(call)) return;
  call.outcomeHandled = true;
  pendingOutcomeCalls.unshift(callSnapshot(call));
  emitWidgetEvent("call_outcome_required", {
    callId: call.id,
    phone: call.phone,
    required: config.required,
    options: config.options
  });
  showNextOutcomePrompt();
}

function suppressOutcomeForCall(call, reason = "transferred") {
  if (!call) return;
  call.skipOutcomeReason = reason;
  call.outcomeHandled = true;
  const pendingIndex = pendingOutcomeCalls.findIndex((item) => item.callId === call.id || item.id === call.id);
  if (pendingIndex >= 0) pendingOutcomeCalls.splice(pendingIndex, 1);
  if (activeOutcomeCall && (activeOutcomeCall.callId === call.id || activeOutcomeCall.id === call.id)) {
    activeOutcomeCall = undefined;
    selectedOutcome = undefined;
    outcomeModalEl.hidden = true;
    outcomeModalEl.dataset.mode = "";
    outcomeOptionsEl.hidden = true;
  }
  rememberCall(call);
}

function suspendOutcomePromptForCall() {
  if (!activeOutcomeCall) return;
  pendingOutcomeCalls.unshift(activeOutcomeCall);
  activeOutcomeCall = undefined;
  selectedOutcome = undefined;
  outcomeModalEl.hidden = true;
  outcomeModalEl.dataset.mode = "";
  outcomeOptionsEl.hidden = true;
  outcomeSelectButtonEl.setAttribute("aria-expanded", "false");
  updatePrimaryButtons();
  updateControlButtons();
}

function renderOutcomeOptions() {
  const config = outcomeConfig();
  outcomeOptionsEl.replaceChildren();
  config.options.forEach((option) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "outcome-option";
    button.textContent = option.label;
    button.classList.toggle("is-selected", selectedOutcome?.id === option.id);
    button.addEventListener("click", () => {
      selectedOutcome = option;
      $("outcomeSelectText").textContent = option.label;
      outcomeSubmitEl.disabled = false;
      outcomeOptionsEl.hidden = true;
      outcomeSelectButtonEl.setAttribute("aria-expanded", "false");
      renderOutcomeOptions();
    });
    outcomeOptionsEl.append(button);
  });
}

function showNextOutcomePrompt() {
  if (activeOutcomeCall || !pendingOutcomeCalls.length) return;
  if (outcomePromptDeferredByCall()) return;
  activeOutcomeCall = pendingOutcomeCalls.shift();
  selectedOutcome = null;
  const config = outcomeConfig();
  $("outcomeCallInfo").textContent = activeOutcomeCall.customerPhone
    ? `${activeOutcomeCall.direction === "incoming" ? "Входящий" : "Исходящий"} * ${activeOutcomeCall.customerPhone}`
    : "Номер не определен";
  $("outcomeSelectText").textContent = "Выбрать результат";
  $("outcomeSkip").hidden = config.required;
  outcomeSubmitEl.disabled = true;
  outcomeOptionsEl.hidden = true;
  renderOutcomeOptions();
  outcomeModalEl.dataset.mode = config.required ? "strict" : "on";
  outcomeModalEl.hidden = false;
  updatePrimaryButtons();
  updateControlButtons();
  setInCall(["talking", "held"].includes(callPhase));
}

function closeOutcomePrompt() {
  outcomeModalEl.hidden = true;
  outcomeModalEl.dataset.mode = "";
  outcomeOptionsEl.hidden = true;
  outcomeSelectButtonEl.setAttribute("aria-expanded", "false");
  activeOutcomeCall = undefined;
  selectedOutcome = undefined;
  updatePrimaryButtons();
  updateControlButtons();
  setInCall(["talking", "held"].includes(callPhase));
  showNextOutcomePrompt();
}

function resolveOutcome(value) {
  if (!value) return null;
  const id = String(value.outcomeId || value.resultId || value.id || value.outcome || "").trim().toLowerCase();
  if (!id && value.label) return value;
  return outcomeConfig().options.find((option) => option.id === id) || value;
}

async function submitCallOutcome(outcome = selectedOutcome, call = activeOutcomeCall) {
  if (!call) return null;
  outcome = resolveOutcome(outcome);
  if (!outcome) throw new Error("Выберите результат звонка");
  const payload = {
    ...call,
    outcomeId: outcome.id,
    outcomeLabel: outcome.label
  };
  emitWidgetEvent("call_outcome_selected", payload);
  fetch(apiUrl("/api/widget/calls/outcome"), {
    method: "POST",
    headers: widgetApiHeaders({ json: true }),
    body: JSON.stringify(payload)
  }).catch((error) => emitWidgetEvent("call_outcome_failed", {
    ...payload,
    code: "CALL_OUTCOME_POST_FAILED",
    message: error.message,
    recoverable: true
  }));
  closeOutcomePrompt();
  return payload;
}

function skipCallOutcome() {
  const config = outcomeConfig();
  if (config.required) {
    showToast("Выберите результат звонка");
    return;
  }
  if (activeOutcomeCall) emitWidgetEvent("call_outcome_skipped", activeOutcomeCall);
  closeOutcomePrompt();
}

function eventSeverity(type, detail = {} as any) {
  if (detail.severity) return detail.severity;
  if (type === "error" || type.endsWith("_failed")) return "error";
  return "info";
}

function eventCode(type, detail = {} as any) {
  if (detail.code) return detail.code;
  if (type === "error") return "SOFTPHONE_ERROR";
  if (type === "transfer_targets_failed") return "TRANSFER_TARGETS_FAILED";
  if (type === "transfer_failed") return "TRANSFER_FAILED";
  return "";
}

function emitWidgetEvent(type, detail = {} as any, sourceCall?: SoftphoneCall) {
  const severity = eventSeverity(type, detail);
  const code = eventCode(type, detail);
  // An explicit old callId must never borrow another call's linkedId/timestamps.
  // Terminal delegates can retain their record after it leaves the live map.
  const eventCall = detail.callId
    ? (sourceCall?.id === detail.callId ? sourceCall : calls.get(detail.callId)) || null
    : sourceCall || activeCall || null;
  if (eventCall) {
    const previousCustomerPhone = eventCall.customerPhone;
    updateCallIdentity(eventCall, detail);
    if (eventCall.customerPhone !== previousCustomerPhone) {
      if (selectedCallId === eventCall.id) updateCallDisplay();
      renderCallStack();
    }
  }
  const identity = callEventIdentity(eventCall, detail);
  logEvent(type, identity.customerPhone || detail.status || "");
  const payload = {
    source: "docrm-softphone",
    type,
    severity,
    code,
    message: detail.message || "",
    recoverable: detail.recoverable ?? severity !== "error",
    timestamp: new Date().toISOString(),
    detail: {
      ...detail,
      ...identity,
      severity,
      code,
      callId: detail.callId || eventCall?.id || null,
      linkedId: detail.linkedId ?? eventCall?.linkedId ?? "",
      sipCallId: detail.sipCallId ?? eventCall?.session?.request?.callId ?? "",
      asteriskChannelId: detail.asteriskChannelId ?? eventCall?.asteriskChannelId ?? "",
      direction: detail.direction || eventCall?.direction || null,
      phone: detail.phone ?? eventCall?.phone ?? (detail.callId ? "" : activePhone) ?? "",
      extension: extensionEl.value.trim(),
      authUser: runtimeConfig.authorizationUsername || runtimeConfig.authUser || extensionEl.value.trim(),
      provider: runtimeConfig.provider || "",
      telephonyProfileId: runtimeConfig.telephonyProfileId || "",
      telephonyKey: runtimeConfig.telephonyKey || "",
      hasSipProfile: Boolean(runtimeConfig.hasSipProfile),
      state: eventCall ? eventCall.phase : detail.callId ? undefined : callStateEl.dataset.state,
      callPhase: eventCall ? eventCall.phase : detail.callId ? undefined : callPhase,
      connected: isSipReady(),
      registrationState: registrationReady ? "registered" : registrationStandby ? "standby" : desiredConnection ? "reconnecting" : "unregistered",
      coordination: coordinationState(),
      availability: availabilityState(),
      muted: eventCall ? Boolean(eventCall.muted) : detail.callId ? undefined : muted,
      held: eventCall ? Boolean(eventCall.held) : detail.callId ? undefined : held,
      holdPending: Boolean(eventCall?.holdOperation),
      holdUnconfirmed: Boolean(eventCall?.holdOperation?.timedOut),
      mediaRecovering: Boolean(eventCall?.holdOperation?.iceRestart),
      startedAt: detail.startedAt ?? eventCall?.createdAt ?? null,
      progressAt: detail.progressAt ?? eventCall?.progressAt ?? null,
      answeredAt: detail.answeredAt ?? eventCall?.answeredAt ?? null,
      endedAt: detail.endedAt ?? eventCall?.endedAt ?? null,
      durationSec: detail.durationSec ?? durationSec(eventCall)
    }
  };
  dispatchWidgetPayload(payload);
  diagnosticQueue.record(type, { ...browserDiagnosticContext(), ...payload.detail,
    onBreak: isOnBreak(), availabilityChanging: Boolean(availabilityTask) });
  if (ownsRegistrationLease() && !isTabLocalWidgetEvent(type)) {
    postTabCoordinationMessage("event", {
      payload: sharedWidgetPayload(payload),
      state: sharedOwnerState()
    });
  }
  return payload;
}

function dispatchWidgetPayload(payload) {
  window.dispatchEvent(new CustomEvent("docrm-softphone:event", { detail: payload }));
  if (window.parent && window.parent !== window) window.parent.postMessage(payload, parentTargetOrigin());
}

function isTabLocalWidgetEvent(type) {
  return ["widget_minimized", "widget_restored", "incoming_notification_clicked", "permissions_changed"].includes(type);
}

function sharedWidgetPayload(payload) {
  return {
    ...payload,
    detail: {
      ...(payload.detail || {}),
      authUser: "",
      userId: "",
      telephonyKey: ""
    }
  };
}

function widgetApiHeaders(options = {} as any) {
  const headers: AnyRecord = { "accept": "application/json" };
  if (options.json) headers["content-type"] = "application/json";
  const token = widgetApiToken();
  if (token) headers.authorization = `Bearer ${token}`;
  return headers;
}

function widgetApiToken(config = runtimeConfig): string {
  return config.widgetConfigToken || config.widgetToken || "";
}

function fetchWidgetAction(url: string, options: RequestInit) {
  // A hung HTTP action must not hold the local command gate forever. An HTTP
  // timeout is not proof that AMI did nothing; never automatically replay it.
  return fetch(url, { ...options, signal: AbortSignal.timeout(10000) });
}

function credentialVerificationUrl() { return new URL(apiUrl("/api/widget/config"), location.href).href; }
function credentialContextKey(config = runtimeConfig) {
  return JSON.stringify([config.apiBaseUrl || "", registrationConfigurationFingerprint(config)]);
}
function configureDiagnosticQueue(changed: boolean) {
  diagnosticQueue.configure(diagnosticScope(widgetApiToken(),
    JSON.stringify([runtimeConfig.tenantId, runtimeConfig.userId, tabCoordinationIdentity()])), changed);
}
function updateCredentialAdvertisement() {
  credentialAdvertisement = undefined;
  const token = widgetApiToken(), key = credentialContextKey();
  credentialAdvertisementToken = token;
  const version = widgetCredentialVersion(token);
  if (!version) return; // Static/origin credentials are never exchanged.
  void widgetCredentialBinding(token, credentialVerificationUrl()).then(binding => {
    if (!binding || token !== widgetApiToken() || key !== credentialContextKey()) return;
    credentialAdvertisement = { binding, ...version };
    if (ownsRegistrationLease()) broadcastOwnerState();
    else if (registrationStandby) requestOwnerState();
  }).catch(() => { /* No credential exchange when secure binding cannot be established. */ });
}
function offerOwnerCredentials(owner: string, version) {
  if (!credentialAdvertisement || credentialAdvertisementToken !== widgetApiToken() ||
    credentialAdvertisement.binding !== version?.binding || !newerCredentialVersion(credentialAdvertisement, version)) return;
  const token = widgetApiToken();
  if (lastCredentialOffer?.owner === owner && lastCredentialOffer.token === token && performance.now() - lastCredentialOffer.at < 10000) return;
  lastCredentialOffer = { owner, token, at: performance.now() };
  postTabCoordinationMessage("credentials", { target: owner, protocol: TAB_RPC_VERSION, token });
}

function setStatus(text) {
  callStateTextEl.textContent = text;
}

function showToast(message) {
  clearTimeout(toastTimer);
  toastEl.textContent = message;
  toastEl.hidden = false;
  toastEl.classList.add("is-visible");
  toastTimer = setTimeout(() => {
    toastEl.classList.remove("is-visible");
    toastEl.hidden = true;
  }, 3600);
}

function clearAuthInvalidState() {
  const panel = $("identityPanel");
  panel.classList.remove("is-auth-invalid", "is-auth-shaking");
  extensionEl.removeAttribute("aria-invalid");
  passwordEl.removeAttribute("aria-invalid");
}

function showAuthInvalidState() {
  const panel = $("identityPanel");
  panel.classList.remove("is-auth-shaking");
  panel.classList.add("is-auth-invalid");
  extensionEl.setAttribute("aria-invalid", "true");
  passwordEl.setAttribute("aria-invalid", "true");
  requestAnimationFrame(() => {
    panel.classList.add("is-auth-shaking");
  });
}

function isAuthInvalidError(error) {
  return /неверн|401|403|Unauthorized|Forbidden/i.test(error?.message || "");
}

function handleConnectError(error) {
  if (isAuthInvalidError(error)) showAuthInvalidState();
  setStatus(error.message);
}

function isSipReady() {
  return Boolean(userAgent && transportConnected && registrationReady);
}

function registrationIdentity() {
  const extension = extensionEl.value.trim();
  const authUser = runtimeConfig.authorizationUsername || runtimeConfig.authUser || extension;
  return `${getSipDomain()}|${authUser}`;
}

function tabCoordinationIdentity() {
  return extensionEl.value.trim() ? registrationIdentity() : "";
}

function ownsRegistrationLease() {
  return Boolean(registrationLeaseName && registrationLeaseOwned && !registrationStandby && desiredConnection);
}

function postTabCoordinationMessage(kind, detail = {} as any) {
  const identity = detail.identity || tabCoordinationIdentity();
  if (!tabCoordinationChannel || !identity) return;
  tabCoordinationChannel.postMessage({
    source: "docrm-softphone-tab",
    kind,
    identity,
    sender: TAB_INSTANCE_ID,
    sentAt: Date.now(),
    ...detail
  });
}

function sharedOwnerState() {
  const state = getSoftphoneState();
  return {
    ...state,
    authUser: "",
    userId: "",
    telephonyKey: ""
  };
}

async function refreshOwnerState() {
  if (ownerRefreshTask) return ownerRefreshTask;
  const identity = tabCoordinationIdentity();
  ownerRefreshTask = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      resolveOwnerRefresh = undefined;
      reject(new Error("Вкладка с телефонией не отвечает. Вернитесь в неё; текущий разговор не будет перезапущен"));
    }, 2500);
    resolveOwnerRefresh = () => { clearTimeout(timer); resolveOwnerRefresh = undefined; resolve(); };
    requestOwnerState();
  }).finally(() => { ownerRefreshTask = undefined; });
  await ownerRefreshTask;
  if (identity !== tabCoordinationIdentity()) throw new Error("Настройки телефонии изменились");
}

async function yieldIdleOwnership(requester: string, signal: AbortSignal) {
  return callControl.run(async () => {
    if (!requester || !ownsRegistrationLease() || ownershipYield || availabilityTask || registrationConfigurationRestartTask || liveCalls().length || transferInProgress || connectPromise) {
      throw new Error("Вкладка занята звонком или подключением. Управление остаётся в ней");
    }
    await requireKnownServerCalls(signal);
    if (!ownsRegistrationLease() || liveCalls().length) {
      throw new Error("Поступил звонок или изменилось подключение: управление остаётся в прежней вкладке");
    }
    const operation = new CallSetup();
    ownershipYield = operation;
    const abort = () => operation.abort();
    signal.addEventListener("abort", abort, { once: true });
    clearReconnectTimer();
    const oldAgent = userAgent, oldRegisterer = registerer;
    // Wait for unregister confirmation; never release a lease on a missed heartbeat.
    const shutdown = (async () => {
      if (oldRegisterer) await oldRegisterer.dispose();
      operation.check();
      if (liveCalls().length) throw new Error("Поступил звонок: управление остаётся в прежней вкладке");
      if (oldAgent) await oldAgent.stop();
      if (userAgent === oldAgent) userAgent = undefined;
      if (registerer === oldRegisterer) registerer = undefined;
      transportConnected = registrationReady = false;
      registrationPromise = undefined;
      ownershipReservation = { target: requester, until: Date.now() + 6000 };
      releaseRegistrationLease(ownershipReservation);
      scheduleRegistrationLeaseRetry();
      return { target: requester, released: true };
    })().finally(() => {
      if (ownershipYield === operation) ownershipYield = undefined;
      if (ownsRegistrationLease() && desiredConnection && !registrationReady) scheduleReconnect({ immediate: true });
    });
    const timer = setTimeout(abort, 8000);
    try { return await operation.run(() => shutdown); }
    catch { throw new Error("Переключение вкладки не подтверждено. Откройте прежнюю вкладку софтфона и повторите действие"); }
    finally { clearTimeout(timer); signal.removeEventListener("abort", abort); }
  });
}

async function claimIdleControl() {
  if (controlClaimTask) return controlClaimTask;
  controlClaimTask = (async () => {
    if (document.visibilityState !== "visible" || !registrationStandby || !desiredConnection) return;
    await refreshOwnerState();
    if (!registrationStandby) return;
    if (mirroredOwnerProtocol !== TAB_RPC_VERSION) throw new Error("Обновите все вкладки DoCRM: версия софтфона изменилась");
    if (mirroredOwnerState?.calls?.length || mirroredOwnerState?.coordination?.busy) {
      throw new Error("Звонок или действие выполняется в другой вкладке. Управление остаётся в ней");
    }
    const identity = tabCoordinationIdentity();
    const result = await tabRpc.request(mirroredOwnerId, "yieldIdleOwnership");
    if (!result?.released || result.target !== TAB_INSTANCE_ID || identity !== tabCoordinationIdentity() || !desiredConnection) {
      throw new Error("Переключение вкладки не подтверждено");
    }
    if (document.visibilityState !== "visible") throw new Error("Вернитесь во вкладку, из которой начали звонок");
    clearTimeout(registrationLeaseRetryTimer); registrationLeaseRetryTimer = undefined;
    await connect();
    if (!ownsRegistrationLease() || (!isOnBreak() && !isSipReady())) throw new Error("Дождитесь подключения телефонии в этой вкладке");
  })().finally(() => { controlClaimTask = undefined; });
  return controlClaimTask;
}

async function takeControl() {
  if (document.visibilityState !== "visible") throw new Error("Откройте вкладку, в которой хотите пользоваться телефонией");
  if (!desiredConnection) await connect();
  await claimIdleControl();
  if (!ownsRegistrationLease()) throw new Error("Не удалось получить управление телефонией в этой вкладке");
  if (!isOnBreak()) await connect();
  const state = getSoftphoneState();
  dispatchWidgetPayload(mirroredStateEvent(state));
  broadcastOwnerState();
  return state;
}

function breakStorageKey() {
  return `${REGISTRATION_LEASE_PREFIX}:${registrationIdentity()}:break`;
}

function isOnBreak() {
  return localStorage.getItem(breakStorageKey()) === "1";
}

function availabilityState() {
  return { onBreak: isOnBreak(), changing: Boolean(availabilityTask), error: availabilityError || null };
}

async function setBreak(enabled: boolean) {
  if (typeof enabled !== "boolean") throw new Error("Не указано состояние перерыва");
  if (!ownsRegistrationLease() || document.visibilityState !== "visible") {
    throw new Error("Перерыв переключается в активной вкладке софтфона");
  }
  return callControl.run(async () => {
    if (availabilityTask || ownershipYield || registrationConfigurationRestartTask || connectPromise || disconnectPromise || transferInProgress || liveCalls().length || hasLiveSipSession()) {
      throw new Error("Завершите звонок или текущее действие перед переключением перерыва");
    }
    const key = breakStorageKey();
    // Persist the user's intention before network work. A new tab/reload must
    // never silently re-register a paused line, even if unregister times out.
    localStorage.setItem(key, enabled ? "1" : "0");
    availabilityError = "";
    clearReconnectTimer();
    availabilityTask = (async () => {
      if (enabled) {
        const oldAgent = userAgent, oldRegisterer = registerer;
        if (oldRegisterer) await oldRegisterer.dispose();
        if (oldAgent) await oldAgent.stop();
        if (userAgent === oldAgent) userAgent = undefined;
        if (registerer === oldRegisterer) registerer = undefined;
        registrationPromise = undefined;
        transportConnected = registrationReady = false;
        setConnected(false);
        setCallUiState("offline", "Перерыв");
        setStatus("Перерыв");
      } else {
        await connect();
        if (!isSipReady()) throw new Error("Возобновление телефонии не подтверждено");
      }
      availabilityError = "";
    })().catch(error => {
      // Fail closed: a failed resume must not turn into an automatic reconnect.
      localStorage.setItem(key, "1");
      availabilityError = error?.message || String(error);
      throw error;
    }).finally(() => {
      availabilityTask = undefined;
      emitWidgetEvent("availability_changed", { availability: availabilityState() });
      broadcastOwnerState();
      void restartRegistrationForConfigurationWhenIdle();
    });
    emitWidgetEvent("availability_changed", { availability: availabilityState() });
    const task = availabilityTask;
    const operation = new CallSetup();
    const timer = setTimeout(() => operation.abort(), 10_000);
    try {
      await operation.run(() => task);
      return getSoftphoneState();
    } catch (error) {
      availabilityError = operation.controller.signal.aborted
        ? "Переключение перерыва не подтверждено. Дождитесь завершения или обновите вкладку"
        : error?.message || String(error);
      emitWidgetEvent("error", { code: "AVAILABILITY_CHANGE_FAILED", message: availabilityError, recoverable: true });
      throw new Error(availabilityError, { cause: error });
    } finally {
      clearTimeout(timer);
      broadcastOwnerState();
    }
  });
}

function broadcastOwnerState() {
  if (!ownsRegistrationLease()) return;
  postTabCoordinationMessage("state", { state: sharedOwnerState(), protocol: TAB_RPC_VERSION,
    credentialVersion: credentialAdvertisement });
}

function requestOwnerState() {
  postTabCoordinationMessage("state-request");
}

function coordinatedSoftphoneState() {
  if (registrationStandby && mirroredOwnerState && Date.now() - mirroredOwnerStateAt < REGISTRATION_LEASE_TTL_MS) {
    return { ...mirroredOwnerState, coordination: coordinationState() };
  }
  return getSoftphoneState();
}

function mirroredStateEvent(state) {
  return {
    source: "docrm-softphone",
    type: "state",
    severity: "info",
    code: "",
    message: "",
    recoverable: true,
    timestamp: new Date().toISOString(),
    detail: state
  };
}

function completeMirroredState(state) {
  return {
    ...state,
    ...callIdentity(state),
    calls: (state.calls || []).map((call) => ({ ...call, ...callIdentity(call) }))
  };
}

function applyMirroredOwnerState(state) {
  if (!registrationStandby || !state) return;
  mirroredOwnerState = completeMirroredState(state);
  mirroredOwnerStateAt = Date.now();
  dispatchWidgetPayload(mirroredStateEvent(coordinatedSoftphoneState()));
}

async function executeLocalTabCommand(command, args, signal?: AbortSignal) {
  signal?.throwIfAborted();
  if (ownershipYield) throw new Error("Телефония переключается между вкладками. Повторите действие после подключения");
  if (command === "call") return call(args[0], args[1], signal);
  if (command === "answer") return answer(args[0] || {}, signal);
  const handlers = {
    connect,
    disconnect,
    call,
    answer,
    hangup,
    mute: toggleMute,
    hold: toggleHold,
    selectCall,
    activateCall,
    sendDtmf,
    setAudioDevices,
    getAudioDevices: refreshAudioDevices,
    silenceRinger,
    setNotifications,
    submitCallOutcome,
    setConferenceState,
    endConference: endConferenceState,
    clearConference: endConferenceState,
    conferenceParticipantLeft,
    conference: conferenceCalls,
    transfer,
    confirmTransfer: confirmAttendedTransfer,
    cancelTransfer: cancelAttendedTransfer,
    setTransferTargets
  };
  const handler = handlers[command];
  if (!handler) throw new Error(`Неизвестная команда софтфона: ${command}`);
  if (command === "disconnect") postTabCoordinationMessage("manual-disconnect");
  return await handler(...args);
}

async function executeCoordinatedCommand(command, args = [] as any[]) {
  if (command === "takeControl") return takeControl();
  if (command === "setBreak") return setBreak(args[0]);
  // Devices and permission prompts belong to the visible requesting tab, not
  // the registration owner. Never make a second getUserMedia request over RPC.
  if (command === "getAudioDevices") return refreshAudioDevices(...args);
  if (command === "call" && registrationStandby) await takeControl();
  if (!registrationStandby) {
    try { return await executeLocalTabCommand(command, args); }
    finally {
      dispatchWidgetPayload(mirroredStateEvent(getSoftphoneState()));
      broadcastOwnerState();
    }
  }
  if (!tabCoordinationChannel || !tabCoordinationIdentity()) {
    throw new Error("Активная вкладка софтфона недоступна");
  }

  if (command === "setNotifications") {
    await setNotifications(...args);
  }
  if (!mirroredOwnerId || Date.now() - mirroredOwnerStateAt >= REGISTRATION_LEASE_TTL_MS) {
    await refreshOwnerState();
    if (!registrationStandby) return executeLocalTabCommand(command, args);
  }
  if (mirroredOwnerProtocol !== TAB_RPC_VERSION) throw new Error("Обновите все вкладки DoCRM: версия софтфона изменилась");
  try { return await tabRpc.request(mirroredOwnerId, command, args); }
  catch (error) { requestOwnerState(); throw error; }
}

function handleTabCoordinationMessage(event) {
  const message = event.data;
  if (!message || message.source !== "docrm-softphone-tab" || message.sender === TAB_INSTANCE_ID) return;
  if (!message.identity || message.identity !== tabCoordinationIdentity()) return;

  if (message.kind === "state-request") {
    broadcastOwnerState();
    return;
  }
  if (message.kind === "state") {
    if (message.sentAt && Date.now() - message.sentAt >= REGISTRATION_LEASE_TTL_MS) return;
    if (registrationStandby) {
      mirroredOwnerId = message.sender;
      mirroredOwnerProtocol = message.protocol || 0;
    }
    applyMirroredOwnerState(message.state);
    resolveOwnerRefresh?.();
    if (registrationStandby && message.protocol === TAB_RPC_VERSION) offerOwnerCredentials(message.sender, message.credentialVersion);
    return;
  }
  if (message.kind === "event") {
    if (!registrationStandby) return;
    if (mirroredOwnerId && mirroredOwnerId !== message.sender) return;
    if (message.sentAt && Date.now() - message.sentAt >= REGISTRATION_LEASE_TTL_MS) return;
    if (message.state) {
      mirroredOwnerState = completeMirroredState(message.state);
      mirroredOwnerStateAt = Date.now();
    }
    if (message.payload && !isTabLocalWidgetEvent(message.payload.type)) dispatchWidgetPayload({
      ...message.payload,
      detail: message.payload.type === "state" ? coordinatedSoftphoneState() : {
        ...message.payload.detail, coordination: coordinationState()
      }
    });
    return;
  }
  if (message.kind === "credentials") {
    if (ownsRegistrationLease() && message.target === TAB_INSTANCE_ID && message.protocol === TAB_RPC_VERSION)
      void credentialSync.accept(message.token);
    return;
  }
  if (tabRpc.handle(message, ownsRegistrationLease())) return;
  if (message.kind === "manual-disconnect") {
    desiredConnection = false;
    registrationStandby = false;
    mirroredOwnerState = undefined;
    mirroredOwnerStateAt = 0;
    clearTimeout(registrationLeaseRetryTimer);
    registrationLeaseRetryTimer = undefined;
    setConnected(false);
    setCallUiState("offline", "Не подключен");
    return;
  }
  if (message.kind === "owner-released" && registrationStandby && desiredConnection) {
    if (mirroredOwnerId && message.sender !== mirroredOwnerId) return;
    ownershipReservation = message.reservation?.until > Date.now() && message.reservation.until <= Date.now() + 7000
      ? message.reservation : undefined;
    // A cooperative handoff still owes its requester a result. No telephony
    // command has been sent by the requester at this point.
    if (ownershipReservation?.target !== TAB_INSTANCE_ID) tabRpc.reset("Активная вкладка завершила работу. Проверьте состояние звонка перед повтором");
    mirroredOwnerId = "";
    mirroredOwnerState = undefined;
    mirroredOwnerStateAt = 0;
    clearTimeout(registrationLeaseRetryTimer);
    registrationLeaseRetryTimer = undefined;
    if (!controlClaimTask) scheduleRegistrationLeaseRetry({ immediate: true });
  }
}

function initTabCoordination() {
  if (typeof BroadcastChannel === "undefined") return;
  tabCoordinationChannel = new BroadcastChannel(TAB_COORDINATION_CHANNEL);
  tabCoordinationChannel.addEventListener("message", handleTabCoordinationMessage);
  tabCoordinationHeartbeatTimer = window.setInterval(() => {
    if (ownsRegistrationLease()) {
      broadcastOwnerState();
    } else if (registrationStandby && Date.now() - mirroredOwnerStateAt >= TAB_COORDINATION_HEARTBEAT_MS * 2) {
      if (mirroredOwnerState && Date.now() - mirroredOwnerStateAt >= REGISTRATION_LEASE_TTL_MS) {
        if (!ownerRefreshTask) void refreshOwnerState().catch(() => {
          // An overdue timer is not evidence that the owner died. Probe first;
          // retain the owner id/lock so a delayed owner cannot be replaced.
          if (!registrationStandby || Date.now() - mirroredOwnerStateAt < REGISTRATION_LEASE_TTL_MS) return;
          mirroredOwnerState = undefined;
          tabRpc.reset(OWNER_UNRESPONSIVE_MESSAGE);
          applyRegistrationStandbyState();
          emitWidgetEvent("tab_owner_unresponsive", { code: "TAB_OWNER_UNRESPONSIVE", message: OWNER_UNRESPONSIVE_MESSAGE, recoverable: true });
        });
        return;
      }
      requestOwnerState();
    }
  }, TAB_COORDINATION_HEARTBEAT_MS);
}

async function acquireRegistrationLease(name) {
  if (registrationLeaseName === name && registrationLeaseOwned && !registrationStandby) return true;
  // localStorage + heartbeat is not an atomic lock: a suspended tab could keep
  // a live SIP session while another takes its lease. Fail closed instead.
  if (!navigator.locks?.request || !tabCoordinationChannel) {
    throw new Error("Для безопасной работы телефонии нужны HTTPS, Web Locks и BroadcastChannel. Обновите браузер");
  }
  if (ownershipReservation?.until > Date.now() && ownershipReservation.target !== TAB_INSTANCE_ID) return false;
  if (registrationLeaseName && registrationLeaseName !== name) releaseRegistrationLease();
  registrationLeaseName = name;
  registrationLeaseOwned = false;

  return await new Promise<boolean>((resolve, reject) => {
    navigator.locks.request(`${REGISTRATION_LEASE_PREFIX}:${name}`, { ifAvailable: true }, async (lock) => {
      if (!lock) { resolve(false); return; }
      registrationStandby = false;
      registrationLeaseOwned = true;
      mirroredOwnerState = undefined;
      mirroredOwnerStateAt = 0;
      resolve(true);
      await new Promise((release) => { registrationLeaseRelease = release; });
    }).catch(() => reject(new Error("Браузер не разрешил блокировку телефонии между вкладками")));
    });
}

function releaseRegistrationLease(reservation?: { target: string; until: number }) {
  credentialSync.cancel();
  const releasedIdentity = registrationLeaseName;
  const wasOwner = ownsRegistrationLease();
  clearTimeout(registrationLeaseRetryTimer);
  registrationLeaseRetryTimer = undefined;
  registrationLeaseRelease?.();
  registrationLeaseRelease = undefined;
  registrationLeaseName = "";
  registrationLeaseOwned = false;
  registrationStandby = false;
  if (wasOwner) postTabCoordinationMessage("owner-released", { identity: releasedIdentity, reservation });
}

function scheduleRegistrationLeaseRetry({ immediate = false } = {}) {
  if (!desiredConnection || registrationLeaseRetryTimer) return;
  registrationStandby = true;
  registrationLeaseOwned = false;
  applyRegistrationStandbyState();
  requestOwnerState();
  registrationLeaseRetryTimer = setTimeout(async () => {
    registrationLeaseRetryTimer = undefined;
    if (!desiredConnection || userAgent || pageSuspended) return;
    if (controlClaimTask) { scheduleRegistrationLeaseRetry(); return; }
    // Acquisition belongs to the same cancellable connect as initial startup
    // and handoff; a late Web Lock callback must not revive a closed page.
    try { await connect(); }
    catch (error) { handleConnectError(error); scheduleRegistrationLeaseRetry(); }
  }, immediate ? 0 : REGISTRATION_LEASE_RETRY_MS);
}

function applyRegistrationStandbyState() {
  setConnected(false);
  if (mirroredOwnerState && Date.now() - mirroredOwnerStateAt < REGISTRATION_LEASE_TTL_MS) {
    // Redisplaying cached state is not a heartbeat from the owner.
    dispatchWidgetPayload(mirroredStateEvent(coordinatedSoftphoneState()));
    return;
  }
  if (!liveCalls().length) {
    setCallUiState("offline", "Активно в другой вкладке");
    setStatus("Телефония активна в другой вкладке");
  }
}

function reconnectDelayMs(attempt) {
  const exponentialDelay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * (2 ** Math.min(attempt, 5)));
  return exponentialDelay + Math.floor(Math.random() * 500);
}

function clearReconnectTimer() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = undefined;
}

function setDisconnectedUi(message = "Не подключен", reconnecting = false) {
  setConnected(false);
  if (callPhase === "idle") {
    setCallUiState("offline", message);
    setStatus(message);
  }
  emitWidgetEvent("unregistered", {
    extension: extensionEl.value.trim(),
    reconnecting
  });
}

function scheduleReconnect({ immediate = false } = {}) {
  if (isOnBreak() || availabilityTask || registrationConfigurationRestartTask || !desiredConnection || ownershipYield || pageSuspended || registrationStandby || reconnectTimer || registrationReady) return;
  const delay = immediate ? 0 : reconnectDelayMs(reconnectAttempt);
  reconnectAttempt += 1;
  setDisconnectedUi("Переподключаем телефонию", true);
  reconnectTimer = setTimeout(async () => {
    reconnectTimer = undefined;
    if (isOnBreak() || availabilityTask || registrationConfigurationRestartTask || !desiredConnection || ownershipYield || pageSuspended || registrationStandby || registrationReady) return;
    try {
      await connect();
    } catch (error) {
      logEvent("reconnect_failed", error?.message || "");
      scheduleReconnect();
    }
  }, delay);
}

function handleTransportDisconnect(error) {
  const wasConnected = transportConnected;
  stopTransportMonitor?.();
  transportDiagnostics.disconnected(error);
  transportConnected = false;
  registrationReady = false;
  registrationPromise = undefined;
  const disconnectedRegisterer = registerer;
  registerer = undefined;
  disconnectedRegisterer?.dispose?.().catch(() => {});
  logEvent("transport_disconnected", error?.message || "");
  setDisconnectedUi("Соединение с телефонией потеряно", desiredConnection);
  if (desiredConnection) scheduleReconnect({ immediate: wasConnected });
}

function handleTransportConnect() {
  const agent = userAgent, generation = connectionGeneration;
  startTransportMonitor(userAgent);
  transportDiagnostics.connected();
  transportConnected = true;
  clearReconnectTimer();
  logEvent("transport_connected");
  if (desiredConnection && !registrationReady) {
    ensureRegistered().catch((error) => {
      if (userAgent !== agent || generation !== connectionGeneration) return;
      logEvent("registration_failed", error?.message || "");
      if (isAuthInvalidError(error)) {
        if (registrationConfigurationRestartPending) return;
        if (!availabilityTask) desiredConnection = false;
        handleConnectError(error);
        return;
      }
      scheduleReconnect();
    });
  }
}

// SIP OPTIONS checks the existing WSS path without changing registrations or
// calls. SIP.js's CRLF keepalive alone does not detect a missing response.
function startTransportMonitor(agent) {
  stopTransportMonitor?.();
  let stopped = false, timer, request;
  const schedule = (delay = 20_000) => { timer = setTimeout(probe, delay); };
  const probe = () => {
    if (stopped || userAgent !== agent) return;
    if (!desiredConnection || !registrationReady || !transportConnected || registrationStandby) { schedule(); return; }
    const started = performance.now();
    let done = false;
    const finish = (received: boolean) => {
      if (done || stopped) return;
      done = true;
      clearTimeout(timer);
      request?.dispose(); request = undefined;
      if (userAgent !== agent) return;
      // A suspended event loop is not proof that the network died. Probe afresh.
      if (received || performance.now() - started > 13_000) { schedule(received ? 20_000 : 0); return; }
      diagnosticQueue.record("transport_probe_failed", browserDiagnosticContext());
      // Disconnect only the transport. UserAgent.stop() would end SIP sessions.
      void agent.transport.disconnect().catch(error => logEvent("transport_disconnect_failed", error?.message || ""));
    };
    timer = setTimeout(() => finish(false), 8000);
    const response = () => finish(true);
    try {
      const aor = agent.userAgentCore.configuration.aor;
      const message = agent.userAgentCore.makeOutgoingRequestMessage("OPTIONS", aor, aor, aor);
      request = agent.userAgentCore.request(message, { onAccept: response, onReject: response,
        onRedirect: response, onProgress: response, onTrying: response });
    } catch { finish(false); }
  };
  probeTransportNow = () => {
    if (stopped || request) return;
    clearTimeout(timer);
    probe();
  };
  stopTransportMonitor = () => {
    stopped = true; clearTimeout(timer); request?.dispose(); probeTransportNow = undefined;
  };
  schedule();
}

async function ensureRegistered() {
  if (!desiredConnection || isOnBreak() || ownershipYield || registrationConfigurationRestartTask || pageSuspended) return;
  if (registrationReady) return;
  if (!transportConnected || !userAgent) throw new Error("Нет соединения с телефонией");
  if (registrationPromise) return registrationPromise;
  const agent = userAgent;
  const task = (async () => {
    const previousRegisterer = registerer;
    registerer = undefined;
    if (previousRegisterer) await previousRegisterer.dispose?.().catch(() => {});
    if (userAgent !== agent || !desiredConnection || !transportConnected) throw new Error("Попытка регистрации больше не актуальна");
    registerer = new SIP.Registerer(agent);
    const activeRegisterer = registerer;
    registerer.stateChange.addListener((state) => {
      if (registerer !== activeRegisterer) return;
      if (state === SIP.RegistererState.Unregistered || state === SIP.RegistererState.Terminated) {
        const wasReady = registrationReady;
        registrationReady = false;
        if (wasReady) {
          setDisconnectedUi("Регистрация телефонии потеряна", desiredConnection);
          if (desiredConnection) scheduleReconnect();
        }
      }
    });
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Телефония не ответила на регистрацию")), 10_000);
      registerer.register({
        requestDelegate: {
          onAccept: () => {
            clearTimeout(timeout);
            resolve(undefined);
          },
          onReject: (response) => {
            clearTimeout(timeout);
            const statusCode = responseStatus(response);
            const reason = reasonFromResponse(response, "Регистрация отклонена");
            reject(new Error(statusCode === 401 || statusCode === 403 ? "Неверный внутренний или пароль" : reason));
          }
        }
      }).catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
    });
    if (registerer !== activeRegisterer || isOnBreak() || !desiredConnection || !transportConnected) {
      throw new Error("Попытка регистрации больше не актуальна");
    }
    registrationReady = true;
    reconnectAttempt = 0;
    setConnected(true);
    if (!liveCalls().length) {
      setCallPhase("idle");
      setCallUiState("registered", "Готов");
      setStatus("Готов");
    }
    logEvent("registered", extensionEl.value.trim());
    emitWidgetEvent("registered", { extension: extensionEl.value.trim() });
    liveCalls().forEach(scheduleMediaRecovery);
  })().finally(() => {
    if (registrationPromise === task) registrationPromise = undefined;
  });
  registrationPromise = task;
  return task;
}

function retryConnectionWhenBrowserReturns() {
  if (!desiredConnection || !navigator.onLine) return;
  // Resume may leave a stale green registration while WSS silently died.
  // Reuse the one existing OPTIONS monitor; do not create another reconnect loop.
  if (registrationReady) { probeTransportNow?.(); return; }
  clearReconnectTimer();
  scheduleReconnect({ immediate: true });
}

function savedUser() {
  try {
    return JSON.parse(localStorage.getItem(SAVED_USER_KEY) || "null");
  } catch {
    return null;
  }
}

function saveUserFields() {
  if (!canShowLocalSettings() || !saveUserEl.checked) return;
  localStorage.setItem(SAVED_USER_KEY, JSON.stringify({
    enabled: true,
    extension: extensionEl.value.trim(),
    password: passwordEl.value
  }));
}

function hasSavedUser() {
  return isTestMode() && canShowLocalSettings() && Boolean(savedUser()?.enabled);
}

function isTestMode() {
  return runtimeConfig.testMode !== false;
}

function normalizeAuthMode(value, testMode = isTestMode()) {
  const mode = String(value || "").trim().toLowerCase();
  if (["local", "widget", "softphone", "internal"].includes(mode)) return "local";
  if (["external", "host", "crm", "parent"].includes(mode)) return "external";
  return testMode ? "local" : "external";
}

function isEmbedMode() {
  return window.__DOCRMSoftphoneEmbed === true || bootParams().get("embed") === "1" || document.documentElement.dataset.embed === "true";
}

function canShowLocalSettings() {
  if (isEmbedMode()) return false;
  return normalizeAuthMode(runtimeConfig.authMode, isTestMode()) === "local";
}

function updateAccountPanel(showForm = false) {
  const panel = $("identityPanel");
  const localSettings = canShowLocalSettings();
  const saved = localSettings && isTestMode() && (saveUserEl.checked || hasSavedUser());
  const connected = isSipReady();
  const compactMode = !localSettings || (!showForm && (connected || saved));
  widgetEl.dataset.testMode = String(isTestMode());
  panel.hidden = compactMode;
  panel.classList.toggle("is-compact", compactMode);
  $("accountAction").hidden = !compactMode;
  $("accountActionIcon").src = connected ? assetUrl("assets/account-exit.png") : assetUrl("assets/account-power.png");
  $("accountAction").classList.toggle("is-connected", connected);
  $("accountAction").setAttribute("aria-label", connected ? "Отключить" : "Подключить");
  $("settingsToggle").hidden = false;
}

function setSavedUserMode(enabled) {
  saveUserEl.checked = enabled;
  updateAccountPanel(!enabled && !userAgent);
}

function updateClearNumberButton() {
  $("clearNumber").hidden = numberEl.value.trim().length === 0;
}

function formatDuration(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function stopCallTimer() {
  clearInterval(callTimerInterval);
  callTimerInterval = undefined;
  callStartedAt = 0;
  $("callTimer").textContent = "00:00";
}

function startCallTimer(call = activeCall) {
  stopCallTimer();
  callStartedAt = call?.answeredAt ? Date.parse(call.answeredAt) : Date.now();
  $("callTimer").textContent = formatDuration(Date.now() - callStartedAt);
  callTimerInterval = setInterval(() => {
    $("callTimer").textContent = formatDuration(Date.now() - callStartedAt);
  }, 1000);
}

function updateCallDisplay() {
  const display = $("callDisplay");
  const inputWrap = runtimeQuerySelector(".dial-input-wrap");
  const inCallFlow = ["incoming", "answering", "calling", "talking", "held"].includes(callPhase);
  const transferInfo = activeCall?.transfer;
  display.hidden = !inCallFlow;
  inputWrap?.classList.toggle("is-call-hidden", inCallFlow);
  display.dataset.mode = callPhase === "held" ? "talking" : callPhase;
  $("callDisplayIcon").src = callPhase === "incoming" ? assetUrl("assets/call-display-incoming.png?v=2") : assetUrl("assets/call-display-outgoing.png?v=2");
  $("callDisplayNumber").textContent = selectedCall()?.customerPhone || "Неизвестный номер";
  if (callPhase === "incoming") {
    $("callDisplayStatus").textContent = incomingTransferLabel(transferInfo) || "Входящий вызов";
  }
  if (callPhase === "answering") $("callDisplayStatus").textContent = "Соединяем";
  if (callPhase === "calling") $("callDisplayStatus").textContent = "Исходящий вызов";
  if (callPhase === "talking") $("callDisplayStatus").textContent = held ? "На удержании" : "Разговор";
  if (callPhase === "held") $("callDisplayStatus").textContent = "На удержании";
  $("callTimer").hidden = callPhase !== "talking";
}

function incomingTransferLabel(transferInfo) {
  if (!transferInfo?.sourceExtension) return "";
  if (transferInfo.type === "conference_invite") return `Помощь ${transferInfo.sourceExtension}`;
  return `Перевод ${transferInfo.sourceExtension}`;
}

function updatePrimaryButtons() {
  const connected = isSipReady();
  const call = selectedCall();
  if (call) syncGlobalsFromCall(call);
  const strictBlocked = outcomeBlockingStrict();
  const canDial = connected && callPhase === "idle";
  const canAnswer = connected && callPhase === "incoming";
  const canEnd = connected && ["incoming", "answering", "calling", "talking", "held"].includes(callPhase);
  const callIcon = $("callActionIcon");
  const hangupIcon = $("hangupActionIcon");
  $("call").disabled = strictBlocked ? !canAnswer : !(canDial || canAnswer);
  $("hangup").disabled = strictBlocked || !canEnd;
  $("callActionText").textContent = callPhase === "incoming" ? "Ответить" : "Позвонить";
  $("hangupActionText").textContent = callPhase === "incoming" ? "Отклонить" : "Отбой";
  callIcon.src = callPhase === "incoming" ? assetUrl("assets/call-answer.png") : assetUrl("assets/call-outgoing.png");
  hangupIcon.src = callPhase === "incoming" ? assetUrl("assets/call-decline.png") : assetUrl("assets/call-hangup.png");
  $("call").classList.toggle("is-busy", ["answering", "calling"].includes(callPhase));
  $("call").setAttribute("aria-label", callPhase === "incoming" ? "Ответить" : "Позвонить");
  $("hangup").setAttribute("aria-label", callPhase === "incoming" ? "Отклонить" : "Завершить");
}

function setCallPhase(phase, phone = activePhone) {
  callPhase = phase;
  activePhone = phone || "";
  if (activeCall) {
    activeCall.phase = phase;
    activeCall.phone = activePhone || activeCall.phone || "";
    if (phase === "idle") activeCall.endedAt = activeCall.endedAt || new Date().toISOString();
    rememberCall(activeCall);
  }
  if (phase !== "talking") stopCallTimer();
  if (phase === "idle") {
    activePhone = "";
    $("contactHint").textContent = "";
    closeTransferPanel();
  }
  updateCallDisplay();
  updatePrimaryButtons();
  setInCall(["talking", "held"].includes(phase));
  renderCallStack();
}

function setKeypadCollapsed(collapsed) {
  const panel = runtimeQuerySelector(".keypad-panel");
  if (!panel) return;

  const toggle = $("keypadToggle");
  panel.classList.toggle("is-collapsed", collapsed);
  toggle.classList.toggle("is-active", !collapsed);
  toggle.setAttribute("aria-expanded", String(!collapsed));
  toggle.setAttribute("aria-label", collapsed ? "Показать клавиатуру" : "Скрыть клавиатуру");
  localStorage.setItem(KEYPAD_COLLAPSED_KEY, collapsed ? "1" : "0");
}

function applyKeypadPreference() {
  setKeypadCollapsed(localStorage.getItem(KEYPAD_COLLAPSED_KEY) === "1");
}

function togglePasswordVisibility() {
  const button = $("togglePassword");
  const visible = passwordEl.type === "password";
  passwordEl.type = visible ? "text" : "password";
  button.classList.toggle("is-visible", visible);
  button.setAttribute("aria-label", visible ? "Скрыть пароль" : "Показать пароль");
}

function readBoolean(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  return fallback;
}

function normalizeTransferMode(value, fallback = "auto") {
  const mode = String(value || fallback).trim().toLowerCase();
  return ["gateway", "provider", "auto"].includes(mode) ? mode : fallback;
}

function normalizeTransferType(value, fallback = "blind") {
  const type = String(value || fallback).trim().toLowerCase();
  return ["blind", "attended"].includes(type) ? type : fallback;
}

function normalizeCallOutcomeMode(value, fallback = "off") {
  const mode = String(value || fallback).trim().toLowerCase();
  return ["off", "on", "strict"].includes(mode) ? mode : fallback;
}

function normalizeCallOutcomes(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      const label = String(typeof item === "string" ? item : item?.label || item?.name || "").trim();
      if (!label) return null;
      const id = String(typeof item === "string" ? item : item?.id || label)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "") || `outcome-${index + 1}`;
      return { id, label, final: item?.final !== false, color: item?.color || "" };
    })
    .filter(Boolean);
}

function normalizeCallOutcomeConfig(config = {} as any) {
  const source = config.callOutcomes ||
    config.outcomes ||
    config.callOutcome ||
    ("mode" in config || "options" in config || "items" in config ? config : DEFAULT_CALL_OUTCOMES);
  const mode = normalizeCallOutcomeMode(source.mode ?? config.callOutcomeMode, "off");
  const options = normalizeCallOutcomes(source.options || source.items || source.outcomes || []);
  return {
    mode,
    enabled: Boolean(source.enabled ?? mode !== "off") && mode !== "off" && options.length > 0,
    required: Boolean(source.required ?? mode === "strict") || mode === "strict",
    options
  };
}

function bootParams() {
  return new URLSearchParams(window.location.search);
}

function bootConfig() {
  const params = bootParams();
  const config: AnyRecord = {};
  ["userId", "extension", "provider", "phoneNumber", "transferMode", "transferType", "transferEnabled", "conferenceEnabled", "authMode"].forEach((key) => {
    const value = params.get(key);
    if (value) config[key] = value;
  });
  const testMode = params.get("testMode") ?? params.get("testmode");
  const autoConnect = params.get("autoConnect") ?? params.get("autoconnect");
  if (testMode !== null) config.testMode = readBoolean(testMode, false);
  if (autoConnect !== null) config.autoConnect = readBoolean(autoConnect, false);
  return config;
}

function widgetConfigUrl() {
  const params = bootParams();
  const query = new URLSearchParams();
  ["userId", "extension"].forEach((key) => {
    const value = params.get(key);
    if (value) query.set(key, value);
  });
  const suffix = query.toString();
  return suffix ? `/api/widget/config?${suffix}` : "/api/widget/config";
}

function apiUrl(path) {
  const baseUrl = String(runtimeConfig.apiBaseUrl || "").replace(/\/$/, "");
  return baseUrl ? `${baseUrl}${path}` : path;
}

function hasExplicitWidgetConfigIdentity() {
  const params = bootParams();
  return Boolean(params.get("userId") || params.get("extension"));
}

function normalizeOrigins(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function allowedOrigins() {
  return normalizeOrigins(runtimeConfig.allowedOrigins || runtimeConfig.allowedOrigin);
}

function originMatchesPattern(origin, pattern) {
  if (pattern === "*") return true;
  if (origin === pattern) return true;
  try {
    const originUrl = new URL(origin);
    const patternUrl = new URL(pattern);
    if (originUrl.protocol !== patternUrl.protocol) return false;
    if (patternUrl.hostname.startsWith("*.")) {
      const suffix = patternUrl.hostname.slice(1);
      return originUrl.hostname.endsWith(suffix) && originUrl.hostname !== patternUrl.hostname.slice(2);
    }
  } catch {
    return false;
  }
  return false;
}

function isAllowedMessageOrigin(origin) {
  const origins = allowedOrigins();
  if (!origins.length || origins.includes("*")) return true;
  if (origin === window.location.origin) return true;
  return origins.some((pattern) => originMatchesPattern(origin, pattern));
}

function parentTargetOrigin() {
  const origins = allowedOrigins().filter((origin) => origin !== "*");
  if (acceptedParentOrigin) return acceptedParentOrigin;
  if (origins.length === 1) return origins[0];
  return "*";
}

function normalizeFeatureFlags(config = {} as any, previous = {} as any) {
  const capabilities = config.capabilities || {};
  const features = config.features || {};
  return {
    ...previous,
    transfer: readBoolean(
      config.transferEnabled ?? config.enableTransfer ?? features.transfer ?? features.transferEnabled ?? capabilities.transfer,
      previous.transfer !== false
    ),
    conference: readBoolean(
      config.conferenceEnabled ?? config.enableConference ?? features.conference ?? features.conferenceEnabled ?? capabilities.conference,
      previous.conference !== false
    )
  };
}

function featureEnabled(name) {
  return runtimeConfig.features?.[name] !== false;
}

function normalizeWidgetConfig(config = {} as any) {
  const sip = config.sip || {};
  const testMode = readBoolean(config.testMode ?? sip.testMode ?? runtimeConfig.testMode, false);
  const merged = {
    ...runtimeConfig,
    ...config
  };
  const authMode = normalizeAuthMode(config.authMode ?? sip.authMode ?? runtimeConfig.authMode, testMode);
  return {
    ...merged,
    testMode,
    authMode,
    features: normalizeFeatureFlags(config, runtimeConfig.features || {}),
    extension: config.extension || sip.extension || config.username || runtimeConfig.extension,
    password: config.password || sip.password || runtimeConfig.password,
    authorizationUsername:
      config.authorizationUsername || config.authUser || sip.authorizationUsername || sip.authUser || runtimeConfig.authorizationUsername,
    displayName: config.displayName || sip.displayName || runtimeConfig.displayName,
    sipDomain: config.sipDomain || sip.domain || sip.sipDomain || runtimeConfig.sipDomain,
    websocketUrl: config.websocketUrl || config.wsUrl || sip.websocketUrl || sip.wsUrl || runtimeConfig.websocketUrl,
    websocketPath: config.websocketPath || sip.websocketPath || runtimeConfig.websocketPath,
    iceServers: config.iceServers || sip.iceServers || runtimeConfig.iceServers,
    provider: config.provider || sip.provider || runtimeConfig.provider,
    telephonyProfileId: config.telephonyProfileId || sip.telephonyProfileId || runtimeConfig.telephonyProfileId,
    telephonyKey: config.telephonyKey || sip.telephonyKey || runtimeConfig.telephonyKey,
    hasSipProfile: Boolean(config.hasSipProfile ?? sip.hasSipProfile ?? runtimeConfig.hasSipProfile),
    codecPolicy: {
      ...(runtimeConfig.codecPolicy || {}),
      ...(config.codecPolicy || sip.codecPolicy || {}),
      webrtcCodecs: normalizeCodecOrder(
        config.webrtcCodecs || sip.webrtcCodecs || config.codecPolicy?.webrtcCodecs || sip.codecPolicy?.webrtcCodecs || runtimeConfig.codecPolicy?.webrtcCodecs,
        ["opus", "alaw", "ulaw"]
      ),
      providerCodecs: normalizeCodecOrder(
        config.providerCodecs || sip.providerCodecs || config.codecs || sip.codecs || config.codecPolicy?.providerCodecs || sip.codecPolicy?.providerCodecs || runtimeConfig.codecPolicy?.providerCodecs,
        ["alaw", "ulaw"]
      )
    },
    audioInputDeviceId: config.audioInputDeviceId || sip.audioInputDeviceId || runtimeConfig.audioInputDeviceId,
    audioOutputDeviceId: config.audioOutputDeviceId || sip.audioOutputDeviceId || runtimeConfig.audioOutputDeviceId,
    transferMode: normalizeTransferMode(config.transferMode || sip.transferMode || runtimeConfig.transferMode, "auto"),
    transferType: normalizeTransferType(config.transferType || sip.transferType || runtimeConfig.transferType, "blind"),
    callOutcomes: normalizeCallOutcomeConfig(config),
    userId: config.userId || runtimeConfig.userId,
    apiBaseUrl: config.apiBaseUrl || runtimeConfig.apiBaseUrl,
    allowedOrigins: config.allowedOrigins || config.allowedOrigin || sip.allowedOrigins || runtimeConfig.allowedOrigins
  };
}

function registrationConfigurationFingerprint(config = runtimeConfig) {
  return JSON.stringify({
    extension: config.extension || "",
    password: config.password || "",
    authorizationUsername: config.authorizationUsername || config.authUser || "",
    sipDomain: config.sipDomain || "",
    websocketUrl: config.websocketUrl || "",
    websocketPath: config.websocketPath || ""
  });
}

function configureWidget(config = {} as any) {
  const previousDiagnosticToken = widgetApiToken();
  const previousRegistrationConfiguration = registrationConfigurationFingerprint();
  const nextRuntimeConfig = normalizeWidgetConfig(config);
  if (Object.hasOwn(config, "widgetConfigToken") || Object.hasOwn(config, "widgetToken")) {
    // Both public aliases refer to one credential. A merged, older alias must
    // never take precedence over a freshly supplied host token.
    nextRuntimeConfig.widgetToken = config.widgetConfigToken || config.widgetToken || "";
    nextRuntimeConfig.widgetConfigToken = nextRuntimeConfig.widgetToken;
  }
  // A late host refresh for this same identity must not roll back a newer
  // verified token/TURN configuration received from another tab.
  if (credentialContextKey() === credentialContextKey(nextRuntimeConfig) &&
    newerWidgetCredential(previousDiagnosticToken, widgetApiToken(nextRuntimeConfig))) {
    for (const key of ["widgetToken", "widgetConfigToken", "widgetTokenExpiresAtUnix", "iceServers", "iceServersExpiresAtUnix"])
      nextRuntimeConfig[key] = runtimeConfig[key];
  }
  if (previousDiagnosticToken !== widgetApiToken(nextRuntimeConfig) || credentialContextKey() !== credentialContextKey(nextRuntimeConfig))
    credentialSync.cancel();
  if (previousRegistrationConfiguration !== registrationConfigurationFingerprint(nextRuntimeConfig)) {
    tabRpc.reset("Изменились настройки пользователя софтфона");
    mirroredOwnerState = undefined;
    mirroredOwnerStateAt = 0;
    mirroredOwnerId = "";
  }
  const registrationConfigurationChanged = (userAgent || registrationLeaseName) &&
    previousRegistrationConfiguration !== registrationConfigurationFingerprint(nextRuntimeConfig);
  runtimeConfig = nextRuntimeConfig;
  if (runtimeConfig.extension) extensionEl.value = runtimeConfig.extension;
  if (runtimeConfig.password) passwordEl.value = runtimeConfig.password;
  configureDiagnosticQueue(previousDiagnosticToken !== widgetApiToken());
  updateCredentialAdvertisement();
  transferTypeEl.value = normalizeTransferType(runtimeConfig.transferType, "blind");
  const phoneNumber = config.phoneNumber || config.number || config.phone;
  if (phoneNumber) {
    numberEl.value = normalizeDialString(phoneNumber);
    updateClearNumberButton();
  }
  updateAccountPanel(false);
  updateHeaderSubtitle(callStateTextEl.textContent);
  emitWidgetEvent("configured", {
    provider: runtimeConfig.provider || "",
    userId: runtimeConfig.userId || "",
    extension: extensionEl.value.trim(),
    authMode: runtimeConfig.authMode || "",
    telephonyProfileId: runtimeConfig.telephonyProfileId || "",
    telephonyKey: runtimeConfig.telephonyKey || "",
    hasSipProfile: Boolean(runtimeConfig.hasSipProfile),
    transferMode: runtimeConfig.transferMode || "auto",
    transferType: runtimeConfig.transferType || "blind",
    features: runtimeConfig.features || {},
    callOutcomeMode: runtimeConfig.callOutcomes?.mode || "off"
  });
  if (registrationConfigurationChanged) requestRegistrationConfigurationRestart();
  requestOwnerState();
}

function applySavedUser() {
  if (!isTestMode() || !canShowLocalSettings()) {
    saveUserEl.checked = false;
    updateAccountPanel(false);
    return;
  }
  const saved = savedUser();
  saveUserEl.checked = Boolean(saved?.enabled);
  if (!saved?.enabled) return;
  if (saved.extension) extensionEl.value = saved.extension;
  if (saved.password) passwordEl.value = saved.password;
  updateAccountPanel(false);
}

function applyTransferTypePreference() {
  if (!canShowLocalSettings()) {
    transferTypeEl.value = normalizeTransferType(runtimeConfig.transferType, "blind");
    return;
  }
  const savedType = localStorage.getItem(TRANSFER_TYPE_KEY);
  const type = normalizeTransferType(savedType || runtimeConfig.transferType, "blind");
  transferTypeEl.value = type;
  runtimeConfig.transferType = type;
}

function applyOutcomeModePreference() {
  const mode = normalizeCallOutcomeMode(
    canShowLocalSettings() ? localStorage.getItem("softphoneCallOutcomeMode") || runtimeConfig.callOutcomes?.mode : runtimeConfig.callOutcomes?.mode,
    "off"
  );
  runtimeConfig.callOutcomes = normalizeCallOutcomeConfig({
    ...runtimeConfig,
    callOutcomes: {
      ...(runtimeConfig.callOutcomes || {}),
      mode
    }
  });
  if (callOutcomeModeEl) callOutcomeModeEl.value = mode;
}

function audioConstraints() {
  const deviceId = runtimeConfig.audioInputDeviceId || localStorage.getItem(AUDIO_INPUT_KEY) || "";
  return deviceId ? { deviceId: { exact: deviceId } } : true;
}

function normalizeCodecOrder(value, fallback = ["opus", "alaw", "ulaw"]) {
  const aliases = new Map([
    ["pcma", "alaw"],
    ["pcmu", "ulaw"],
    ["g711a", "alaw"],
    ["g711u", "ulaw"]
  ]);
  const raw = Array.isArray(value) ? value : String(value || "").split(",");
  const codecs = raw
    .map((codec) => aliases.get(String(codec).trim().toLowerCase()) || String(codec).trim().toLowerCase())
    .filter((codec, index, all) => ["opus", "alaw", "ulaw"].includes(codec) && all.indexOf(codec) === index);
  return codecs.length ? codecs : fallback.slice();
}

function activeWebrtcCodecs() {
  return normalizeCodecOrder(
    runtimeConfig.codecPolicy?.webrtcCodecs || runtimeConfig.webrtcCodecs || runtimeConfig.codecs,
    ["opus", "alaw", "ulaw"]
  );
}

function codecNameFromRtpmap(line = "") {
  const match = line.match(/^a=rtpmap:(\d+)\s+([^/]+)/i);
  if (!match) return null;
  const codec = match[2].toLowerCase();
  if (codec === "pcma") return { payload: match[1], codec: "alaw" };
  if (codec === "pcmu") return { payload: match[1], codec: "ulaw" };
  if (codec === "opus") return { payload: match[1], codec: "opus" };
  return { payload: match[1], codec };
}

function reorderAudioCodecsInSdp(sdp = "", preferredCodecs = activeWebrtcCodecs()) {
  const lines = String(sdp || "").split(/\r\n|\n/);
  const audioLineIndex = lines.findIndex((line) => line.startsWith("m=audio "));
  if (audioLineIndex < 0) return sdp;
  const payloadCodecs = new Map();
  lines.forEach((line) => {
    const parsed = codecNameFromRtpmap(line);
    if (parsed) payloadCodecs.set(parsed.payload, parsed.codec);
  });
  const parts = lines[audioLineIndex].trim().split(/\s+/);
  const header = parts.slice(0, 3);
  const payloads = parts.slice(3);
  const priority = new Map(preferredCodecs.map((codec, index) => [codec, index]));
  const ordered = payloads.slice().sort((left, right) => {
    const leftPriority = priority.has(payloadCodecs.get(left)) ? priority.get(payloadCodecs.get(left)) : 100;
    const rightPriority = priority.has(payloadCodecs.get(right)) ? priority.get(payloadCodecs.get(right)) : 100;
    return leftPriority === rightPriority ? payloads.indexOf(left) - payloads.indexOf(right) : leftPriority - rightPriority;
  });
  lines[audioLineIndex] = [...header, ...ordered].join(" ");
  return lines.join("\r\n");
}

function codecSdpModifier() {
  const preferredCodecs = activeWebrtcCodecs();
  return (description) => Promise.resolve({
    type: description.type,
    sdp: reorderAudioCodecsInSdp(description.sdp, preferredCodecs)
  });
}

function mediaOfferOptions(extra = {} as any) {
  return {
    sessionDescriptionHandlerModifiers: [codecSdpModifier()],
    sessionDescriptionHandlerOptions: {
      constraints: { audio: audioConstraints(), video: false },
      ...(extra.sessionDescriptionHandlerOptions || {})
    },
    ...extra
  };
}

function applyAudioOutput(deviceId?: string, persist = false): Promise<void> {
  // One physical output: serialize attachment and user selection, committing the
  // preference with the corresponding native result. Resolve defaults at execution
  // time so a queued attachment cannot restore a superseded headset selection.
  const operation = audioOutputTask.then(async () => {
    const target = deviceId ?? (runtimeConfig.audioOutputDeviceId || localStorage.getItem(AUDIO_OUTPUT_KEY) || "");
    if (typeof remoteAudio.setSinkId !== "function") {
      if (target) throw new DOMException("Браузер не поддерживает выбор динамика", "NotSupportedError");
    } else if (remoteAudio.sinkId !== target) {
      // Empty string explicitly selects the system default, not "no change".
      await remoteAudio.setSinkId(target);
    }
    if (persist) {
      runtimeConfig.audioOutputDeviceId = target;
      localStorage.setItem(AUDIO_OUTPUT_KEY, target);
    }
  });
  // Keep the queue usable after rejection; the caller still gets the rejection.
  audioOutputTask = operation.catch(() => {});
  return operation;
}

function renderAudioDeviceOptions(devices = []) {
  const inputId = runtimeConfig.audioInputDeviceId || localStorage.getItem(AUDIO_INPUT_KEY) || "";
  const outputId = runtimeConfig.audioOutputDeviceId || localStorage.getItem(AUDIO_OUTPUT_KEY) || "";
  const fill = (select, kind, selectedId) => {
    if (!select) return;
    select.replaceChildren(new Option("По умолчанию", ""));
    devices
      .filter((device) => device.kind === kind)
      .forEach((device, index) => {
        const label = device.label || (kind === "audioinput" ? `Микрофон ${index + 1}` : `Динамик ${index + 1}`);
        select.append(new Option(label, device.deviceId));
      });
    select.value = selectedId;
  };
  fill(audioInputEl, "audioinput", inputId);
  fill(audioOutputEl, "audiooutput", outputId);
  renderDeviceDropdown(audioInputEl, audioInputTextEl, audioInputOptionsEl);
  renderDeviceDropdown(audioOutputEl, audioOutputTextEl, audioOutputOptionsEl);
}

function closeDeviceDropdowns(except?: Element) {
  [
    [audioInputButtonEl, audioInputOptionsEl],
    [audioOutputButtonEl, audioOutputOptionsEl]
  ].forEach(([button, options]) => {
    if (!button || !options || options === except) return;
    options.hidden = true;
    button.setAttribute("aria-expanded", "false");
  });
}

function renderDeviceDropdown(select, textEl, optionsEl) {
  if (!select || !textEl || !optionsEl) return;
  const selectedOption = select.options[select.selectedIndex] || select.options[0];
  textEl.textContent = selectedOption?.textContent || "По умолчанию";
  optionsEl.replaceChildren();
  [...select.options].forEach((option) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "device-select-option";
    button.dataset.value = option.value;
    button.classList.toggle("is-selected", option.value === select.value);
    const label = document.createElement("span");
    label.textContent = option.textContent;
    button.append(label);
    button.addEventListener("click", () => {
      select.value = option.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      closeDeviceDropdowns();
    });
    optionsEl.append(button);
  });
}

async function toggleDeviceDropdown(kind) {
  const isInput = kind === "input";
  const button = isInput ? audioInputButtonEl : audioOutputButtonEl;
  const options = isInput ? audioInputOptionsEl : audioOutputOptionsEl;
  if (!button || !options) return;
  const opened = options.hidden;
  closeDeviceDropdowns(options);
  if (opened) {
    await refreshAudioDevices({ requestPermission: isInput });
  }
  options.hidden = !opened;
  button.setAttribute("aria-expanded", String(opened));
}

async function refreshAudioDevices(options = {} as any) {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  if (options.requestPermission) {
    const stream = await captureMicrophone({ audio: true, video: false });
    stream.getTracks().forEach((track) => track.stop());
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  renderAudioDeviceOptions(devices);
  emitWidgetEvent("audio_devices_changed", {
    inputs: devices.filter((device) => device.kind === "audioinput").length,
    outputs: devices.filter((device) => device.kind === "audiooutput").length,
    outputSelectionSupported: typeof remoteAudio.setSinkId === "function"
  });
  return devices.map((device) => ({
    deviceId: device.deviceId,
    kind: device.kind,
    label: device.label
  }));
}

async function setAudioDevices(options = {} as any) {
  if ("audioInputDeviceId" in options || "inputDeviceId" in options) {
    runtimeConfig.audioInputDeviceId = options.audioInputDeviceId || options.inputDeviceId || "";
    localStorage.setItem(AUDIO_INPUT_KEY, runtimeConfig.audioInputDeviceId);
  }
  if ("audioOutputDeviceId" in options || "outputDeviceId" in options) {
    const outputDeviceId = options.audioOutputDeviceId || options.outputDeviceId || "";
    const attachedSession = remoteAudioSession;
    const attachedStream = remoteAudio.srcObject;
    const generation = remoteAudioPlaybackGeneration;
    try { await applyAudioOutput(outputDeviceId, true); }
    catch (error) {
      // Only translate the command's failure; keep the device selection and
      // native cause intact. No fallback, retry or extra failure event.
      throw Object.assign(new Error(audioFailureMessage(error, "output"), { cause: error }), {
        name: error?.name || "Error"
      });
    }
    // Explicit output selection may recover a paused/failed player. Never
    // reattach a different call, clear hold/mute, or wait for media to arrive.
    void startRemoteAudioPlayback(attachedSession, attachedStream, generation);
  }
  renderAudioDeviceOptions(await navigator.mediaDevices?.enumerateDevices?.() || []);
  emitWidgetEvent("audio_device_selected", {
    audioInputDeviceId: runtimeConfig.audioInputDeviceId || "",
    audioOutputDeviceId: runtimeConfig.audioOutputDeviceId || ""
  });
  return getSoftphoneState();
}

function closeSettings() {
  const dropdown = $("settingsDropdown");
  dropdown.hidden = true;
  $("settingsToggle").classList.remove("is-active");
  $("settingsToggle").setAttribute("aria-label", "Настройки");
}

function accountTitle() {
  return isSipReady() ? `Внутренний ${extensionEl.value.trim()}` : "Не подключен";
}

function updateHeaderSubtitle(text) {
  const account = accountTitle();
  accountLabelEl.textContent = !text || text === account ? account : `${text} * ${account}`;
}

function setCallUiState(state, text) {
  callStateEl.dataset.state = state;
  headerStatusDotEl.dataset.state = state;
  callStateTextEl.textContent = text;
  updateHeaderSubtitle(text);
  document.body.dataset.callState = state;
  emitWidgetEvent("state", { ...getSoftphoneState(), status: state });
}

function setConnected(connected) {
  const strictBlocked = outcomeBlockingStrict();
  $("connect").disabled = strictBlocked || connected;
  $("disconnect").disabled = strictBlocked || !connected;
  updateHeaderSubtitle(callStateTextEl.textContent);
  updatePrimaryButtons();
  updateAccountPanel(false);
}

function updateNotificationButton() {
  const button = $("enableNotifications");
  const enabled = notificationsEnabled && "Notification" in window && Notification.permission === "granted";
  button.classList.toggle("is-active", enabled);
  button.setAttribute("aria-label", enabled ? "Уведомления включены" : "Включить уведомления");
}

function getSipDomain() {
  if (runtimeConfig.sipDomain) return runtimeConfig.sipDomain;
  return window.location.hostname === "127.0.0.1" ? "localhost" : window.location.hostname;
}

function getWebSocketServer() {
  if (runtimeConfig.websocketUrl) return runtimeConfig.websocketUrl;
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${window.location.host}${runtimeConfig.websocketPath || "/ws"}`;
}

function defaultIceServers() {
  const host = window.location.hostname;
  if (["localhost", "127.0.0.1", "::1"].includes(host)) return [];
  return [{ urls: [`stun:${host}:3478`] }];
}

function normalizeDialString(value) {
  return value.trim().replace(/[^\d+*#]/g, "");
}

function normalizeOutboundDialString(value) {
  const normalized = normalizeDialString(value);
  if (/^8\d{10}$/.test(normalized)) return `+7${normalized.slice(1)}`;
  if (/^7\d{10}$/.test(normalized)) return `+${normalized}`;
  if (/^9\d{9}$/.test(normalized)) return `+7${normalized}`;
  return normalized;
}

function makeSipUri(user, domain) {
  const cleanDomain = domain.split(":")[0];
  return new SIP.URI("sip", user, cleanDomain);
}

async function prepareCallContext(call) {
  requireCurrentCall(call);
  if (call.session.state !== SIP.SessionState.Established) throw new Error("Нет активного разговора");
  const response = await fetchWidgetAction(apiUrl("/api/widget/calls/context"), {
    method: "POST",
    headers: widgetApiHeaders({ json: true }),
    body: JSON.stringify({
      extension: extensionEl.value.trim(),
      sipCallId: call.session.request?.callId,
      customerPhone: call.customerPhone
    })
  });
  requireCurrentCall(call);
  if (!response.ok) throw new Error("Не удалось подтвердить данные переводимого звонка");
}

async function setRemoteActionLock(extensions = [], payload = {} as any) {
  const cleanExtensions = [...new Set(extensions.map((item) => normalizeDialString(item)).filter(Boolean))];
  if (!cleanExtensions.length) return;
  await fetchWidgetAction(apiUrl("/api/widget/calls/action-lock"), {
    method: "POST",
    headers: widgetApiHeaders({ json: true }),
    body: JSON.stringify({
      extensions: cleanExtensions,
      sourceExtension: extensionEl.value.trim(),
      ...payload
    })
  }).catch(() => {});
}

async function clearRemoteActionLock(extensions = [], payload = {} as any) {
  const cleanExtensions = [...new Set(extensions.map((item) => normalizeDialString(item)).filter(Boolean))];
  if (!cleanExtensions.length && !payload.room) return;
  await fetchWidgetAction(apiUrl("/api/widget/calls/action-lock"), {
    method: "DELETE",
    headers: widgetApiHeaders({ json: true }),
    body: JSON.stringify({
      extensions: cleanExtensions,
      sourceExtension: extensionEl.value.trim(),
      ...payload
    })
  }).catch(() => {});
}

function setInCall(inCall) {
  const strictBlocked = outcomeBlockingStrict();
  $("mute").disabled = strictBlocked || !inCall;
  $("hold").disabled = strictBlocked || !inCall;
  $("callActions").disabled = strictBlocked ||
    !inCall ||
    Boolean(callActionLock) ||
    transferInProgress ||
    callHasActiveConference() ||
    (!featureEnabled("transfer") && !featureEnabled("conference"));
}

function activeTransferType() {
  return normalizeTransferType(canShowLocalSettings() ? transferTypeEl.value : runtimeConfig.transferType, "blind");
}

function applyMediaState() {
  const managedCalls = liveCalls();
  if (managedCalls.length) {
    // An unconfirmed hold can leak audio to another call. ICE repair does not
    // change hold state: keep its SIP operation serialized, but preserve audio.
    const canTransmit = call => isCurrentCall(call) && !call.held &&
      !(call.holdOperation?.timedOut && !call.holdOperation.iceRestart) &&
      ["calling", "answering", "talking"].includes(call.phase);
    const foreground = managedCalls.find(call => call.id === activeMediaCallId && canTransmit(call)) ||
      managedCalls.find(call => call.id === selectedCallId && canTransmit(call)) || managedCalls.find(canTransmit);
    managedCalls.forEach((call) => {
      const pc = call.session?.sessionDescriptionHandler?.peerConnection;
      if (!pc) return;
      pc.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === "audio") sender.track.enabled = call === foreground && !call.muted;
      });
    });
    const mediaCall = calls.get(activeMediaCallId) || callBySession(remoteAudioSession);
    remoteAudio.muted = Boolean(mediaCall?.held);
    return;
  }
  const pc = session?.sessionDescriptionHandler?.peerConnection;
  if (pc) {
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") sender.track.enabled = !(muted || held);
    });
  }
  remoteAudio.muted = held && remoteAudioSession === session;
}

function updateControlButtons() {
  const muteButton = $("mute");
  const holdButton = $("hold");
  const actionsButton = $("callActions");
  const call = selectedCall();
  muted = Boolean(call?.muted);
  held = Boolean(call?.held);
  muteButton.classList.toggle("is-muted", muted);
  holdButton.classList.toggle("is-held", held);
  muteButton.classList.toggle("is-active", muted);
  holdButton.classList.toggle("is-active", held);
  actionsButton.classList.toggle("is-active", transferInProgress || !$("callActionsMenu").hidden);
  actionsButton.classList.toggle("has-conference", Boolean(call?.conference?.active));
  actionsButton.disabled = outcomeBlockingStrict() ||
    !call ||
    call.session?.state !== SIP.SessionState.Established ||
    transferInProgress ||
    callHasActiveConference(call) ||
    (!featureEnabled("transfer") && !featureEnabled("conference"));
  muteButton.setAttribute("aria-label", muted ? "Включить микрофон" : "Выключить микрофон");
  holdButton.setAttribute("aria-label", held ? "Вернуть звонок" : "Удержать");
  actionsButton.setAttribute("aria-label", call?.conference?.active ? "Конференция активна" : (transferInProgress ? "Действие выполняется" : "Действия со звонком"));
  if (callPhase === "talking") updateCallDisplay();
}

async function setSipHold(nextHeld, options = {} as any) {
  const call = options.call || selectedCall();
  requireCurrentCall(call);
  const activeSession = call.session;
  if (!activeSession || activeSession.state !== SIP.SessionState.Established) return;
  if (call.holdOperation) throw new Error("Предыдущее изменение удержания ещё не подтверждено телефонией");
  const iceRestart = options.iceRestart === true;
  if (Boolean(call?.held ?? held) === nextHeld && !iceRestart) return;
  const previousHold = Boolean(call?.held ?? held);
  const sessionDescriptionHandlerOptions = {
    ...(activeSession.sessionDescriptionHandlerOptionsReInvite || {}),
    hold: nextHeld,
    ...(iceRestart ? { offerOptions: { iceRestart: true } } : {})
  };
  const operation = { timedOut: false, iceRestart };
  call.holdOperation = operation;
  emitWidgetEvent("state", getSoftphoneState());
  const signal = callSetup(activeSession).controller.signal;
  await new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error?) => {
      if (settled) return;
      settled = true;
      if (error) reject(error); else resolve(undefined);
    };
    const cleanup = () => {
      clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
      if (call.holdOperation === operation) delete call.holdOperation;
    };
    const abort = () => { cleanup(); finish(new Error("Звонок завершён во время изменения удержания")); };
    const complete = (accepted: boolean, error?) => {
      const current = isCurrentCall(call) && call.holdOperation === operation;
      cleanup();
      if (!current) { finish(new Error("Этот звонок уже завершён")); return; }
      call.held = accepted ? nextHeld : previousHold;
      call.phase = call.held ? "held" : "talking";
      if (selectedCallId === call.id) syncGlobalsFromCall(call);
      applyMediaState();
      updateControlButtons(); updateCallDisplay(); renderCallStack();
      if (accepted && options.emit !== false && !iceRestart) emitWidgetEvent(nextHeld ? "held" : "resumed", { callId: call.id, mode: "sip-hold" });
      else emitWidgetEvent("state", getSoftphoneState());
      finish(error);
    };
    const timeout = setTimeout(() => {
      operation.timedOut = true;
      applyMediaState(); // The remote hold state is unknown: never leak audio to a second call.
      emitWidgetEvent("state", getSoftphoneState());
      finish(new Error(iceRestart ? "Телефония не подтвердила восстановление звука" : "Телефония не подтвердила удержание. Дождитесь ответа или завершите этот звонок"));
    }, 8000);
    signal.addEventListener("abort", abort, { once: true });
    Promise.resolve().then(() => {
      requireCurrentCall(call);
      return activeSession.invite({
      sessionDescriptionHandlerOptions,
      sessionDescriptionHandlerModifiers: [codecSdpModifier()],
      requestDelegate: {
        onAccept: () => complete(true),
        onReject: () => complete(false, new Error(iceRestart ? "Восстановление звука отклонено" : nextHeld ? "Удержание отклонено" : "Возврат с удержания отклонен"))
      }
      });
    }).catch(error => complete(false, error));
  });
}

// One recovery episode per live call, serialized with hold/answer/transfer.
// Never redial, steal ownership or resurrect an ended session to repair audio.
function scheduleMediaRecovery(call) {
  if (!isCurrentCall(call)) return;
  const peer = call.session?.sessionDescriptionHandler?.peerConnection;
  const state = peer?.iceConnectionState;
  if (state === "connected" || state === "completed") {
    clearTimeout(call.mediaRecoveryTimer); call.mediaRecoveryTimer = undefined;
    call.mediaRecoveryAttempts = 0;
    return;
  }
  if (call.session?.state !== SIP.SessionState.Established || !["failed", "disconnected"].includes(state)
    || call.mediaRecoveryTimer || (call.mediaRecoveryAttempts || 0) >= 2) return;
  call.mediaRecoveryTimer = setTimeout(async () => {
    call.mediaRecoveryTimer = undefined;
    if (!isCurrentCall(call) || !["failed", "disconnected"].includes(peer.iceConnectionState)) return;
    if (!registrationReady || !transportConnected || registrationStandby) return; // registration resumes this episode
    if (callControl.busy || call.holdOperation || peer.signalingState !== "stable") {
      call.mediaRecoveryTimer = setTimeout(() => { call.mediaRecoveryTimer = undefined; scheduleMediaRecovery(call); }, 2000);
      return;
    }
    call.mediaRecoveryAttempts = (call.mediaRecoveryAttempts || 0) + 1;
    diagnosticQueue.record("media_recovery_started", { callId: call.id, ...callMediaDiagnosticContext(call.session) });
    try { await callControl.run(() => setSipHold(Boolean(call.held), { call, iceRestart: true, emit: false })); }
    catch (error) {
      if (isCurrentCall(call)) emitWidgetEvent("media_recovery_failed", { callId: call.id, message: error.message });
    }
    finally { scheduleMediaRecovery(call); }
  }, call.mediaRecoveryAttempts ? 2000 : state === "failed" ? 0 : 5000);
}

function notifyStatusCode(notification) {
  const body = String(notification?.request?.body || "");
  const match = body.match(/SIP\/2\.0\s+(\d{3})/i);
  return match ? Number(match[1]) : 0;
}

async function completeTransfer(activeSession, payload) {
  if (!activeSession || activeSession.state !== SIP.SessionState.Established) return;
  const call = callBySession(activeSession);
  suppressOutcomeForCall(call, "transferred");
  emitWidgetEvent("transfer_completed", { ...payload, method: "sip-refer" });
  showToast("Перевод выполнен");
  await activeSession.bye();
}

function referSession(activeSession, referTo, payload, options = {} as any) {
  return activeSession.refer(referTo, {
    onNotify: (notification) => {
      notification.accept();
      const statusCode = notifyStatusCode(notification);
      if (statusCode >= 100 && statusCode < 200) {
        setStatus("Перевод звонка");
        setTransferStatus("Перевод выполняется");
        return;
      }
      if (statusCode >= 200 && statusCode < 300) {
        options.onSuccess?.(statusCode);
        return;
      }
      if (statusCode >= 300) {
        options.onFailure?.(statusCode);
      }
    },
    requestDelegate: {
      onAccept: () => {
        setStatus("Перевод звонка");
      },
      onReject: (response) => {
        options.onRejected?.(response);
      }
    }
  });
}

function closeTransferPanel() {
  $("transferPanel").hidden = true;
  $("transferStatus").hidden = true;
  $("transferConsultActions").hidden = true;
  transferSearchQuery = "";
  const search = $("transferSearch");
  if (search) search.value = "";
}

function closeCallActionsMenu() {
  $("callActionsMenu").hidden = true;
  $("callActions").classList.remove("is-active");
}

function callHasActiveConference(call = selectedCall()) {
  return Boolean(call?.conference?.active);
}

function setTransferStatus(text) {
  const status = $("transferStatus");
  status.textContent = text;
  status.hidden = !text;
}

function setConsultActionsVisible(visible) {
  $("transferConsultActions").hidden = !visible;
}

function setTransferBusy(busy) {
  transferInProgress = busy;
  updatePrimaryButtons();
  updateControlButtons();
}

function transferTargetsUrl() {
  const query = new URLSearchParams();
  const userId = runtimeConfig.userId || "";
  const extension = extensionEl.value.trim();
  if (userId) query.set("userId", userId);
  else if (extension) query.set("extension", extension);
  return `/api/widget/transfer-targets?${query.toString()}`;
}

function transferModeLabel(target) {
  const mode = target?.transfer?.mode;
  if (mode === "provider") return "Через SIP АТС";
  if (mode === "gateway") return "Внутри Asterisk";
  return target?.transfer?.reason || "Недоступно";
}

function targetStatusMeta(target) {
  if (target?.inCall || target?.status === "in_call") return { label: "В разговоре", state: "busy" };
  if (target?.registered === true || target?.status === "available") return { label: "Онлайн", state: "online" };
  if (target?.registered === false || target?.status === "offline") return { label: "Офлайн", state: "offline" };
  return { label: "Статус уточняется", state: "unknown" };
}

function visibleActionTargets(targets = []) {
  const query = transferSearchQuery.trim().toLowerCase();
  if (query.length < 3) return [];
  return targets.filter((target) => {
    const state = targetStatusMeta(target).state;
    if (state === "offline" || state === "unknown") return false;
    const haystack = [
      target.label,
      target.displayName,
      target.name,
      target.extension,
      target.userId
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(query);
  });
}

function renderTransferTargets(targets) {
  const list = $("transferList");
  const empty = $("transferEmpty");
  const visibleTargets = visibleActionTargets(targets);
  list.replaceChildren();
  empty.hidden = visibleTargets.length > 0;
  empty.textContent = transferSearchQuery.trim().length < 3
    ? "Введите минимум 3 символа"
    : "Нет доступных пользователей";

  visibleTargets.forEach((target) => {
    const statusMeta = targetStatusMeta(target);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "transfer-target";
    button.disabled = !target.transfer?.available;

    const main = document.createElement("span");
    main.className = "transfer-target-main";
    const name = document.createElement("span");
    name.className = "transfer-target-name";
    const indicator = document.createElement("span");
    indicator.className = "transfer-target-dot";
    indicator.dataset.state = statusMeta.state;
    const label = document.createElement("span");
    label.textContent = target.label || target.displayName || target.extension;
    name.append(indicator, label);
    const meta = document.createElement("span");
    meta.className = "transfer-target-meta";
    meta.textContent = target.extension;
    main.append(name, meta);

    const mode = document.createElement("span");
    mode.className = "transfer-target-mode";
    mode.textContent = targetPanelMode === "conference" ? "В помощь" : transferModeLabel(target);

    button.append(main, mode);
    button.addEventListener("click", () => {
      if (targetPanelMode === "conference") {
        inviteToConference(target).catch((error) => {
          showToast(error.message);
          setStatus(error.message);
        });
        return;
      }
      const type = activeTransferType();
      emitWidgetEvent("transfer_target_selected", {
        sourceUserId: runtimeConfig.userId || "",
        sourceExtension: extensionEl.value.trim(),
        sourceProvider: runtimeConfig.provider || "",
        sourceTelephonyProfileId: runtimeConfig.telephonyProfileId || "",
        targetUserId: target.userId,
        targetExtension: target.extension,
        targetDisplayName: target.displayName || target.name || "",
        targetProvider: target.provider || "",
        targetTelephonyProfileId: target.telephonyProfileId || "",
        targetTelephonyKey: target.telephonyKey || "",
        targetHasSipProfile: Boolean(target.hasSipProfile),
        mode: target.transfer?.mode || "",
        transferReason: target.transfer?.reason || "",
        transferAvailable: Boolean(target.transfer?.available),
        transferType: type
      });
      showToast(type === "attended" ? "Консультация перед переводом" : "Перевод звонка");
      transfer(target, { mode: target.transfer?.mode || "auto", transferType: type }).catch((error) => {
        showToast(error.message);
        setStatus(error.message);
      });
    });
    list.append(button);
  });
}

function openTargetPanel(mode) {
  if (!session || session.state !== SIP.SessionState.Established) return;
  if (mode === "transfer" && !featureEnabled("transfer")) {
    showToast("Перевод отключен");
    closeCallActionsMenu();
    return;
  }
  if (mode === "conference" && !featureEnabled("conference")) {
    showToast("Конференции отключены");
    closeCallActionsMenu();
    return;
  }
  if (mode === "transfer" && callHasActiveConference()) {
    showToast("Перевод недоступен во время конференции");
    closeCallActionsMenu();
    return;
  }
  if (mode === "conference" && callHasActiveConference()) {
    showToast("Помощник уже подключен");
    closeCallActionsMenu();
    return;
  }
  targetPanelMode = mode;
  $("targetPanelTitle").textContent = mode === "conference" ? "Позвать в звонок" : "Перевести звонок";
  transferSearchQuery = "";
  $("transferSearch").value = "";
  setTransferStatus("");
  setConsultActionsVisible(false);
  closeCallActionsMenu();
  loadTransferTargets().catch((error) => {
    renderTransferTargets([]);
    showToast(error.message);
    emitWidgetEvent("transfer_targets_failed", {
      code: "TRANSFER_TARGETS_FAILED",
      message: error.message,
      recoverable: true
    });
  });
}

function setTransferTargets(targets = []) {
  transferTargets = Array.isArray(targets) ? targets : [];
  $("transferPanel").hidden = false;
  renderTransferTargets(transferTargets);
  return getSoftphoneState();
}

async function loadTransferTargets() {
  $("transferPanel").hidden = false;
  $("transferEmpty").hidden = true;
  $("transferList").replaceChildren();
  showToast(targetPanelMode === "conference" ? "Загрузка списка для приглашения" : "Загрузка списка для перевода");
  emitWidgetEvent("transfer_targets_requested");
  const response = await fetch(apiUrl(transferTargetsUrl()), { headers: widgetApiHeaders() });
  if (!response.ok) throw new Error("Не удалось получить список");
  const payload = await response.json();
  transferTargets = Array.isArray(payload.targets) ? payload.targets : [];
  renderTransferTargets(transferTargets);
  emitWidgetEvent("transfer_targets_loaded", { count: transferTargets.length });
}

async function transfer(target, options = {} as any) {
  return callControl.run(() => transferInternal(target, options));
}

async function transferInternal(target, options = {} as any) {
  if (options.callId && !isCurrentCall(calls.get(options.callId))) throw new Error("Этот звонок уже завершён");
  if (options.callId) await selectCall(options.callId, { attach: true });
  if (!featureEnabled("transfer")) throw new Error("Перевод отключен");
  if (!activeCall?.id) throw new Error("Нет активного звонка");
  if (!session || session.state !== SIP.SessionState.Established) throw new Error("Нет активного разговора");
  if (activeCall.conference?.active) throw new Error("Перевод недоступен во время конференции");
  if (transferInProgress) throw new Error("Перевод уже выполняется");
  const targetPayload = typeof target === "string" ? { targetExtension: target } : { ...(target || {}) };
  if (targetPayload.targetCallId) return transferToExistingCall(targetPayload.targetCallId, options);
  const payload = {
    userId: runtimeConfig.userId || "",
    sourceUserId: runtimeConfig.userId || "",
    extension: extensionEl.value.trim(),
    sourceExtension: extensionEl.value.trim(),
    sourceProvider: runtimeConfig.provider || "",
    sourceTelephonyProfileId: runtimeConfig.telephonyProfileId || "",
    sourceTelephonyKey: runtimeConfig.telephonyKey || "",
    sourceHasSipProfile: Boolean(runtimeConfig.hasSipProfile),
    callId: activeCall.id,
    targetUserId: targetPayload.userId || targetPayload.targetUserId || "",
    targetExtension: targetPayload.extension || targetPayload.targetExtension || "",
    targetDisplayName: targetPayload.displayName || targetPayload.name || "",
    targetProvider: targetPayload.provider || "",
    targetTelephonyProfileId: targetPayload.telephonyProfileId || "",
    targetTelephonyKey: targetPayload.telephonyKey || "",
    targetHasSipProfile: Boolean(targetPayload.hasSipProfile),
    mode: options.mode || targetPayload.mode || runtimeConfig.transferMode || "auto",
    transferMode: options.mode || targetPayload.mode || runtimeConfig.transferMode || "auto",
    transferReason: targetPayload.transfer?.reason || "",
    transferAvailable: targetPayload.transfer?.available ?? true,
    transferFallbackMode: targetPayload.transfer?.fallbackMode || "",
    transferCapability: targetPayload.transfer || null,
    transferType: normalizeTransferType(options.transferType || targetPayload.transferType || runtimeConfig.transferType, "blind"),
    requestId: options.requestId || targetPayload.requestId || crypto.randomUUID()
  };
  if (!payload.targetExtension && !payload.targetUserId) throw new Error("Не выбран пользователь для перевода");
  if (payload.transferType === "attended") return startAttendedTransfer(payload);
  return startBlindTransfer(payload);
}

async function transferToExistingCall(targetCallId, options = {} as any) {
  const sourceCall = selectedCall();
  const targetCall = calls.get(targetCallId);
  if (!sourceCall?.session || sourceCall.session.state !== SIP.SessionState.Established) throw new Error("Нет активного разговора");
  if (!targetCall?.session || targetCall.session.state !== SIP.SessionState.Established) throw new Error("Целевая линия недоступна");
  if (sourceCall.id === targetCall.id) throw new Error("Нельзя перевести звонок на самого себя");
  await prepareCallContext(sourceCall);
  await prepareCallContext(targetCall);
  requireCurrentCall(sourceCall);
  const payload = {
    extension: extensionEl.value.trim(),
    callId: sourceCall.id,
    targetCallId,
    mode: options.mode || "gateway",
    transferType: "attended",
    requestId: options.requestId || crypto.randomUUID()
  };
  setTransferBusy(true);
  setTransferStatus("Перевод выполняется");
  emitWidgetEvent("transfer_started", { ...payload, method: "sip-refer-replaces" });
  await referSession(sourceCall.session, targetCall.session, payload, {
    onSuccess: () => {
      setTransferBusy(false);
      removeCall(sourceCall);
      removeCall(targetCall);
      sourceCall.session.bye?.().catch(() => {});
      targetCall.session.bye?.().catch(() => {});
      emitWidgetEvent("transfer_completed", { ...payload, method: "sip-refer-replaces" });
      showToast("Перевод выполнен");
      closeTransferPanel();
    },
    onFailure: () => {
      setTransferBusy(false);
      setTransferStatus("");
      emitWidgetEvent("transfer_failed", {
        ...payload,
        method: "sip-refer-replaces",
        code: "TRANSFER_REFER_FAILED",
        message: "Перевод отклонен телефонией",
        recoverable: true
      });
    },
    onRejected: (response) => {
      setTransferBusy(false);
      setTransferStatus("");
      emitWidgetEvent("transfer_failed", {
        ...payload,
        method: "sip-refer-replaces",
        code: "TRANSFER_REFER_REJECTED",
        message: response?.message?.reasonPhrase || "Перевод отклонен",
        recoverable: true
      });
    }
  }).catch((error) => {
    setTransferBusy(false);
    setTransferStatus("");
    throw error;
  });
  return { ok: true, transfer: { ...payload, state: "requested" } };
}

async function conferenceCalls(options = {} as any) {
  return callControl.run(() => conferenceCallsInternal(options));
}

async function conferenceCallsInternal(options = {} as any) {
  if (!featureEnabled("conference")) throw new Error("Конференции отключены");
  const targetExtensions = [...new Set((Array.isArray(options.targetExtensions)
    ? options.targetExtensions : options.targetExtension ? [options.targetExtension] : [])
    .map((extension) => normalizeDialString(extension)).filter(Boolean))];
  const callIds = Array.isArray(options.callIds) && options.callIds.length
    ? options.callIds
    : targetExtensions.length ? [selectedCallId] : liveCalls()
        .filter((call) => call.session?.state === SIP.SessionState.Established)
        .map((call) => call.id);
  if (new Set(callIds).size !== callIds.length) throw new Error("Выберите разные звонки");
  const selectedCalls = callIds.map((id) => calls.get(id));
  if (selectedCalls.length < 1) throw new Error("Нет активного разговора");
  selectedCalls.forEach(requireCurrentCall);
  if (selectedCalls.length < 2 && !targetExtensions.length) throw new Error("Выберите, кого позвать");
  if (selectedCalls.some((call) => callHasActiveConference(call))) throw new Error("Помощник уже подключен");
  for (const call of selectedCalls) await prepareCallContext(call);
  const payload = {
    userId: runtimeConfig.userId || "",
    extension: extensionEl.value.trim(),
    callIds: selectedCalls.map((call) => call.id),
    sipCallIds: selectedCalls.map((call) => call.session.request?.callId),
    targetExtensions,
    requestId: options.requestId || crypto.randomUUID()
  };
  await setRemoteActionLock([payload.extension, ...payload.targetExtensions], {
    reason: "conference",
    callId: payload.callIds[0] || "",
    targetExtension: payload.targetExtensions[0] || ""
  });
  let result;
  try {
    selectedCalls.forEach(requireCurrentCall);
    emitWidgetEvent("conference_requested", {
      callIds: payload.callIds,
      targetExtensions: payload.targetExtensions,
      phones: selectedCalls.map((call) => call.phone)
    });
    const response = await fetchWidgetAction(apiUrl("/api/widget/calls/conference"), {
      method: "POST",
      headers: widgetApiHeaders({ json: true }),
      body: JSON.stringify(payload)
    });
    result = await response.json().catch(() => ({}));
    if (selectedCalls.some(call => !isCurrentCall(call))) throw new Error("Состав звонков изменился во время создания конференции");
    if (!response.ok || result.ok === false) throw new Error(result.error?.message || result.error || "Конференция не запущена");
  } catch (error) {
    await clearRemoteActionLock([payload.extension, ...payload.targetExtensions]);
    emitWidgetEvent("conference_failed", {
      ...payload,
      code: result?.error?.code || "CONFERENCE_FAILED",
      message: error.message,
      recoverable: true
    });
    throw error;
  }
  emitWidgetEvent("conference_started", { ...payload, result });
  selectedCalls.forEach((call) => {
    call.conference = {
      active: true,
      room: result.result?.room || "",
      targetExtensions: payload.targetExtensions,
      participantCount: result.result?.participantCount || 0,
      hadTwoParticipants: false,
      autoCloseAfter: Date.now() + 10000,
      startedAt: new Date().toISOString()
    };
    rememberCall(call);
  });
  if (result.result?.room) {
    await setRemoteActionLock([payload.extension, ...payload.targetExtensions], {
      reason: "conference",
      room: result.result.room,
      callId: payload.callIds[0] || "",
      targetExtension: payload.targetExtensions[0] || ""
    });
  }
  startConferenceStatusMonitor();
  updateControlButtons();
  showToast("Конференция запущена");
  return result;
}

function stopConferenceStatusMonitor() {
  conferenceStatusRequest?.abort();
  conferenceStatusRequest = undefined;
  if (!conferenceStatusTimer) return;
  clearInterval(conferenceStatusTimer);
  conferenceStatusTimer = undefined;
}

function activeConferenceCalls() {
  return liveCalls().filter((call) => call.conference?.active && call.conference?.room);
}

function startConferenceStatusMonitor() {
  stopConferenceStatusMonitor();
  if (!activeConferenceCalls().length) return;
  conferenceStatusTimer = setInterval(checkConferenceStatuses, 2500);
}

async function checkConferenceStatuses() {
  if (conferenceStatusRequest) return;
  const activeConferences = activeConferenceCalls();
  if (!activeConferences.length) {
    stopConferenceStatusMonitor();
    return;
  }
  const request = new CallSetup();
  conferenceStatusRequest = request;
  const timeout = setTimeout(() => request.abort(), 8000);
  try { await request.run(() => Promise.all(activeConferences.map(async (call) => {
    const room = call.conference.room;
    const current = () => !request.controller.signal.aborted && isCurrentCall(call) &&
      call.conference?.active && call.conference.room === room;
    const canAutoClose = Boolean(call.conference.hadTwoParticipants) && Date.now() >= Number(call.conference.autoCloseAfter || 0);
    const query = new URLSearchParams({
      room,
      extension: extensionEl.value.trim(),
      callId: call.id,
      autoClose: canAutoClose ? "1" : "0"
    });
    const response = await fetch(apiUrl(`/api/widget/calls/conference/status?${query.toString()}`), {
      headers: widgetApiHeaders(), signal: request.controller.signal
    });
    const status = await response.json().catch(() => ({}));
    if (!current()) return;
    if (!response.ok || status.ok === false) {
      emitWidgetEvent("conference_status_failed", {
        callId: call.id,
        room: call.conference.room,
        code: status.error?.code || "CONFERENCE_STATUS_FAILED",
        message: status.error?.message || "Не удалось получить статус конференции",
        recoverable: true
      });
      return;
    }
    const participantCount = Number(status.conference.participantCount || 0);
    const hadTwoParticipants = Boolean(call.conference.hadTwoParticipants) || participantCount >= 2;
    call.conference = {
      ...call.conference,
      participantCount,
      hadTwoParticipants,
      participants: status.conference.participants,
      updatedAt: new Date().toISOString()
    };
    rememberCall(call);
    emitWidgetEvent("conference_state_changed", { callId: call.id, conference: call.conference });
    const canCloseNow = hadTwoParticipants && Date.now() >= Number(call.conference.autoCloseAfter || 0);
    if (status.conference.collapsed) {
      await clearRemoteActionLock([], { room: call.conference.room });
      if (!current()) return;
      emitWidgetEvent("conference_ended", {
        callId: call.id,
        room: call.conference.room,
        reason: "collapsed_to_p2p",
        bridged: status.conference.bridged || []
      });
      endConferenceState(call.id);
      showToast("Помощь завершена");
      return;
    }
    if (canCloseNow && (!status.conference.active || participantCount < 2)) {
      await clearRemoteActionLock([], { room: call.conference.room });
      if (!current()) return;
      emitWidgetEvent("conference_ended", {
        callId: call.id,
        room: call.conference.room,
        reason: "not_enough_participants"
      });
      endConferenceState(call.id);
      await closeCallLocally(call, "conference_not_enough_participants");
      return;
    }
    // A ringing helper has not joined ConfBridge yet. Keep monitoring until the
    // server confirms collapse/closure; a participant count of two is not an end.
  }))); } catch {
    // A failed/expired status query cannot end or resurrect a SIP session.
  } finally {
    clearTimeout(timeout);
    if (conferenceStatusRequest === request) conferenceStatusRequest = undefined;
  }
}

function endConferenceState(callId = selectedCallId) {
  const call = calls.get(callId);
  if (!call?.conference?.active) return getSoftphoneState();
  call.conference = {
    ...call.conference,
    active: false,
    endedAt: new Date().toISOString()
  };
  rememberCall(call);
  emitWidgetEvent("conference_ended", { callId: call.id, room: call.conference.room || "" });
  if (!activeConferenceCalls().length) stopConferenceStatusMonitor();
  updateControlButtons();
  return getSoftphoneState();
}

function setConferenceState(payload = {} as any) {
  const call = calls.get(payload.callId || selectedCallId);
  if (!call) return getSoftphoneState();
  if (payload.active === false) return endConferenceState(call.id);
  call.conference = {
    ...(call.conference || {}),
    active: true,
    room: payload.room || call.conference?.room || "",
    targetExtensions: payload.targetExtensions || call.conference?.targetExtensions || [],
    role: payload.role || call.conference?.role || "participant",
    startedAt: payload.startedAt || call.conference?.startedAt || new Date().toISOString()
  };
  rememberCall(call);
  emitWidgetEvent("conference_state_changed", { callId: call.id, conference: call.conference });
  startConferenceStatusMonitor();
  updateControlButtons();
  return getSoftphoneState();
}

function conferenceParticipantLeft(payload = {} as any) {
  const state = endConferenceState(payload.callId || selectedCallId);
  emitWidgetEvent("conference_participant_left", {
    callId: payload.callId || selectedCallId || "",
    extension: payload.extension || "",
    reason: payload.reason || "participant_left"
  });
  return state;
}

async function inviteToConference(target) {
  const call = selectedCall();
  if (!call?.session || call.session.state !== SIP.SessionState.Established) throw new Error("Нет активного разговора");
  const targetExtension = target.extension || target.targetExtension;
  if (!targetExtension) throw new Error("Не выбран пользователь");
  emitWidgetEvent("conference_invite_started", {
    callId: call.id,
    targetUserId: target.userId || "",
    targetExtension
  });
  const result = await conferenceCalls({
    callIds: [call.id],
    targetExtensions: [targetExtension]
  });
  closeTransferPanel();
  return result;
}

async function startBlindTransfer(payload) {
  const transferCall = selectedCall();
  await prepareCallContext(transferCall);
  const useSipRefer = payload.targetExtension && payload.mode === "provider";
  if (useSipRefer) {
    const activeSession = transferCall.session;
    const referTo = makeSipUri(payload.targetExtension, getSipDomain());
    setTransferBusy(true);
    setTransferStatus("Перевод звонка");
    emitWidgetEvent("transfer_started", { ...payload, method: "sip-refer", transferType: "blind" });
    await setSipHold(true, { call: transferCall, emit: true });
    await referSession(activeSession, referTo, payload, {
      onSuccess: () => {
        setTransferBusy(false);
        completeTransfer(activeSession, payload).catch((error) => {
          emitWidgetEvent("transfer_failed", {
            ...payload,
            method: "sip-refer",
            code: "TRANSFER_FINISH_FAILED",
            message: error.message,
            recoverable: true
          });
        });
      },
      onFailure: () => {
        setTransferBusy(false);
        setSipHold(false, { call: transferCall, emit: true }).catch(() => {});
        emitWidgetEvent("transfer_failed", {
          ...payload,
          method: "sip-refer",
          code: "TRANSFER_REFER_FAILED",
          message: "Перевод отклонен телефонией",
          recoverable: true
        });
        showToast("Перевод отклонен");
      },
      onRejected: (response) => {
        setTransferBusy(false);
        setSipHold(false, { call: transferCall, emit: true }).catch(() => {});
        emitWidgetEvent("transfer_failed", {
          ...payload,
          method: "sip-refer",
          code: "TRANSFER_REFER_REJECTED",
          message: response?.message?.reasonPhrase || "Перевод отклонен",
          recoverable: true
        });
      }
    }).catch(async (error) => {
      setTransferBusy(false);
      await setSipHold(false, { call: transferCall, emit: true }).catch(() => {});
      throw error;
    });
    const result = {
      ok: true,
      transfer: {
        method: "sip-refer",
        mode: payload.mode,
        transferType: "blind",
        sourceExtension: payload.extension,
        targetExtension: payload.targetExtension,
        callId: payload.callId,
        requestId: payload.requestId,
        state: "requested"
      }
    };
    emitWidgetEvent("transfer_success", { ...payload, method: "sip-refer", result });
    showToast("Перевод запущен");
    closeTransferPanel();
    return result;
  }
  emitWidgetEvent("transfer_started", payload);
  const response = await fetchWidgetAction(apiUrl("/api/widget/calls/transfer"), {
    method: "POST",
    headers: widgetApiHeaders({ json: true }),
    body: JSON.stringify(payload)
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.ok === false) {
    const message = result.error?.message || result.error || "Перевод не выполнен";
    emitWidgetEvent("transfer_failed", {
      ...payload,
      code: result.error?.code || "TRANSFER_FAILED",
      message,
      recoverable: true
    });
    throw new Error(message);
  }
  emitWidgetEvent("transfer_success", { ...payload, result });
  suppressOutcomeForCall(transferCall, "transferred");
  emitWidgetEvent("transfer_completed", { ...payload, method: "gateway", result });
  closeTransferPanel();
  return result;
}

async function startAttendedTransfer(payload) {
  requireCallCapacity("consult");
  if (!payload.targetExtension) throw new Error("Для зрячего перевода нужен внутренний номер");
  const originalCall = selectedCall();
  await requireKnownServerCalls();
  requireCurrentCall(originalCall);
  const activeSession = originalCall?.session || session;
  await setRemoteActionLock([payload.sourceExtension || payload.extension, payload.targetExtension], {
    reason: "attended_transfer",
    callId: payload.callId,
    targetExtension: payload.targetExtension
  });
  setTransferBusy(true);
  setTransferStatus("Клиент на удержании");
  emitWidgetEvent("transfer_consult_started", { ...payload, method: "attended", transferType: "attended" });
  await setSipHold(true, { call: originalCall, emit: true });
  requireCurrentCall(originalCall);
  requireCallCapacity("consult");

  const target = makeSipUri(payload.targetExtension, getSipDomain());
  consultSession = new SIP.Inviter(userAgent, target, mediaOfferOptions({ earlyMedia: true }));
  const consultCall = createCallRecord({
    activeSession: consultSession,
    direction: "consult",
    phone: payload.targetExtension,
    customerPhone: null,
    dialNumber: payload.targetExtension,
    phase: "calling",
    transfer: { sourceCallId: originalCall?.id || payload.callId, targetExtension: payload.targetExtension },
    select: true
  });
  sessionDelegate(consultSession);
  consultSessionDelegate(consultSession, payload);
  transferFlow = {
    payload,
    originalCallId: originalCall?.id || "",
    originalSession: activeSession,
    consultCallId: consultCall.id,
    consultSession,
    targetExtension: payload.targetExtension
  };
  setTransferStatus(`Звоним ${payload.targetExtension}`);
  setConsultActionsVisible(false);
  await consultSession.invite({
    sessionDescriptionHandlerModifiers: [codecSdpModifier()],
    requestDelegate: {
      onTrying: () => setTransferStatus(`Звоним ${payload.targetExtension}`),
      onProgress: (response) => {
        if (!isPendingDialCall(consultCall)) return;
        setTransferStatus(`Идет вызов ${payload.targetExtension}`);
        const statusCode = responseStatus(response);
        consultCall.sipStatusCode = statusCode;
        consultCall.sipReason = statusCode === 180 ? "Ringing" : "Progress";
        if (statusCode === 180) markCallProgress(consultCall);
        emitWidgetEvent("outgoing_progress", {
          callId: consultCall.id, statusCode,
          phase: statusCode === 180 ? "ringing" : "progress",
          earlyMedia: responseHasEarlyMedia(response)
        });
      },
      onReject: (response) => {
        const message = reasonFromResponse(response, "Коллега не ответил");
        cancelAttendedTransfer(message).catch(() => {});
      }
    }
  }).catch(async (error) => {
    await clearRemoteActionLock([payload.sourceExtension || payload.extension, payload.targetExtension]);
    await cancelAttendedTransfer(error.message).catch(() => {});
    throw error;
  });
  showToast("Консультация начата");
  return { ok: true, transfer: { ...payload, transferType: "attended", state: "consulting" } };
}

async function confirmAttendedTransfer() {
  if (!transferFlow?.originalSession || !transferFlow?.consultSession) throw new Error("Нет активной консультации");
  const flow = transferFlow;
  const { payload, originalSession, consultSession: activeConsultSession } = flow;
  if (activeConsultSession.state !== SIP.SessionState.Established) throw new Error("Коллега еще не на линии");
  await prepareCallContext(callBySession(originalSession));
  requireCurrentCall(callBySession(activeConsultSession));
  if (transferFlow !== flow) throw new Error("Консультация уже завершена");
  flow.confirming = true;
  setTransferStatus("Перевод выполняется");
  setConsultActionsVisible(false);
  emitWidgetEvent("transfer_started", { ...payload, method: "sip-refer-replaces", transferType: "attended" });
  await referSession(originalSession, activeConsultSession, payload, {
    onSuccess: () => {
      const originalCall = callBySession(originalSession);
      const consultCall = callBySession(activeConsultSession);
      if (originalCall) {
        originalCall.skipOutcomeReason = "transferred";
        originalCall.outcomeHandled = true;
      }
      if (consultCall) {
        consultCall.skipOutcomeReason = "consult";
        consultCall.outcomeHandled = true;
      }
      removeCall(originalCall);
      removeCall(consultCall);
      transferFlow = undefined;
      if (consultSession === activeConsultSession) consultSession = undefined;
      setTransferBusy(false);
      completeTransfer(originalSession, payload).catch((error) => {
        emitWidgetEvent("transfer_failed", {
          ...payload,
          method: "sip-refer-replaces",
          code: "TRANSFER_FINISH_FAILED",
          message: error.message,
          recoverable: true
        });
      });
      activeConsultSession.bye?.().catch(() => {});
      clearRemoteActionLock([payload.sourceExtension || payload.extension, payload.targetExtension]).catch(() => {});
    },
    onFailure: () => {
      cancelAttendedTransfer("Перевод отклонен телефонией").catch(() => {});
    },
    onRejected: (response) => {
      cancelAttendedTransfer(response?.message?.reasonPhrase || "Перевод отклонен").catch(() => {});
    }
  }).catch(async (error) => {
    await cancelAttendedTransfer(error.message).catch(() => {});
    throw error;
  });
}

async function cancelAttendedTransfer(reason = "Перевод отменен") {
  const flow = transferFlow;
  transferFlow = undefined;
  setConsultActionsVisible(false);
  if (consultSession && consultSession.state !== SIP.SessionState.Terminated) {
    await consultSession.bye?.().catch(() => consultSession.cancel?.().catch(() => {}));
  }
  removeCall(callBySession(consultSession));
  consultSession = undefined;
  const originalCall = flow?.originalCallId ? calls.get(flow.originalCallId) : callBySession(flow?.originalSession);
  await clearRemoteActionLock([flow?.payload?.sourceExtension || flow?.payload?.extension, flow?.payload?.targetExtension]);
  if (isCurrentCall(originalCall) && flow?.originalSession?.state === SIP.SessionState.Established) {
      await selectCall(originalCall.id, { attach: true });
      await setSipHold(false, { call: originalCall, emit: true }).catch(() => {});
  }
  setTransferBusy(false);
  setTransferStatus("");
  showToast(reason);
  emitWidgetEvent("transfer_canceled", {
    ...(flow?.payload || {}),
    method: "attended",
    message: reason
  });
}

function attachRemoteAudio(activeSession) {
  const pc = activeSession.sessionDescriptionHandler?.peerConnection;
  const call = callBySession(activeSession);
  if (!pc || !call || !isLiveSession(activeSession) || pc.signalingState === "closed") return;
  const sameAttachment = remoteAudioSession === activeSession && remoteAudio.srcObject instanceof MediaStream;
  const remoteStream = sameAttachment ? remoteAudio.srcObject as MediaStream : new MediaStream();
  const tracks = pc.getReceivers().map(receiver => receiver.track).filter(track => track?.kind === "audio");
  remoteStream.getTracks().filter(track => !tracks.includes(track)).forEach(track => remoteStream.removeTrack(track));
  tracks.filter(track => !remoteStream.getTracks().includes(track)).forEach(track => remoteStream.addTrack(track));
  if (!sameAttachment) {
    remoteAudioPlaybackGeneration += 1;
    remoteAudioPlaybackTask = undefined;
    remoteAudio.srcObject = remoteStream;
  }
  remoteAudioSession = activeSession;
  activeMediaCallId = call.id;
  applyMediaState();
  void startRemoteAudioPlayback(activeSession, remoteStream, remoteAudioPlaybackGeneration);
}

function isCurrentRemoteAudioAttachment(activeSession, remoteStream, playbackGeneration) {
  return playbackGeneration === remoteAudioPlaybackGeneration &&
    remoteAudioSession === activeSession &&
    remoteAudio.srcObject === remoteStream;
}

function detachRemoteAudio(activeSession?) {
  if (activeSession && remoteAudioSession !== activeSession) return false;
  const detachedSession = remoteAudioSession;
  const detachedCallId = detachedSession?.__softphoneCallId || activeMediaCallId;
  const detachedCall = calls.get(detachedCallId);
  remoteAudioPlaybackGeneration += 1;
  remoteAudioPlaybackTask = undefined;
  remoteAudio.pause();
  remoteAudio.srcObject = null;
  remoteAudioSession = undefined;
  activeMediaCallId = "";
  if (detachedCallId) {
    try {
      diagnosticQueue.record("audio_detached", { ...callMediaDiagnosticContext(detachedSession),
        ...playbackDiagnosticState(remoteAudio, true), audioOutputSelected: false,
        callId: detachedCallId, linkedId: detachedCall?.linkedId });
    } catch { /* Observation must not interrupt cleanup. */ }
  }
  return true;
}

function startRemoteAudioPlayback(activeSession, remoteStream, playbackGeneration): Promise<void> {
  const call = callBySession(activeSession);
  const current = () => call && calls.get(call.id) === call && isLiveSession(activeSession) &&
    isCurrentRemoteAudioAttachment(activeSession, remoteStream, playbackGeneration);
  const playable = () => current() && !call.held && !["ended", "terminating"].includes(call.phase);
  if (!playable()) return Promise.resolve();
  if (remoteAudioPlaybackTask) return remoteAudioPlaybackTask;
  if (!remoteAudio.paused) return Promise.resolve();
  const report = (type, code, error) => {
    if (!current()) return;
    emitWidgetEvent(type, { callId: call.id, code,
      message: audioFailureMessage(error, code === "AUDIO_OUTPUT_FAILED" ? "output" : "playback"), errorName: error.name,
      recoverable: true, ...callMediaDiagnosticContext(activeSession) }, call);
  };
  const task = (async () => {
    try { await applyAudioOutput(); }
    catch (error) { report("audio_device_failed", "AUDIO_OUTPUT_FAILED", error); return; }
    if (!playable() || !remoteAudio.paused) return;
    try { await remoteAudio.play(); }
    catch (error) { report("audio_playback_failed", "AUDIO_PLAYBACK_FAILED", error); }
  })();
  remoteAudioPlaybackTask = task;
  const completed = () => { if (remoteAudioPlaybackTask === task) remoteAudioPlaybackTask = undefined; };
  void task.then(completed, completed);
  return task;
}

function responseStatus(response) {
  return Number(response?.message?.statusCode || 0);
}

function responseHasEarlyMedia(response) {
  return Boolean(response?.message?.body && String(response.message.getHeader?.("content-type") || "").toLowerCase().includes("sdp"));
}

function reasonFromResponse(response, fallback = "Вызов завершен") {
  const statusCode = responseStatus(response);
  const reason = response?.message?.reasonPhrase || "";
  const label = SIP_ERROR_LABELS[statusCode] || reason;
  return statusCode ? `${statusCode}${label ? ` ${label}` : ""}` : fallback;
}

function inviteFailureMessage(error) {
  if (error?.code === "MICROPHONE_DEVICE_UNAVAILABLE") return error.message;
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  const text = `${name} ${message}`.toLowerCase();
  if (text.includes("permission") || text.includes("notallowed") || text.includes("denied")) return "Нет доступа к микрофону";
  if (text.includes("notfound") || text.includes("devicesnotfound")) return "Микрофон не найден";
  if (text.includes("notreadable") || text.includes("trackstart")) return "Микрофон занят или недоступен";
  return message || "Вызов не прошел";
}

function isExpectedLocalCancelError(error) {
  return /peer connection closed|session terminated|already terminated|invalid session state/i.test(
    `${error?.name || ""} ${error?.message || ""}`
  );
}

async function resolveOutgoingFailureMessage(response, phone = activePhone) {
  const statusCode = responseStatus(response);
  const number = normalizeDialString(phone || "");
  if (statusCode === 404 && number && !number.startsWith("+")) {
    try {
      const query = new URLSearchParams({ extension: extensionEl.value.trim(), targetExtension: number });
      const status = await fetchWidgetAction(apiUrl(`/api/widget/extensions/status?${query.toString()}`), {
        headers: widgetApiHeaders()
      });
      if (!status.ok) throw new Error(`Extension status unavailable (${status.status})`);
      const result = await status.json();
      if (result?.exists && result.registered === false) return "Внутренний номер найден, но сейчас не зарегистрирован";
    } catch {
      // An optional availability lookup must not hide the actual SIP rejection.
      return outgoingFailureMessage(response);
    }
  }
  return outgoingFailureMessage(response);
}

function showCallFailure(message) {
  if (!message) return;
  clearTimeout(callFailureTimer);
  setCallPhase("idle");
  setCallUiState(isSipReady() ? "registered" : "offline", isSipReady() ? "Готов" : "Не подключен");
  setStatus(isSipReady() ? "Готов" : "Не подключен");
  $("contactHint").textContent = "";
  updatePrimaryButtons();
  updateControlButtons();
  showToast(message);
}

function emitOutgoingFailed(call, message, statusCode = call?.sipStatusCode || 0, code = "OUTGOING_CALL_REJECTED") {
  if (!call || call.failureEventEmitted) return;
  call.failureEventEmitted = true;
  call.failureMessage = message;
  if (calls.has(call.id)) rememberCall(call);
  emitWidgetEvent("outgoing_failed", {
    ...(call.failureEvidence || { failureSource: "local_error" }),
    callId: call.id,
    phone: call.phone || activePhone || "",
    code,
    statusCode,
    message,
    recoverable: true
  }, call);
}

function emitOutgoingCanceled(call, statusCode = call?.sipStatusCode || 487) {
  if (!call || call.canceledEventEmitted) return;
  call.canceledEventEmitted = true;
  call.failureEvidence = { failureSource: "local_cancel" };
  call.failureMessage = "";
  if (calls.has(call.id)) rememberCall(call);
  emitWidgetEvent("outgoing_canceled", {
    ...call.failureEvidence,
    callId: call.id,
    phone: call.phone || activePhone || "",
    code: "OUTGOING_CALL_CANCELED",
    statusCode,
    message: "Вызов отменен пользователем",
    recoverable: true
  }, call);
}

function isPendingOutgoingCall(call) {
  return call && calls.get(call.id) === call && call.direction === "outgoing" && call.phase === "calling" &&
    !outgoingFailureSettled(call);
}

function isPendingDialCall(call) {
  return isCurrentCall(call) && ["outgoing", "consult"].includes(call.direction) &&
    call.phase === "calling" && !outgoingFailureSettled(call);
}

function markCallProgress(call) {
  if (!isPendingDialCall(call) || call.progressAt) return false;
  call.progressAt = new Date().toISOString();
  return true;
}

function outgoingFailureSettled(call) {
  return !call || call.localCancelRequested || call.canceledEventEmitted || call.setupTimedOut ||
    call.failureEventEmitted || Boolean(call.answeredAt);
}

function canPresentCallFailure(call) {
  // SIP.js removes a rejected session before invoking onReject. Still report
  // that failure, but never reset a newer call (even if it has already ended).
  return call && (selectedCallId === call.id || (!liveCalls().length && latestCallId === call.id));
}

function handleOutgoingTrying(response, outgoingCall = selectedCall()) {
  if (!isPendingOutgoingCall(outgoingCall)) return;
  const call = outgoingCall;
  call.sipStatusCode = responseStatus(response);
  call.sipReason = "Trying";
  rememberCall(call);
  if (selectedCallId === call.id) {
    stopOutgoingTryingTimer();
    clearTimeout(callFailureTimer);
    stopRingback();
    setCallUiState("calling", "Маршрутизируем");
    setStatus("Маршрутизируем");
    outgoingTryingTimer = setTimeout(() => {
      if (selectedCallId === call.id && isPendingOutgoingCall(call) && call.sipStatusCode === 100) {
        setCallUiState("calling", "Идет вызов");
        setStatus("Идет вызов");
      }
    }, 900);
  }
  emitWidgetEvent("outgoing_progress", { callId: call?.id, statusCode: responseStatus(response), phase: "trying" });
}

function handleOutgoingProgress(response, outgoingCall = selectedCall()) {
  if (!isPendingOutgoingCall(outgoingCall)) return;
  const statusCode = responseStatus(response);
  const hasEarlyMedia = responseHasEarlyMedia(response);
  const call = outgoingCall;
  if (call && [180, 183].includes(statusCode) && !call.setupProgressReceived) {
    call.setupProgressReceived = true;
    callSetup(call.session).stage = "ringing";
    startCallSetupTimer(call, CALL_RINGING_TIMEOUT_MS);
  }
  if (call) {
    call.sipStatusCode = statusCode;
    call.sipReason = statusCode === 180 ? "Ringing" : "Progress";
    if (statusCode === 180) markCallProgress(call);
    rememberCall(call);
  }
  if (selectedCallId === call.id) {
    stopOutgoingTryingTimer();
    setCallUiState("calling", "Идет вызов");
    setStatus("Идет вызов");
    if (hasEarlyMedia) {
      stopRingback();
      attachRemoteAudio(call.session);
    } else if (statusCode === 180 || statusCode === 183) {
      startRingback();
    }
  }
  emitWidgetEvent("outgoing_progress", {
    callId: call?.id,
    statusCode,
    phase: statusCode === 180 ? "ringing" : "progress",
    earlyMedia: hasEarlyMedia
  });
}

async function handleOutgoingReject(response, outgoingCall = selectedCall()) {
  const call = outgoingCall;
  if (outgoingFailureSettled(call)) return;
  stopCallSetupTimer(call);
  if (canPresentCallFailure(call)) {
    stopOutgoingTryingTimer();
    stopRingback();
  }
  const statusCode = responseStatus(response);
  const message = await resolveOutgoingFailureMessage(response, call.phone);
  // A 404 availability lookup can finish after Hangup, timeout or another call.
  if (outgoingFailureSettled(call)) return;
  call.sipStatusCode = statusCode;
  call.sipReason = message;
  call.failureEvidence = sipFailureEvidence(response);
  call.failureMessage = message;
  if (calls.get(call.id) === call) rememberCall(call);
  if (canPresentCallFailure(call)) showCallFailure(message);
  emitOutgoingFailed(call, message, statusCode);
}

function consultSessionDelegate(activeSession, payload) {
  activeSession.stateChange.addListener((state) => {
    if (consultSession !== activeSession) return;
    if (state === SIP.SessionState.Established) {
      attachRemoteAudio(activeSession);
      setTransferStatus("Коллега на линии");
      setConsultActionsVisible(true);
      emitWidgetEvent("transfer_consult_established", payload);
    }
    if (state === SIP.SessionState.Terminated) {
      callSetups.get(activeSession)?.abort();
      if (transferFlow && transferFlow.consultSession === activeSession && !transferFlow.confirming) {
        cancelAttendedTransfer("Коллега завершил консультацию").catch(() => {});
        return;
      }
      consultSession = undefined;
    }
  });
}

function notificationAllowed() {
  return notificationsEnabled && "Notification" in window && Notification.permission === "granted";
}

async function setNotifications(enabled = true) {
  if (!("Notification" in window)) {
    throw new Error("Браузер не поддерживает уведомления");
  }
  if (!enabled) {
    notificationsEnabled = false;
    localStorage.setItem("softphoneNotifications", "off");
    updateNotificationButton();
    closeAllIncomingNotifications();
    return getSoftphoneState();
  }
  if (Notification.permission === "denied") {
    throw new Error("Уведомления запрещены в настройках браузера");
  }
  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  notificationsEnabled = permission === "granted";
  localStorage.setItem("softphoneNotifications", notificationsEnabled ? "on" : "off");
  updateNotificationButton();
  if (!notificationsEnabled) throw new Error("Уведомления не разрешены");
  refreshIncomingAttention();
  return getSoftphoneState();
}

async function getBrowserPermissions() {
  if (!microphonePermission && navigator.permissions?.query) {
    try {
      microphonePermission = await navigator.permissions.query({ name: "microphone" as PermissionName });
      microphonePermission.addEventListener("change", () => {
        void getBrowserPermissions().then(permissions => emitWidgetEvent("permissions_changed", { permissions }));
      });
    } catch (error) {
      // Unsupported permission queries mean unknown, not granted/denied.
      if (!(error instanceof TypeError)) throw error;
    }
  }
  return {
    microphone: navigator.mediaDevices?.getUserMedia ? microphonePermission?.state || "unknown" : "unsupported",
    audio: window.AudioContext || window.webkitAudioContext ? ringtoneContext?.state === "running" ? "granted" : "prompt" : "unsupported",
    notifications: "Notification" in window ? Notification.permission === "default" ? "prompt" : Notification.permission : "unsupported",
    notificationsEnabled
  };
}

async function requestBrowserPermission(kind: string) {
  if (kind === "microphone") await refreshAudioDevices({ requestPermission: true });
  else if (kind === "notifications") await setNotifications(true);
  else if (kind === "audio") {
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context) throw new Error("Браузер не поддерживает воспроизведение звука");
    ringtoneContext = ringtoneContext || new Context();
    let timeout;
    try {
      await Promise.race([ringtoneContext.resume(), new Promise((_, reject) => {
        timeout = setTimeout(() => reject(new Error("Браузер не разрешил звук. Проверьте настройки сайта")), 5000);
      })]);
    } finally { clearTimeout(timeout); }
    if (ringtoneContext.state !== "running") throw new Error("Браузер заблокировал звук. Проверьте настройки сайта");
    const tone = ringtoneContext.createOscillator(), gain = ringtoneContext.createGain();
    tone.connect(gain); gain.connect(ringtoneContext.destination);
    gain.gain.value = 0.035; tone.frequency.value = 660;
    tone.onended = () => { tone.disconnect(); gain.disconnect(); };
    tone.start(); tone.stop(ringtoneContext.currentTime + 0.15);
  } else throw new Error("Неизвестное разрешение браузера");
  const permissions = await getBrowserPermissions();
  emitWidgetEvent("permissions_changed", { permissions });
  return permissions;
}

async function enableNotifications() {
  const enabled = !notificationAllowed();
  try {
    await setNotifications(enabled);
    showToast(enabled ? "Уведомления включены" : "Уведомления выключены");
  } catch (error) {
    showToast(error.message);
  }
}

function isPageForeground() {
  return document.visibilityState === "visible" && document.hasFocus();
}

function showIncomingNotification(callId, phone) {
  if (!notificationAllowed() || notifiedIncomingCallIds.has(callId)) return;
  if (isPageForeground()) return;
  notifiedIncomingCallIds.add(callId);
  try {
    const notification = new Notification("Входящий звонок", {
      body: phone ? `Номер: ${phone}` : "Номер не определен",
      tag: `incoming-${callId}`,
      requireInteraction: true
    });
    incomingNotifications.set(callId, notification);
    notification.onclick = () => {
      window.focus();
      notification.close();
      emitWidgetEvent("incoming_notification_clicked", { callId, phone: phone || "" });
    };
    notification.onclose = () => incomingNotifications.delete(callId);
  } catch (error) {
    recordIncomingAttention("notification_failed", callId, { errorName: error?.name });
  }
}

function closeIncomingNotification(callId) {
  const notification = incomingNotifications.get(callId);
  incomingNotifications.delete(callId);
  notification?.close();
}

function closeAllIncomingNotifications() {
  for (const callId of incomingNotifications.keys()) closeIncomingNotification(callId);
}

function titleAttentionTick() {
  if (!incomingAttentionPhones.size || isPageForeground()) return;
  if (document.title !== lastAttentionTitle && document.title !== INCOMING_DOCUMENT_TITLE) {
    titleBeforeIncoming = document.title;
  }
  titleFlashActive = !titleFlashActive;
  incomingFavicon.setVisible(titleFlashActive);
  document.title = titleFlashActive ? INCOMING_DOCUMENT_TITLE : titleBeforeIncoming;
  lastAttentionTitle = document.title;
}

function stopTitleAttention() {
  if (titleFlashTimer) clearInterval(titleFlashTimer);
  titleFlashTimer = undefined;
  if (document.title === lastAttentionTitle || document.title === INCOMING_DOCUMENT_TITLE) {
    document.title = titleBeforeIncoming || document.title;
  }
  lastAttentionTitle = "";
  titleFlashActive = false;
  incomingFavicon.setVisible(true);
}

function refreshIncomingAttention() {
  void incomingFavicon.setActive(incomingAttentionPhones.size > 0)
    .catch(() => logEvent("incoming_favicon_failed", "Не удалось обновить значок входящего звонка"));
  if (!incomingAttentionPhones.size || isPageForeground()) {
    stopTitleAttention();
    closeAllIncomingNotifications();
    return;
  }
  if (!titleFlashTimer) {
    if (document.title !== INCOMING_DOCUMENT_TITLE) titleBeforeIncoming = document.title;
    titleAttentionTick();
    titleFlashTimer = setInterval(titleAttentionTick, 900);
  }
  for (const [callId, phone] of incomingAttentionPhones) showIncomingNotification(callId, phone);
}

function startIncomingAttention(callId, phone = "") {
  incomingAttentionPhones.set(callId, phone || "");
  refreshIncomingAttention();
}

function stopIncomingAttention(callId) {
  if (!callId) return;
  incomingAttentionPhones.delete(callId);
  notifiedIncomingCallIds.delete(callId);
  closeIncomingNotification(callId);
  refreshIncomingAttention();
}

function stopAllIncomingAttention() {
  incomingAttentionPhones.clear();
  notifiedIncomingCallIds.clear();
  refreshIncomingAttention();
}

function startRingtone() {
  if (ringtoneAudio) return;
  const callId = ringingCallId;
  try {
    const audio = new Audio(assetUrl("assets/incoming-ringtone.mp3?v=1"));
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0.8;
    ringtoneAudio = audio;
    void audio.play().then(() => {
      if (ringtoneAudio !== audio || ringingCallId !== callId) return;
      recordIncomingAttention("ringtone_started", callId, { ringerKind: "ringtone" });
      emitWidgetEvent("state", getSoftphoneState());
    }, error => {
      if (ringtoneAudio !== audio || ringingCallId !== callId) return;
      recordIncomingAttention("ringtone_failed", callId, { ringerKind: "ringtone", errorName: error?.name });
    });
  } catch (error) {
    recordIncomingAttention("ringtone_failed", callId, { ringerKind: "ringtone", errorName: error?.name });
  }
}

function recordIncomingAttention(type, callId, detail = {}) {
  const call = calls.get(callId);
  diagnosticQueue.record(type, { ...browserDiagnosticContext(), ...detail,
    callId, linkedId: call?.linkedId, asteriskChannelId: call?.asteriskChannelId,
    sipCallId: call?.session?.request?.callId, direction: "incoming" });
}

function ringerState() {
  return { callId: ringingCallId || null, playing: Boolean(ringingCallId &&
    ((ringtoneAudio && !ringtoneAudio.paused) || waitingToneOscillator)) };
}

function reconcileRinging() {
  const incoming = liveCalls().filter(call => call.phase === "incoming" && !call.ringerSilenced);
  const busy = liveCalls().some(call => ["calling", "answering", "talking", "held"].includes(call.phase));
  if (!incoming.some(call => call.id === ringingCallId)) {
    stopRingtone(); stopWaitingTone(); ringingCallId = "";
  }
  if (!incoming.length) return;
  if (!busy) {
    stopWaitingTone();
    ringingCallId = incoming[0].id;
    startRingtone();
    return;
  }
  stopRingtone();
  if (waitingToneOscillator) return;
  const waiting = incoming.find(call => !call.waitingTonePlayed);
  ringingCallId = waiting?.id || "";
  if (waiting) {
    waiting.waitingTonePlayed = true;
    playWaitingTone();
  }
}

function silenceRinger(options = {} as any) {
  const call = calls.get(options.callId || ringingCallId);
  if (!isCurrentCall(call) || call.phase !== "incoming" || call.id !== ringingCallId) return getSoftphoneState();
  call.ringerSilenced = true;
  reconcileRinging();
  emitWidgetEvent("state", getSoftphoneState());
  return getSoftphoneState();
}

function stopRingtone() {
  if (!ringtoneAudio) return;
  const audio = ringtoneAudio;
  ringtoneAudio = undefined;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (error) { logEvent("ringtone_stop_failed", error?.name || "Error"); }
}

function startRingback() {
  if (ringbackOscillator) return;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  ringtoneContext = ringtoneContext || new AudioContext();
  ringtoneContext.resume?.();
  ringbackOscillator = ringtoneContext.createOscillator();
  ringbackGain = ringtoneContext.createGain();
  ringbackOscillator.type = "sine";
  ringbackOscillator.frequency.value = 425;
  ringbackOscillator.connect(ringbackGain);
  ringbackGain.connect(ringtoneContext.destination);
  ringbackGain.gain.value = 0.0001;
  ringbackOscillator.start();

  const pulse = () => {
    if (!ringbackGain || !ringtoneContext) return;
    const now = ringtoneContext.currentTime;
    ringbackGain.gain.cancelScheduledValues(now);
    ringbackGain.gain.setValueAtTime(0.0001, now);
    ringbackGain.gain.linearRampToValueAtTime(0.08, now + 0.04);
    ringbackGain.gain.setValueAtTime(0.08, now + 0.85);
    ringbackGain.gain.linearRampToValueAtTime(0.0001, now + 0.95);
  };
  pulse();
  ringbackOscillator._pulseTimer = setInterval(pulse, 4000);
}

function stopRingback() {
  if (!ringbackOscillator) return;
  clearInterval(ringbackOscillator._pulseTimer);
  ringbackOscillator.stop();
  ringbackOscillator.disconnect();
  ringbackGain?.disconnect();
  ringbackOscillator = undefined;
  ringbackGain = undefined;
}

function stopOutgoingTryingTimer() {
  clearTimeout(outgoingTryingTimer);
  outgoingTryingTimer = undefined;
}

function stopCallSetupTimer(call) {
  if (!call?.callSetupTimer) return;
  clearTimeout(call.callSetupTimer);
  call.callSetupTimer = undefined;
}

function startCallSetupTimer(call, timeoutMs = CALL_SETUP_TIMEOUT_MS) {
  stopCallSetupTimer(call);
  const startedAt = performance.now();
  call.callSetupTimer = setTimeout(() => {
    void expireCallSetup(call, setupTimerTiming(startedAt, timeoutMs));
  }, timeoutMs);
}

async function expireCallSetup(call, timing?: ReturnType<typeof setupTimerTiming>) {
  call.callSetupTimer = undefined;
  if (!calls.has(call.id) || call.answeredAt || !["calling", "answering"].includes(call.phase)) return;

  const setup = callSetup(call.session);
  const message = setupTimeoutMessage(setup.stage);
  const lastSipStatusCode = call.sipStatusCode || 0;
  // Abort closes the peer synchronously. Preserve the state that timed out.
  const mediaContext = callMediaDiagnosticContext(call.session);
  call.setupTimedOut = true;
  call.failureEvidence = { failureSource: "local_timeout" };
  setup.abort();
  call.sipStatusCode = 408;
  call.sipReason = message;
  call.failureMessage = message;
  rememberCall(call);
  stopOutgoingTryingTimer();
  stopRingback();
  const code = call.direction === "incoming" ? "INCOMING_CALL_TIMEOUT" : "OUTGOING_CALL_TIMEOUT";
  if (call.direction === "outgoing") emitOutgoingFailed(call, message, 408, code);
  else emitWidgetEvent("error", { callId: call.id, code, message, recoverable: true });
  emitWidgetEvent("call_setup_timeout", { callId: call.id, stage: setup.stage, code,
    sipStatusCode: lastSipStatusCode, ...mediaContext, ...timing });

  try {
    if (call.direction === "incoming") await call.session?.reject?.();
    else await call.session?.cancel?.();
  } catch (error) {
    if (!isExpectedLocalCancelError(error)) {
      emitWidgetEvent("call_cleanup_failed", {
        callId: call.id,
        reason: "call_setup_timeout",
        message: error.message,
        recoverable: true
      });
    }
  }

  if (!calls.has(call.id)) return;
  const wasSelected = selectedCallId === call.id;
  finishCall(call);
  removeCall(call);
  if (wasSelected && !liveCalls().length) showCallFailure(message);
  updatePrimaryButtons();
  updateControlButtons();
  emitWidgetEvent("ended", {
    callId: call.id,
    phone: call.phone || "",
    direction: call.direction || null,
    startedAt: call.createdAt || null,
    answeredAt: null,
    endedAt: call.endedAt || null,
    durationSec: 0,
    failureMessage: message,
    canceled: false
  }, call);
}

function playWaitingTone() {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (waitingToneOscillator) return;
  const callId = ringingCallId;
  try {
    if (!AudioContext) throw new DOMException("AudioContext unavailable", "NotSupportedError");
    ringtoneContext = ringtoneContext || new AudioContext();
    let oscillator;
    void ringtoneContext.resume().then(() => {
      if (!oscillator || waitingToneOscillator !== oscillator || ringingCallId !== callId) return;
      recordIncomingAttention(ringtoneContext.state === "running" ? "ringtone_started" : "ringtone_failed",
        callId, { ringerKind: "waiting_tone" });
    }, error => {
      if (!oscillator || waitingToneOscillator !== oscillator || ringingCallId !== callId) return;
      recordIncomingAttention("ringtone_failed", callId, { ringerKind: "waiting_tone", errorName: error?.name });
    });
    oscillator = waitingToneOscillator = ringtoneContext.createOscillator();
    waitingToneGain = ringtoneContext.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 640;
    oscillator.connect(waitingToneGain);
    waitingToneGain.connect(ringtoneContext.destination);
    waitingToneGain.gain.value = 0.0001;
    oscillator.start();

    const now = ringtoneContext.currentTime;
    waitingToneGain.gain.setValueAtTime(0.0001, now);
    waitingToneGain.gain.linearRampToValueAtTime(0.045, now + 0.03);
    waitingToneGain.gain.linearRampToValueAtTime(0.0001, now + 0.18);
    waitingToneGain.gain.setValueAtTime(0.0001, now + 0.35);
    waitingToneGain.gain.linearRampToValueAtTime(0.04, now + 0.38);
    waitingToneGain.gain.linearRampToValueAtTime(0.0001, now + 0.52);
    waitingToneTimer = setTimeout(() => {
      stopWaitingTone();
      ringingCallId = "";
      reconcileRinging();
      emitWidgetEvent("state", getSoftphoneState());
    }, 650);
  } catch (error) {
    recordIncomingAttention("ringtone_failed", callId, { ringerKind: "waiting_tone", errorName: error?.name });
    stopWaitingTone();
  }
}

function stopWaitingTone() {
  clearTimeout(waitingToneTimer);
  try { waitingToneOscillator?.stop(); } catch { /* already stopped/failed to start */ }
  try { waitingToneOscillator?.disconnect(); waitingToneGain?.disconnect(); } catch { /* disposed context */ }
  waitingToneOscillator = waitingToneGain = undefined;
}

function incomingPhone(invitation) {
  return (
    invitation?.remoteIdentity?.uri?.user ||
    invitation?.remoteIdentity?.displayName ||
    invitation?.request?.from?.uri?.user ||
    ""
  );
}

function incomingDiagnosticContext(invitation, callId: string = newCallId()) {
  const channelId = name => {
    const value = invitation.request?.getHeader?.(name);
    return typeof value === "string" && /^\d{1,16}\.\d{1,16}$/.test(value) ? value : "";
  };
  return { callId, direction: "incoming", sipCallId: invitation.request?.callId || "",
    linkedId: channelId("X-DoCRM-Linked-ID"), asteriskChannelId: channelId("X-DoCRM-Channel-ID") };
}

function rejectIncomingInvitation(invitation, type, sipStatusCode, context) {
  // A late INVITE on an obsolete UA was never a product call. Observe its
  // rejection without opening a panel or surfacing a stale-agent error in DoCRM.
  const observe = (event, detail) => type === "incoming_rejected_unavailable"
    ? diagnosticQueue.record(event, { ...browserDiagnosticContext(), ...detail })
    : emitWidgetEvent(event, detail);
  void (async () => {
    try { await invitation.reject({ statusCode: sipStatusCode }); }
    catch (error) { observe("error", { ...context, sipStatusCode,
      code: "INCOMING_REJECT_FAILED", message: error?.message || String(error), recoverable: true }); }
  })();
  observe(type, { ...context, sipStatusCode });
}

function handleIncomingInvitation(invitation, available = true) {
  const contextIds = incomingDiagnosticContext(invitation, callBySession(invitation)?.id);
  diagnosticQueue.record("incoming_received", { ...browserDiagnosticContext(), ...contextIds });
  if (!available) {
    rejectIncomingInvitation(invitation, "incoming_rejected_unavailable", 480, contextIds);
    return;
  }
  if (isOnBreak() || availabilityTask) {
    rejectIncomingInvitation(invitation, "incoming_rejected_on_break", 480, contextIds);
    return;
  }
  if (!hasCallCapacity()) {
    rejectIncomingInvitation(invitation, "incoming_rejected_capacity", 486, contextIds);
    return;
  }
  ownershipYield?.abort();
  suspendOutcomePromptForCall();
  const phone = incomingPhone(invitation);
  let context = null;
  let invalidContext = false;
  try { context = incomingCallContext(invitation); } catch { invalidContext = true; }
  // Explicit unknown/malformed transfer identity must not become the SIP routing number.
  const customerPhone = invalidContext ? null : context ? context.customerPhone : incomingCustomerPhone(invitation);
  const hasActiveConversation = liveCalls().some((call) => ["talking", "held", "calling", "answering"].includes(call.phase));
  const incomingCall = createCallRecord({
    id: contextIds.callId,
    linkedId: contextIds.linkedId,
    asteriskChannelId: contextIds.asteriskChannelId,
    activeSession: invitation,
    direction: "incoming",
    phone,
    customerPhone,
    dialNumber: null,
    transfer: context?.transfer || null,
    phase: "incoming",
    select: !hasActiveConversation
  });
  if (context?.transfer.type === "conference_invite") {
    incomingCall.skipOutcomeReason = "conference_helper";
    incomingCall.outcomeHandled = true;
  }
  sessionDelegate(invitation);
  if (!hasActiveConversation) {
    syncGlobalsFromCall(incomingCall);
    setStatus("Входящий звонок");
    setCallPhase("incoming", phone);
    setCallUiState("incoming", customerPhone ? `Входящий ${customerPhone}` : "Входящий звонок");
  } else {
    renderCallStack();
    showToast(customerPhone ? `Второй входящий ${customerPhone}` : "Второй входящий");
  }
  $("contactHint").textContent = "";
  $("screenPopStatus").textContent = "Отправлено";
  startIncomingAttention(incomingCall.id, incomingCall.customerPhone || "");
  emitWidgetEvent("incoming", { callId: incomingCall.id, phone, waiting: hasActiveConversation }, incomingCall);
  if (invalidContext) emitWidgetEvent("error", {
    code: "INVALID_CALL_CONTEXT", message: "Не удалось прочитать данные клиента переводимого звонка", recoverable: true
  }, incomingCall);
  void checkActiveCallStatus();
}

function sessionDelegate(activeSession) {
  observeSessionBye(activeSession, (type, detail) => {
    const call = callBySession(activeSession);
    diagnosticQueue.record(type, { ...detail, ...callMediaDiagnosticContext(activeSession),
      callId: call?.id, linkedId: call?.linkedId });
  });
  activeSession.stateChange.addListener((state) => {
    logEvent("session", state);
    const call = callBySession(activeSession);
    diagnosticQueue.record("session_state", { sessionState: state, callId: call?.id, linkedId: call?.linkedId,
      ...callMediaDiagnosticContext(activeSession) });
    if (state === SIP.SessionState.Established) {
      // A late answer belongs only to this live record, never to the selected
      // (possibly newer) call or to the last number in the dial input.
      if (!isCurrentCall(call)) return;
      callSetup(activeSession).stage = "talking";
      stopCallSetupTimer(call);
      const phone = call.phone;
      call.phase = "talking";
      call.held = false;
      call.answeredAt = call.answeredAt || new Date().toISOString();
      rememberCall(call);
      if (!selectedCallId || selectedCallId === call?.id || activeSession === consultSession) {
        selectedCallId = call?.id || selectedCallId;
        syncGlobalsFromCall(call || activeCall);
        attachRemoteAudio(activeSession);
      }
      if (call?.direction === "incoming") stopIncomingAttention(call.id);
      if (selectedCallId === call.id) {
        stopOutgoingTryingTimer();
        stopRingback();
        setCallPhase("talking", phone);
        startCallTimer(call);
        setCallUiState("talking", "Разговор");
      }
      renderCallStack();
      reconcileRinging();
      emitWidgetEvent("answered", { callId: call.id, phone }, call);
    }
    if (state === SIP.SessionState.Terminated) {
      // Local cleanup may already have removed this session. Its late terminal
      // notification must not stop the next call's timer or emit an unbound ended.
      if (!call) return;
      if (call?.id) stopIncomingAttention(call.id);
      callSetups.get(activeSession)?.abort();
      if (selectedCallId === call.id) stopOutgoingTryingTimer();
      const locallyCanceled = Boolean(call?.localCancelRequested && !call?.answeredAt && call?.direction === "outgoing");
      const failureMessage = locallyCanceled ? "" : call?.failureMessage ||
        (!call?.answeredAt && call?.direction === "outgoing" && Number(call?.sipStatusCode || 0) >= 300 ? call?.sipReason : "");
      const unansweredOutgoing = Boolean(call && !call.answeredAt && call.direction === "outgoing");
      finishCall(call);
      const wasSelected = selectedCallId === call?.id;
      const wasMedia = activeMediaCallId === call?.id || remoteAudioSession === activeSession;
      if (consultSession === activeSession) consultSession = undefined;
      if (transferFlow?.consultSession === activeSession && !transferFlow.confirming) {
        cancelAttendedTransfer("Коллега завершил консультацию").catch(() => {});
        return;
      }
      removeCall(call);
      if (wasMedia) {
        detachRemoteAudio(activeSession);
      }
      if (wasSelected) {
        if (liveCalls().length) {
          selectNextLiveCall();
          updateCallDisplay();
          updatePrimaryButtons();
        } else {
          transferInProgress = false;
          transferFlow = undefined;
          stopRingtone();
          stopRingback();
          stopCallTimer();
          muted = false;
          held = false;
          setCallPhase("idle");
          if (failureMessage) {
            if (!call.failureEventEmitted) showCallFailure(failureMessage);
            if (unansweredOutgoing) emitOutgoingFailed(call, failureMessage, call?.sipStatusCode || 0);
          } else {
            setCallUiState(isSipReady() ? "registered" : "offline", isSipReady() ? "Готов" : "Не подключен");
            setStatus(isSipReady() ? "Готов" : "Не подключен");
          }
          activeCall = undefined;
        }
      }
      if (locallyCanceled) emitOutgoingCanceled(call, call?.sipStatusCode || 487);
      applyMediaState();
      updateControlButtons();
      emitWidgetEvent("ended", {
        ...call.failureEvidence,
        callId: call?.id,
        phone: call?.phone || "",
        direction: call?.direction || null,
        startedAt: call?.createdAt || null,
        answeredAt: call?.answeredAt || null,
        endedAt: call?.endedAt || null,
        durationSec: durationSec(call),
        failureMessage: failureMessage || "",
        canceled: locallyCanceled
      }, call);
    }
  });
}

function runtimeSessionDescriptionHandlerFactory(activeSession, options = {} as any) {
  const setup = callSetup(activeSession);
  setup.check();
  const factory = SIP.Web.defaultSessionDescriptionHandlerFactory((constraints) => {
    return setup.run(() => captureMicrophone(constraints, setup.controller.signal, activeSession),
      (stream) => stream.getTracks().forEach((track) => track.stop())).then((stream) => {
      setup.keep(stream, (media) => media.getTracks().forEach((track) => track.stop()));
      if (!["talking", "ringing"].includes(setup.stage)) setup.stage = "ice";
      return stream;
    });
  });
  const handler = factory(activeSession, {
    ...options,
    constraints: { audio: audioConstraints(), video: false },
    peerConnectionConfiguration: {
      ...(options.peerConnectionConfiguration || {}),
      iceServers: runtimeConfig.iceServers || defaultIceServers()
    }
  });
  setup.controller.signal.addEventListener("abort", () => handler.close(), { once: true });
  const getDescription: typeof handler.getDescription = handler.getDescription.bind(handler);
  handler.getDescription = (...args) => setup.run(() => getDescription(...args)).then((description) => {
    setup.check();
    if (!["talking", "ringing"].includes(setup.stage)) setup.stage = "signaling";
    return description;
  });
  const peer = (handler as SIP.Web.SessionDescriptionHandler).peerConnection;
  if (peer) {
    const flowDiagnostics = new MediaFlowDiagnostics();
    let collecting = false;
    let statsTimer: ReturnType<typeof setTimeout>;
    let lastDiagnosticAt = -Infinity;
    const collect = async (forceDiagnostic = false) => {
      if (collecting || setup.controller.signal.aborted) return;
      collecting = true;
      clearTimeout(statsTimer);
      try {
        const stats = audioStats(await peer.getStats());
        const call = callBySession(activeSession);
        if (!setup.controller.signal.aborted && call) {
          for (const event of flowDiagnostics.observe(stats, activeSession.state === SIP.SessionState.Established && !call.held, performance.now())) {
            diagnosticQueue.record(event.type, { ...event.detail, ...stats,
              callId: call.id, linkedId: call.linkedId, muted: call.muted || false,
              ...callMediaDiagnosticContext(activeSession) });
          }
          // SDP/ICE/play() alone do not prove early RTP has arrived.
          if (call.sipStatusCode === 183 && stats.packetsReceived > 0 && markCallProgress(call)) {
            emitWidgetEvent("outgoing_progress", {
              callId: call.id, statusCode: 183, phase: "progress", earlyMedia: true
            });
          }
          if (forceDiagnostic || performance.now() - lastDiagnosticAt >= 10_000) {
            lastDiagnosticAt = performance.now();
            diagnosticQueue.record("media_stats", {
              ...stats, callId: call.id, linkedId: call.linkedId, direction: call.direction,
              muted: call.muted || false, held: call.held || false,
              ...callMediaDiagnosticContext(activeSession)
            });
          }
        }
      } catch { /* Telemetry must never interrupt a call. */ }
      finally {
        collecting = false;
        if (!setup.controller.signal.aborted) {
          const call = callBySession(activeSession);
          // Reuse this collector until progress is observed; keep the normal log cadence.
          statsTimer = setTimeout(() => void collect(), isPendingDialCall(call) && !call.progressAt ? 1000 : 10_000);
        }
      }
    };
    const stateChanged = () => {
      const call = callBySession(activeSession);
      diagnosticQueue.record("media_state", { callId: call?.id, linkedId: call?.linkedId,
        ...callMediaDiagnosticContext(activeSession) });
      void collect(true);
      if (call) scheduleMediaRecovery(call);
    };
    peer.addEventListener("connectionstatechange", stateChanged);
    peer.addEventListener("iceconnectionstatechange", stateChanged);
    const trackChanged = () => {
      // A late track updates only the currently attached stream. A background or
      // ended session must never steal another call's audio output.
      if (remoteAudioSession === activeSession && !setup.controller.signal.aborted) attachRemoteAudio(activeSession);
    };
    peer.addEventListener("track", trackChanged);
    statsTimer = setTimeout(() => void collect(), 1000);
    setup.controller.signal.addEventListener("abort", () => {
      clearTimeout(statsTimer);
      peer.removeEventListener("connectionstatechange", stateChanged);
      peer.removeEventListener("iceconnectionstatechange", stateChanged);
      peer.removeEventListener("track", trackChanged);
    }, { once: true });
  }
  return handler;
}

function requestRegistrationConfigurationRestart() {
  registrationConfigurationRestartPending = true;
  void restartRegistrationForConfigurationWhenIdle();
}

function hasLiveSipSession() {
  const knownSessions = [session, consultSession, remoteAudioSession, ...liveCalls().map((call) => call.session)];
  return knownSessions.some((activeSession, index) => activeSession &&
    knownSessions.indexOf(activeSession) === index &&
    activeSession.state !== SIP.SessionState.Terminated);
}

async function restartRegistrationForConfigurationWhenIdle() {
  if (!registrationConfigurationRestartPending || registrationConfigurationRestartTask ||
    disconnectPromise || ownershipYield || availabilityTask || pageSuspended || !desiredConnection || liveCalls().length || hasLiveSipSession()) return;
  if (connectPromise) {
    void connectPromise.then(
      restartRegistrationForConfigurationWhenIdle,
      restartRegistrationForConfigurationWhenIdle
    );
    return;
  }

  registrationConfigurationRestartPending = false;
  registrationConfigurationRestartTask = (async () => {
    clearReconnectTimer();
    const previousRegisterer = registerer;
    const previousUserAgent = userAgent;
    registerer = undefined;
    registrationPromise = undefined;
    registrationReady = false;
    try {
      // dispose waits for the final unregister response; unregister() alone
      // only waits until the request has been sent.
      await previousRegisterer?.dispose();
    } catch (error) {
      emitWidgetEvent("registration_refresh_cleanup_failed", {
        code: "REGISTRATION_REFRESH_UNREGISTER_FAILED",
        message: error.message,
        recoverable: true
      });
    }
    if (liveCalls().length || hasLiveSipSession()) {
      registrationConfigurationRestartPending = true;
      return;
    }
    userAgent = undefined;
    transportConnected = false;
    stopTransportMonitor?.();
    try {
      await previousUserAgent?.stop();
    } catch (error) {
      // Keep the old agent/lease if shutdown was not confirmed. A later
      // explicit connect retries this refresh, not a second SIP registration.
      userAgent = previousUserAgent;
      registrationConfigurationRestartPending = true;
      emitWidgetEvent("registration_refresh_cleanup_failed", {
        code: "REGISTRATION_REFRESH_STOP_FAILED",
        message: error.message,
        recoverable: true
      });
      throw error;
    }
    // Keep the same-identity Web Lock across refresh. connectInternal releases
    // it only if the new registration identity is different.
    registrationConfigurationRestartPending = false;
  })()
    .finally(() => { registrationConfigurationRestartTask = undefined; });
  setDisconnectedUi("Обновляем настройки телефонии", true);
  try {
    await registrationConfigurationRestartTask;
    // Reconnect outside the teardown barrier: connect() also awaits it.
    if (desiredConnection) await connect();
    if (registrationConfigurationRestartPending) void restartRegistrationForConfigurationWhenIdle();
  } catch (error) {
    emitWidgetEvent("registration_refresh_failed", {
      code: "REGISTRATION_REFRESH_FAILED",
      message: error.message,
      recoverable: true
    });
  } finally {
    emitWidgetEvent("state", getSoftphoneState());
  }
}

async function connect() {
  const generation = connectionGeneration;
  if (ownershipYield || pageSuspended) throw new Error("Телефония завершает переключение вкладки");
  // A new connect may follow shutdown, but cannot reuse its half-stopped agent.
  if (disconnectPromise) await disconnectPromise;
  if (generation !== connectionGeneration) throw new Error("Подключение телефонии отменено");
  if (ownershipYield || pageSuspended) throw new Error("Телефония завершает переключение вкладки");
  desiredConnection = true;
  if (registrationConfigurationRestartPending) void restartRegistrationForConfigurationWhenIdle();
  while (registrationConfigurationRestartTask) {
    await registrationConfigurationRestartTask;
    checkConnectionAttempt(generation);
  }
  if (connectPromise) return connectPromise;
  if (isSipReady()) return;
  const existingAgent = userAgent;
  connectPromise = connectInternal(generation)
    .catch(async (error) => {
      // disconnect owns teardown of a canceled attempt and keeps its Web Lock
      // until that attempt (including start/REGISTER) has actually settled.
      if (!disconnectPromise && !existingAgent && !registrationReady && !registrationStandby) {
        await userAgent?.stop?.().catch(() => {});
        userAgent = undefined;
        registerer = undefined;
        transportConnected = false;
        if (!availabilityTask) releaseRegistrationLease();
      }
      if (isAuthInvalidError(error) && !availabilityTask && !registrationConfigurationRestartPending) desiredConnection = false;
      if (desiredConnection) scheduleReconnect();
      throw error;
    })
    .finally(() => {
      connectPromise = undefined;
      emitWidgetEvent("state", getSoftphoneState());
    });
  return connectPromise;
}

function checkConnectionAttempt(generation) {
  if (generation !== connectionGeneration || !desiredConnection || pageSuspended) {
    throw new Error("Подключение телефонии отменено");
  }
}

async function connectInternal(generation = connectionGeneration) {
  clearAuthInvalidState();
  clearReconnectTimer();
  checkConnectionAttempt(generation);
  if (userAgent) {
    if (isOnBreak()) return;
    const agent = userAgent;
    if (!transportConnected) await agent.reconnect();
    checkConnectionAttempt(generation);
    if (userAgent !== agent || !desiredConnection) throw new Error("Попытка подключения больше не актуальна");
    await ensureRegistered();
    checkConnectionAttempt(generation);
    return;
  }
  transportConnected = false;
  registrationReady = false;
  const extension = extensionEl.value.trim();
  const password = passwordEl.value;
  runtimeConfig.extension = extension;
  runtimeConfig.password = password;
  const domain = getSipDomain();
  const uri = makeSipUri(extension, domain);
  const authorizationUsername = runtimeConfig.authorizationUsername || runtimeConfig.authUser || extension;
  const acquired = await acquireRegistrationLease(`${domain}|${authorizationUsername}`);
  checkConnectionAttempt(generation);
  if (!acquired) {
    scheduleRegistrationLeaseRetry();
    return;
  }
  registrationStandby = false;

  if (isOnBreak()) {
    setConnected(false);
    setCallUiState("offline", "Перерыв");
    emitWidgetEvent("availability_changed", { availability: availabilityState() });
    broadcastOwnerState();
    return;
  }

  const agent = new SIP.UserAgent({
    uri,
    displayName: runtimeConfig.displayName || extension,
    authorizationUsername,
    authorizationPassword: password,
    sessionDescriptionHandlerFactory: runtimeSessionDescriptionHandlerFactory,
    transportOptions: {
      server: getWebSocketServer(),
      keepAliveInterval: 0
    },
    delegate: {
      onConnect: () => { if (userAgent === agent) handleTransportConnect(); },
      onDisconnect: (error) => { if (userAgent === agent) handleTransportDisconnect(error); },
      onInvite: (invitation) => {
        handleIncomingInvitation(invitation, userAgent === agent && desiredConnection && !disconnectPromise);
      }
    }
  });

  userAgent = agent;
  await agent.start();
  checkConnectionAttempt(generation);
  await ensureRegistered();
  checkConnectionAttempt(generation);
  if (saveUserEl.checked) {
    saveUserFields();
    updateAccountPanel(false);
  }
}

async function disconnect() {
  stopTransportMonitor?.();
  desiredConnection = false;
  connectionGeneration++;
  clearReconnectTimer();
  if (!disconnectPromise) {
    disconnectPromise = disconnectInternal().finally(() => { disconnectPromise = undefined; });
  }
  return disconnectPromise;
}

async function disconnectInternal() {
  // An in-flight refresh still owns the old SIP transport and its Web Lock.
  if (registrationConfigurationRestartTask) await registrationConfigurationRestartTask.catch(() => {});
  if (availabilityTask) await availabilityTask.catch(() => {});
  if (connectPromise) await connectPromise.catch(() => {});
  if (registrationPromise) await registrationPromise.catch(() => {});
  registrationConfigurationRestartPending = false;
  if (consultSession) await consultSession.bye?.().catch(() => consultSession.cancel?.().catch(() => {}));
  for (const call of liveCalls()) {
    selectedCallId = call.id;
    syncGlobalsFromCall(call);
    await hangup().catch(() => {});
  }
  if (registerer) await registerer.dispose().catch(() => {});
  try {
    if (userAgent) await userAgent.stop();
  } catch (error) {
    // An unconfirmed stop must not release ownership or allow a second agent.
    registrationConfigurationRestartPending = true;
    throw error;
  }
  userAgent = undefined;
  registerer = undefined;
  transportConnected = false;
  registrationReady = false;
  registrationPromise = undefined;
  releaseRegistrationLease();
  session = undefined;
  callActionLock = undefined;
  calls.clear();
  selectedCallId = "";
  activeCall = undefined;
  detachRemoteAudio();
  stopCallStatusMonitor();
  stopRingtone();
  stopWaitingTone();
  ringingCallId = "";
  stopAllIncomingAttention();
  stopRingback();
  stopCallTimer();
  muted = false;
  held = false;
  applyMediaState();
  updateControlButtons();
  setCallPhase("idle");
  setConnected(false);
  setCallUiState("offline", "Не подключен");
  setStatus("Не подключен");
  logEvent("offline");
  emitWidgetEvent("unregistered");
}

async function call(requestedNumber?: string, options: SoftphoneCallOptions = {}, commandSignal?: AbortSignal) {
  return callControl.run(() => callInternal(requestedNumber, options, commandSignal));
}

async function callInternal(requestedNumber?: string, options: SoftphoneCallOptions = {}, commandSignal?: AbortSignal) {
  commandSignal?.throwIfAborted();
  if (isOnBreak() || availabilityTask) throw new Error("Софтфон на перерыве. Сначала возобновите работу");
  const identity = callIdentity(options);
  if (!isSipReady()) throw new Error("Телефония переподключается. Дождитесь статуса «Готов»");
  requireCallCapacity();
  if (requestedNumber) numberEl.value = normalizeOutboundDialString(requestedNumber);
  const number = normalizeOutboundDialString(numberEl.value);
  if (!number) throw new Error("Введите номер");
  numberEl.value = number;
  identity.dialNumber = number;
  if (liveCalls().some((item) => item.direction === "outgoing" && item.phase === "calling" && item.phone === number)) {
    throw new Error("Вызов на этот номер уже устанавливается");
  }
  mediaCapture.checkAvailable();
  await requireKnownServerCalls(commandSignal);
  if (!isSipReady()) throw new Error("Телефония переподключается. Повторите действие после регистрации");
  mediaCapture.checkAvailable();
  requireCallCapacity();
  await holdOtherCalls();
  commandSignal?.throwIfAborted();
  if (!isSipReady()) throw new Error("Телефония переподключается. Повторите действие после регистрации");
  requireCallCapacity();
  const target = makeSipUri(number, getSipDomain());
  const outgoingSession = new SIP.Inviter(userAgent, target, mediaOfferOptions({ earlyMedia: true }));
  session = outgoingSession;
  sessionDelegate(outgoingSession);
  const outgoingCall = beginCall("outgoing", number, { ...identity, activeSession: outgoingSession, phase: "calling", select: true });
  const abortCommand = () => { void closeCallLocally(outgoingCall, "command_canceled").catch(() => {}); };
  commandSignal?.addEventListener("abort", abortCommand, { once: true });
  startCallSetupTimer(outgoingCall);
  setCallPhase("calling", number);
  setCallUiState("calling", "Отправляем вызов");
  setStatus("Отправляем вызов");
  logEvent("outgoing", outgoingCall.customerPhone || "");
  $("contactHint").textContent = "";
  $("screenPopStatus").textContent = "Отправлено";
  emitWidgetEvent("outgoing", { callId: outgoingCall.id, phone: number }, outgoingCall);
  try {
    await outgoingSession.invite({
      sessionDescriptionHandlerModifiers: [codecSdpModifier()],
      requestDelegate: {
        onTrying: (response) => handleOutgoingTrying(response, outgoingCall),
        onProgress: (response) => handleOutgoingProgress(response, outgoingCall),
        onReject: (response) => handleOutgoingReject(response, outgoingCall),
        onAccept: () => {
          if (!isCurrentCall(outgoingCall)) return;
          stopCallSetupTimer(outgoingCall);
          if (selectedCallId === outgoingCall.id) stopRingback();
        }
      }
    });
  } catch (error) {
    if (outgoingCall.setupTimedOut) return;
    const failedCall = outgoingCall;
    if (canPresentCallFailure(failedCall)) stopRingback();
    if (failedCall?.localCancelRequested) {
      failedCall.sipStatusCode = failedCall.sipStatusCode || 487;
      failedCall.sipReason = "Canceled locally";
      failedCall.failureMessage = "";
      emitOutgoingCanceled(failedCall, failedCall.sipStatusCode);
      if (calls.has(failedCall.id)) removeCall(failedCall);
      if (!liveCalls().length) {
        setCallPhase("idle");
        setCallUiState(isSipReady() ? "registered" : "offline", isSipReady() ? "Готов" : "Не подключен");
        setStatus(isSipReady() ? "Готов" : "Не подключен");
      }
      return;
    }
    if (!isCurrentCall(failedCall)) return;
    const message = inviteFailureMessage(error);
    if (failedCall) {
      failedCall.failureEvidence = { failureSource: "local_error" };
      failedCall.failureMessage = message;
      failedCall.sipReason = message;
      rememberCall(failedCall);
      finishCall(failedCall);
      if (!liveCalls().some((item) => item.id !== failedCall.id)) showCallFailure(message);
      emitOutgoingFailed(failedCall, message, 0);
      removeCall(failedCall);
    } else {
      showCallFailure(message);
    }
    if (!liveCalls().length) setCallPhase("idle");
    throw new Error(message);
  } finally {
    commandSignal?.removeEventListener("abort", abortCommand);
  }
}

async function answer(options = {} as any, commandSignal?: AbortSignal) {
  const target = options.callId ? calls.get(options.callId) : selectedCall();
  if (!target || target.phase !== "incoming") return;
  return callControl.run(() => answerInternal({ ...options, callId: target.id }, commandSignal));
}

async function answerInternal(options = {} as any, commandSignal?: AbortSignal) {
  commandSignal?.throwIfAborted();
  const call = options.callId ? calls.get(options.callId) : selectedCall();
  if (!isCurrentCall(call)) return;
  if (call.phase !== "incoming") return;
  // A hidden owner's incoming call must keep ringing, not be rejected just
  // because Answer was pressed in another tab. Ask the operator to open it.
  try { mediaCapture.checkAvailable(); }
  catch (error) {
    diagnosticQueue.record("microphone_failed", { ...browserDiagnosticContext(), callId: call.id,
      code: error?.code, errorName: error?.name, elapsedMs: 0 });
    throw error;
  }
  const answeringSession = call.session;
  const abortCommand = () => { void closeCallLocally(call, "command_canceled").catch(() => {}); };
  commandSignal?.addEventListener("abort", abortCommand, { once: true });
  // Claim the incoming call before awaiting hold/media to suppress duplicate answers.
  call.phase = "answering";
  reconcileRinging();
  startCallSetupTimer(call);
  let accepting = false;
  try {
    if (options.endOutgoingCallId) {
      const outgoing = calls.get(options.endOutgoingCallId);
      if (!isCurrentCall(outgoing) || outgoing.direction !== "outgoing" || outgoing.phase !== "calling") {
        throw new Error("Состояние исходящего изменилось. Проверьте звонки перед ответом");
      }
      await callSetup(answeringSession).run(() => closeCallLocally(outgoing, "answer_waiting"));
    }
    await callSetup(answeringSession).run(() => holdOtherCalls(call));
    commandSignal?.throwIfAborted();
    // Keep the current conversation selected if its hold is rejected. Selecting
    // the incoming line is part of a confirmed switch, not the initial click.
    await selectCall(call.id, { attach: false });
    callSetup(answeringSession).check();
    stopIncomingAttention(call.id);
    rememberCall(call);
    syncGlobalsFromCall(call);
    setCallPhase("answering", call.phone || incomingPhone(answeringSession));
    setCallUiState("answering", "Соединяем");
    setStatus("Соединяем");
    emitWidgetEvent("answering", { callId: call.id, phone: call.phone || incomingPhone(answeringSession) });
    accepting = true;
    await callSetup(answeringSession).run(() => answeringSession.accept(mediaOfferOptions()));
  } catch (error) {
    if (call.setupTimedOut) return;
    if (!callSetup(answeringSession).controller.signal.aborted && calls.has(call.id)) {
      if (accepting) await closeCallLocally(call, "answer_failed");
      else {
        call.phase = "incoming";
        rememberCall(call);
        if (selectedCallId === call.id) syncGlobalsFromCall(call);
        reconcileRinging();
        emitWidgetEvent("state", getSoftphoneState());
      }
      throw error;
    }
  } finally {
    stopCallSetupTimer(call);
    commandSignal?.removeEventListener("abort", abortCommand);
  }
}

async function primaryCallAction() {
  if (callPhase === "incoming") {
    await answer();
    return;
  }
  // This legacy dial input contains a ready-to-dial target, not separately supplied
  // customer identity. DoCRM's product dialer supplies customerPhone via client.call.
  await executeCoordinatedCommand("call", [numberEl.value, { customerPhone: null }]);
}

function payloadFromMessage(data = {} as any) {
  return data.detail || data.payload || data.config || data;
}

function setWidgetMinimized(minimized) {
  widgetEl.classList.toggle("is-minimized", minimized);
  $("minimizeWidget").setAttribute("aria-label", minimized ? "Развернуть" : "Свернуть");
  emitWidgetEvent(minimized ? "widget_minimized" : "widget_restored", { minimized });
  return getSoftphoneState();
}

async function runCommand(command, payload = {} as any) {
  if (command === "configure") {
    configureWidget(payload);
    return getSoftphoneState();
  }
  if (command === "connect") return connect();
  if (command === "disconnect") return disconnect();
  if (command === "takeControl") return takeControl();
  if (command === "setBreak") return setBreak(payload.enabled);
  if (command === "call") return executeCoordinatedCommand("call", [
    payload.dialNumber ?? payload.number ?? payload.phone ?? payload.phoneNumber,
    { customerPhone: payload.customerPhone }
  ]);
  if (command === "answer") return answer(payload);
  if (command === "hangup") return hangup(payload);
  if (command === "mute") return toggleMute(payload);
  if (command === "hold") return toggleHold(payload);
  if (command === "selectCall") return selectCall(payload.callId);
  if (command === "activateCall") return activateCall(payload.callId);
  if (command === "sendDtmf") return sendDtmf(payload);
  if (command === "setAudioDevices") return setAudioDevices(payload);
  if (command === "getAudioDevices") return refreshAudioDevices({ requestPermission: Boolean(payload.requestPermission) });
  if (command === "setNotifications") return setNotifications(payload.enabled !== false);
  if (command === "setCallOutcomes") {
    runtimeConfig.callOutcomes = normalizeCallOutcomeConfig(payload);
    emitWidgetEvent("configured", { callOutcomeMode: runtimeConfig.callOutcomes.mode });
    return getSoftphoneState();
  }
  if (command === "submitCallOutcome") {
    const call = payload.call || (payload.callId ? { ...payload, id: payload.callId } : activeOutcomeCall);
    return submitCallOutcome(payload.outcome || payload, call);
  }
  if (command === "setConferenceState") return setConferenceState(payload);
  if (command === "endConference" || command === "clearConference") return endConferenceState(payload.callId || selectedCallId);
  if (command === "conferenceParticipantLeft") return conferenceParticipantLeft(payload);
  if (command === "conference") return conferenceCalls(payload);
  if (command === "transfer") return transfer(payload.target || payload, { ...payload, ...payload.options });
  if (command === "confirmTransfer") return confirmAttendedTransfer();
  if (command === "cancelTransfer") return cancelAttendedTransfer(payload.reason || "Перевод отменен");
  if (command === "setTransferTargets") return setTransferTargets(payload.targets || payload);
  if (command === "minimize") return setWidgetMinimized(true);
  if (command === "restore") return setWidgetMinimized(false);
  if (command === "getState") return coordinatedSoftphoneState();
  throw new Error(`Unknown command: ${command}`);
}

function initHostCommands() {
  window.addEventListener("message", (event) => {
    const data = event.data || {};
    if (data.source === "docrm-softphone") return;
    if (!isAllowedMessageOrigin(event.origin)) {
      emitWidgetEvent("error", {
        command: "message",
        code: "MESSAGE_ORIGIN_DENIED",
        message: "Недоверенный источник команды",
        recoverable: true
      });
      return;
    }
    if (event.origin && event.origin !== "null") acceptedParentOrigin = event.origin;
    const command =
      data.command ||
      (String(data.type || "").startsWith("docrm-softphone:") ? String(data.type).split(":").pop() : "") ||
      ([
        "configure",
        "connect",
        "disconnect",
        "call",
        "answer",
        "hangup",
        "mute",
        "hold",
        "selectCall",
        "activateCall",
        "sendDtmf",
        "setAudioDevices",
        "getAudioDevices",
        "setNotifications",
        "setCallOutcomes",
        "submitCallOutcome",
        "endConference",
        "conference",
        "transfer",
        "confirmTransfer",
        "cancelTransfer",
        "setTransferTargets",
        "getState"
      ].includes(data.type) ? data.type : "");
    if (!command) return;
    runCommand(command, payloadFromMessage(data))
      .then((result) => {
        if (command === "getState" && event.source) {
          (event.source as WindowProxy).postMessage({
            source: "docrm-softphone",
            type: "state",
            requestId: data.requestId,
            detail: result
          }, event.origin || parentTargetOrigin());
        }
      })
      .catch((error) => emitWidgetEvent("error", {
        command,
        code: "COMMAND_FAILED",
        message: error.message,
        recoverable: true
      }));
  });
}

async function hangup(options = {} as any) {
  const call = options.callId ? calls.get(options.callId) : selectedCall();
  const callId = call?.id || options.callId;
  const requestId = crypto.randomUUID(), started = performance.now();
  let hangupMethod = "none";
  const observe = (commandPhase, error?) => {
    try {
      diagnosticQueue.record("hangup_command", { ...callMediaDiagnosticContext(call?.session),
        callId, linkedId: call?.linkedId, callPhase: call?.phase, requestId, commandPhase, hangupMethod,
        elapsedMs: Math.round(performance.now() - started), errorName: error?.name });
    } catch { /* Diagnostics must not affect the command or hide its error. */ }
  };
  observe("received");
  if (!isCurrentCall(call)) { observe("ignored"); return; }
  try {
    const activeSession = call.session;
    if (selectedCallId === call.id) stopRingback();
    if (activeSession.state === SIP.SessionState.Established) {
      hangupMethod = "bye";
      const previousPhase = call.phase;
      call.phase = "terminating";
      applyMediaState();
      try { await activeSession.bye(); }
      catch (error) {
        if (isCurrentCall(call)) { call.phase = previousPhase; applyMediaState(); }
        throw error;
      }
    } else if (["incoming", "calling", "answering"].includes(call.phase)) {
      hangupMethod = call.direction === "incoming" ? "reject" : "cancel";
      await closeCallLocally(call, "local_hangup");
    } else {
      observe("ignored");
      return;
    }
    // Handler completion is not a remote SIP acknowledgement. Terminal events
    // and call_cleanup_failed remain the evidence of termination/cleanup.
    observe("completed");
  } catch (error) {
    observe("failed", error);
    throw error;
  }
}

async function toggleMute(options = {} as any) {
  const call = options.callId ? calls.get(options.callId) : selectedCall();
  if (!isCurrentCall(call)) return;
  call.muted = !call.muted;
  rememberCall(call);
  if (selectedCallId === call.id) syncGlobalsFromCall(call);
  applyMediaState();
  updateControlButtons();
  emitWidgetEvent(call.muted ? "muted" : "unmuted", { callId: call.id });
}

async function toggleHold(options = {} as any) {
  return callControl.run(() => toggleHoldInternal(options));
}

async function toggleHoldInternal(options = {} as any) {
  const call = options.callId ? calls.get(options.callId) : selectedCall();
  if (!isCurrentCall(call)) return;
  if (!call?.session || call.session.state !== SIP.SessionState.Established) return;
  if (call.held) { await holdOtherCalls(call); requireCurrentCall(call); }
  await setSipHold(!call.held, { call, emit: true });
}

function openTransferPanel() {
  openTargetPanel("transfer");
}

function toggleCallActionsMenu() {
  if (!session || session.state !== SIP.SessionState.Established) return;
  if (callHasActiveConference()) return;
  const menu = $("callActionsMenu");
  const opened = menu.hidden;
  $("inviteAction").hidden = !featureEnabled("conference");
  $("transferAction").hidden = !featureEnabled("transfer");
  if (opened && !featureEnabled("transfer") && !featureEnabled("conference")) return;
  menu.hidden = !opened;
  $("callActions").classList.toggle("is-active", opened);
}

async function sendDtmf(tone) {
  const requestedCallId = typeof tone === "object" ? tone.callId : undefined;
  if (requestedCallId && !isCurrentCall(calls.get(requestedCallId))) return;
  if (typeof tone === "object") {
    tone = tone.tone || tone.digit || tone.key;
  }
  if (!tone) throw new Error("Не указан тон");
  const call = requestedCallId ? calls.get(requestedCallId) : selectedCall();
  if (requestedCallId && !isCurrentCall(call)) return;
  if (call?.session && call.session.state === SIP.SessionState.Established) {
    if (call.held) throw new Error("Сначала верните звонок с удержания");
    await call.session.info({
      requestOptions: {
        body: {
          contentDisposition: "render",
          contentType: "application/dtmf-relay",
          content: `Signal=${tone}\r\nDuration=200`
        }
      }
    });
    emitWidgetEvent("dtmf", { callId: call.id, tone });
    return;
  }
  numberEl.value += tone;
  updateClearNumberButton();
}

async function loadWidgetConfig() {
  const initialBootConfig = bootConfig();
  let configuredFromServer = false;
  const shouldFetchServerConfig = !isEmbedMode() || hasExplicitWidgetConfigIdentity();
  if (shouldFetchServerConfig) {
    try {
      const response = await fetch(apiUrl(widgetConfigUrl()), { headers: widgetApiHeaders() });
      if (response.ok) {
        configureWidget({
          ...await response.json(),
          ...initialBootConfig
        });
        configuredFromServer = true;
      }
    } catch {
      // Standalone mode may still be configured manually after startup.
    }
  }
  if (!configuredFromServer && Object.keys(initialBootConfig).length) configureWidget(initialBootConfig);
  applySavedUser();
  applyTransferTypePreference();
  applyOutcomeModePreference();
  refreshAudioDevices().catch(() => {});
  emitWidgetEvent("ready", getSoftphoneState());
  if (readBoolean(runtimeConfig.autoConnect, false) && !userAgent) {
    connect().catch(handleConnectError);
  }
}

function coordinationState() {
  const fresh = Boolean(mirroredOwnerId && Date.now() - mirroredOwnerStateAt < REGISTRATION_LEASE_TTL_MS);
  return {
    role: registrationStandby ? "standby" : ownsRegistrationLease() ? "owner" : "disconnected",
    tabId: TAB_INSTANCE_ID,
    ownerTabId: registrationStandby ? mirroredOwnerId || null : ownsRegistrationLease() ? TAB_INSTANCE_ID : null,
    responsive: registrationStandby ? fresh : !pageSuspended,
    ownerVisible: registrationStandby ? (fresh ? mirroredOwnerState?.coordination?.ownerVisible ?? null : null) : document.visibilityState === "visible",
    busy: registrationStandby ? (fresh ? Boolean(mirroredOwnerState?.coordination?.busy) : true) :
      Boolean(callControl.busy || ownershipYield || availabilityTask || registrationConfigurationRestartTask || connectPromise || disconnectPromise),
    switching: Boolean(ownershipYield || controlClaimTask)
  };
}

function getSoftphoneState() {
  const call = selectedCall();
  return {
    diagnostics: diagnosticQueue.status(),
    coordination: coordinationState(),
    availability: availabilityState(),
    state: callStateEl.dataset.state,
    callPhase,
    callId: call?.id || null,
    ...callIdentity(call),
    linkedId: call?.linkedId || "",
    selectedCallId: selectedCallId || null,
    activeMediaCallId: activeMediaCallId || null,
    direction: call?.direction || null,
    extension: extensionEl.value.trim(),
    authUser: runtimeConfig.authorizationUsername || runtimeConfig.authUser || extensionEl.value.trim(),
    provider: runtimeConfig.provider || "",
    telephonyProfileId: runtimeConfig.telephonyProfileId || "",
    telephonyKey: runtimeConfig.telephonyKey || "",
    hasSipProfile: Boolean(runtimeConfig.hasSipProfile),
    userId: runtimeConfig.userId || "",
    number: call?.phone || activePhone || numberEl.value.trim(),
    connected: isSipReady(),
    transportConnected,
    registrationState: registrationReady ? "registered" : registrationStandby ? "standby" : desiredConnection ? "reconnecting" : "unregistered",
    muted: Boolean(call?.muted),
    held: Boolean(call?.held),
    conference: call?.conference || null,
    audio: {
      inputDeviceId: runtimeConfig.audioInputDeviceId || localStorage.getItem(AUDIO_INPUT_KEY) || "",
      outputDeviceId: runtimeConfig.audioOutputDeviceId || localStorage.getItem(AUDIO_OUTPUT_KEY) || "",
      outputSelectionSupported: typeof remoteAudio.setSinkId === "function"
    },
    notifications: {
      supported: "Notification" in window,
      permission: "Notification" in window ? Notification.permission : "unsupported",
      enabled: notificationAllowed()
    },
    ringer: ringerState(),
    callOutcomes: outcomeConfig(),
    calls: liveCalls().map((item) => ({
      id: item.id,
      linkedId: item.linkedId || "",
      direction: item.direction,
      phone: item.phone,
      ...callIdentity(item),
      phase: item.phase,
      conference: item.conference || null,
      sipStatusCode: item.sipStatusCode || 0,
      sipReason: item.sipReason || "",
      muted: Boolean(item.muted),
      held: Boolean(item.held),
      holdPending: Boolean(item.holdOperation),
      holdUnconfirmed: Boolean(item.holdOperation?.timedOut),
      mediaRecovering: Boolean(item.holdOperation?.iceRestart),
      ringerSilenced: Boolean(item.ringerSilenced),
      transfer: item.transfer || null,
      selected: item.id === selectedCallId,
      activeMedia: item.id === activeMediaCallId,
      startedAt: item.createdAt || null,
      progressAt: item.progressAt || null,
      answeredAt: item.answeredAt || null,
      endedAt: item.endedAt || null,
      durationSec: durationSec(item)
    })),
    transferMode: runtimeConfig.transferMode || "auto",
    transferType: activeTransferType(),
    features: runtimeConfig.features || {},
    transferInProgress,
    transferFlow: transferFlow ? {
      type: transferFlow.payload?.transferType || "attended",
      targetExtension: transferFlow.targetExtension,
      consulting: Boolean(consultSession)
    } : null,
    startedAt: call?.createdAt || null,
    progressAt: call?.progressAt || null,
    answeredAt: call?.answeredAt || null,
    endedAt: call?.endedAt || null,
    durationSec: durationSec(call)
  };
}

function exposeApi() {
  window.DoCRMSoftphone = {
    connect: () => executeCoordinatedCommand("connect"),
    disconnect: () => executeCoordinatedCommand("disconnect"),
    takeControl: () => executeCoordinatedCommand("takeControl"),
    setBreak: (enabled) => executeCoordinatedCommand("setBreak", [enabled]),
    call: (...args) => executeCoordinatedCommand("call", args),
    answer: (...args) => executeCoordinatedCommand("answer", args),
    hangup: (...args) => executeCoordinatedCommand("hangup", args),
    mute: (...args) => executeCoordinatedCommand("mute", args),
    hold: (...args) => executeCoordinatedCommand("hold", args),
    selectCall: (...args) => executeCoordinatedCommand("selectCall", args),
    activateCall: (...args) => executeCoordinatedCommand("activateCall", args),
    sendDtmf: (...args) => executeCoordinatedCommand("sendDtmf", args),
    setAudioDevices: (...args) => executeCoordinatedCommand("setAudioDevices", args),
    getAudioDevices: (...args) => executeCoordinatedCommand("getAudioDevices", args),
    silenceRinger: (...args) => executeCoordinatedCommand("silenceRinger", args),
    getBrowserPermissions,
    requestBrowserPermission,
    setNotifications: (...args) => executeCoordinatedCommand("setNotifications", args),
    setCallOutcomes: (config) => {
      runtimeConfig.callOutcomes = normalizeCallOutcomeConfig(config);
      return getSoftphoneState();
    },
    submitCallOutcome: (...args) => executeCoordinatedCommand("submitCallOutcome", args),
    setConferenceState: (...args) => executeCoordinatedCommand("setConferenceState", args),
    endConference: (...args) => executeCoordinatedCommand("endConference", args),
    clearConference: (...args) => executeCoordinatedCommand("clearConference", args),
    conferenceParticipantLeft: (...args) => executeCoordinatedCommand("conferenceParticipantLeft", args),
    conference: (...args) => executeCoordinatedCommand("conference", args),
    transfer: (...args) => executeCoordinatedCommand("transfer", args),
    confirmTransfer: (...args) => executeCoordinatedCommand("confirmTransfer", args),
    cancelTransfer: (...args) => executeCoordinatedCommand("cancelTransfer", args),
    setTransferTargets: (...args) => executeCoordinatedCommand("setTransferTargets", args),
    minimize: () => setWidgetMinimized(true),
    restore: () => setWidgetMinimized(false),
    configure: configureWidget,
    getState: coordinatedSoftphoneState
  };
}

function handlePageExit() {
  if (pageSuspended) return pageExitTask;
  pageSuspended = true;
  pageWasConnected = desiredConnection;
  desiredConnection = false;
  ownershipYield?.abort();
  clearReconnectTimer();
  clearTimeout(registrationLeaseRetryTimer);
  registrationLeaseRetryTimer = undefined;
  // Start SIP teardown while pagehide still runs. Waiting for registration or
  // another call first can let the document disappear before any BYE is sent.
  const exitHangups = liveCalls().map(call => hangup({ callId: call.id }).catch(() => {}));
  diagnosticQueue.dispose();
  stopAllIncomingAttention();
  stopCallStatusMonitor();
  stopConferenceStatusMonitor();
  for (const call of liveCalls()) {
    callSetups.get(call.session)?.abort();
    call.session?.sessionDescriptionHandler?.close?.();
  }
  clearInterval(tabCoordinationHeartbeatTimer);
  tabCoordinationHeartbeatTimer = undefined;
  tabRpc.reset();
  tabCoordinationChannel?.close();
  tabCoordinationChannel = undefined;
  // Keep the Web Lock until SIP teardown finishes. Closing the document itself
  // releases it in the browser; a BFCache restore must await the same teardown.
  pageExitTask = Promise.allSettled(exitHangups)
    .then(() => disconnect()).catch(() => {}).finally(() => { pageExitTask = undefined; });
  return pageExitTask;
}

async function handlePageReturn(event) {
  if (!event.persisted || !pageSuspended) return;
  const resume = pageWasConnected;
  await pageExitTask;
  pageSuspended = false;
  initTabCoordination();
  configureDiagnosticQueue(true);
  if (resume) connect().catch(handleConnectError);
}

$("connect").addEventListener("click", () => connect().catch(handleConnectError));
$("disconnect").addEventListener("click", () => disconnect().catch((error) => setStatus(error.message)));
$("accountAction").addEventListener("click", () => {
  const saved = canShowLocalSettings() && (hasSavedUser() || saveUserEl.checked);
  if (!userAgent) {
    connect().catch(handleConnectError);
    return;
  }
  disconnect()
    .catch((error) => setStatus(error.message))
    .finally(() => {
      if (!saved) updateAccountPanel(true);
    });
});
$("call").addEventListener("click", () => primaryCallAction().catch((error) => setStatus(error.message)));
$("hangup").addEventListener("click", () => hangup().catch((error) => setStatus(error.message)));
$("mute").addEventListener("click", () => toggleMute().catch((error) => setStatus(error.message)));
$("hold").addEventListener("click", () => toggleHold().catch((error) => setStatus(error.message)));
$("callActions").addEventListener("click", toggleCallActionsMenu);
$("inviteAction").addEventListener("click", () => openTargetPanel("conference"));
$("transferAction").addEventListener("click", () => {
  if (selectedCall()?.conference?.active) {
    showToast("Перевод недоступен во время конференции");
    closeCallActionsMenu();
    return;
  }
  openTransferPanel();
});
$("transferClose").addEventListener("click", closeTransferPanel);
$("transferSearch").addEventListener("input", (event) => {
  transferSearchQuery = event.target.value || "";
  renderTransferTargets(transferTargets);
});
$("transferConfirm").addEventListener("click", () => confirmAttendedTransfer().catch((error) => {
  showToast(error.message);
  setStatus(error.message);
}));
$("transferCancel").addEventListener("click", () => cancelAttendedTransfer("Перевод отменен").catch((error) => {
  showToast(error.message);
  setStatus(error.message);
}));
$("togglePassword").addEventListener("click", togglePasswordVisibility);
$("keypadToggle").addEventListener("click", () => {
  const keypadPanel = runtimeQuerySelector(".keypad-panel");
  if (keypadPanel) setKeypadCollapsed(!keypadPanel.classList.contains("is-collapsed"));
});
$("clearNumber").addEventListener("click", () => {
  numberEl.value = "";
  $("contactHint").textContent = "";
  updateClearNumberButton();
  numberEl.focus();
});
$("enableNotifications").addEventListener("click", () => enableNotifications().catch((error) => setStatus(error.message)));
$("saveUser").addEventListener("change", () => {
  if (!canShowLocalSettings()) return;
  if (saveUserEl.checked) {
    showToast("Пользователь сохранится после подключения");
    return;
  }
  localStorage.removeItem(SAVED_USER_KEY);
  updateAccountPanel(true);
  showToast("Пользователь не сохраняется");
});
$("transferType").addEventListener("change", () => {
  const type = normalizeTransferType(transferTypeEl.value, "blind");
  runtimeConfig.transferType = type;
  if (canShowLocalSettings()) localStorage.setItem(TRANSFER_TYPE_KEY, type);
  emitWidgetEvent("configured", { transferType: type });
});
callOutcomeModeEl?.addEventListener("change", () => {
  const mode = normalizeCallOutcomeMode(callOutcomeModeEl.value, "off");
  runtimeConfig.callOutcomes = normalizeCallOutcomeConfig({
    ...runtimeConfig,
    callOutcomes: {
      ...(runtimeConfig.callOutcomes || {}),
      mode
    }
  });
  if (canShowLocalSettings()) localStorage.setItem("softphoneCallOutcomeMode", mode);
  emitWidgetEvent("configured", { callOutcomeMode: mode });
});
audioInputEl?.addEventListener("focus", () => refreshAudioDevices({ requestPermission: true }).catch((error) => {
  emitWidgetEvent("audio_device_failed", { code: "AUDIO_INPUT_PERMISSION_FAILED", message: error.message, recoverable: true });
}));
audioOutputEl?.addEventListener("focus", () => refreshAudioDevices().catch(() => {}));
audioInputButtonEl?.addEventListener("click", () => toggleDeviceDropdown("input").catch((error) => {
  emitWidgetEvent("audio_device_failed", { code: "AUDIO_INPUT_PERMISSION_FAILED", message: error.message, recoverable: true });
}));
audioOutputButtonEl?.addEventListener("click", () => toggleDeviceDropdown("output").catch((error) => {
  emitWidgetEvent("audio_device_failed", { code: "AUDIO_OUTPUT_LIST_FAILED", message: error.message, recoverable: true });
}));
audioInputEl?.addEventListener("change", () => setAudioDevices({ audioInputDeviceId: audioInputEl.value }).catch((error) => {
  emitWidgetEvent("audio_device_failed", { code: "AUDIO_INPUT_SELECT_FAILED", message: error.message, recoverable: true });
}));
audioOutputEl?.addEventListener("change", () => setAudioDevices({ audioOutputDeviceId: audioOutputEl.value }).catch((error) => {
  emitWidgetEvent("audio_device_failed", { code: "AUDIO_OUTPUT_SELECT_FAILED", message: error.message, recoverable: true });
}));
outcomeSelectButtonEl?.addEventListener("click", () => {
  const opened = outcomeOptionsEl.hidden;
  outcomeOptionsEl.hidden = !opened;
  outcomeSelectButtonEl.setAttribute("aria-expanded", String(opened));
});
outcomeSubmitEl?.addEventListener("click", () => submitCallOutcome().catch((error) => showToast(error.message)));
$("outcomeSkip")?.addEventListener("click", skipCallOutcome);
$("settingsToggle").addEventListener("click", () => {
  const dropdown = $("settingsDropdown");
  const opened = dropdown.hidden;
  dropdown.hidden = !opened;
  $("settingsToggle").classList.toggle("is-active", opened);
  $("settingsToggle").setAttribute("aria-label", opened ? "Закрыть настройки" : "Настройки");
});
document.addEventListener("pointerdown", (event) => {
  if (window.__DOCRMSoftphoneHeadless) return;

  const dropdown = $("settingsDropdown");
  const toggle = $("settingsToggle");
  const target = event.target as Element;
  if (!dropdown.hidden && !dropdown.contains(target) && !toggle.contains(target)) closeSettings();
  if (!$("callActionsMenu").hidden && !target.closest("#callActionsMenu") && !target.closest("#callActions")) closeCallActionsMenu();
  if (!target.closest(".device-select")) closeDeviceDropdowns();
  if (!outcomeOptionsEl.hidden && !target.closest(".outcome-select-wrap")) {
    outcomeOptionsEl.hidden = true;
    outcomeSelectButtonEl.setAttribute("aria-expanded", "false");
  }
});
$("minimizeWidget").addEventListener("click", () => {
  setWidgetMinimized(!widgetEl.classList.contains("is-minimized"));
});
runtimeQuerySelectorAll(".dial-key").forEach((button) => {
  button.addEventListener("click", () => sendDtmf((button as HTMLElement).dataset.key).catch((error) => setStatus(error.message)));
});
numberEl.addEventListener("input", () => {
  numberEl.value = normalizeDialString(numberEl.value);
  updateClearNumberButton();
});
extensionEl.addEventListener("input", clearAuthInvalidState);
passwordEl.addEventListener("input", clearAuthInvalidState);
window.addEventListener("online", retryConnectionWhenBrowserReturns);
window.addEventListener("storage", event => {
  if (event.storageArea !== localStorage || (event.key !== "softphoneNotifications" && event.key !== null)) return;
  notificationsEnabled = localStorage.getItem("softphoneNotifications") !== "off";
  updateNotificationButton();
  if (!notificationsEnabled) closeAllIncomingNotifications();
  refreshIncomingAttention();
  void getBrowserPermissions().then(permissions => emitWidgetEvent("permissions_changed", { permissions }))
    .catch(error => emitWidgetEvent("error", { code: "PERMISSIONS_READ_FAILED", message: error.message, recoverable: true }));
});
document.addEventListener("resume", () => {
  if (ownsRegistrationLease()) broadcastOwnerState();
  else if (registrationStandby) requestOwnerState();
  retryConnectionWhenBrowserReturns();
  refreshIncomingAttention();
  void checkActiveCallStatus();
});
window.addEventListener("pagehide", handlePageExit);
window.addEventListener("pageshow", handlePageReturn);
window.addEventListener("focus", refreshIncomingAttention);
window.addEventListener("blur", refreshIncomingAttention);
window.addEventListener("docrm:page-title", (event: CustomEvent<string>) => {
  if (!incomingAttentionPhones.size || typeof event.detail !== "string") return;
  titleBeforeIncoming = event.detail;
  if (!isPageForeground()) {
    document.title = titleFlashActive ? INCOMING_DOCUMENT_TITLE : titleBeforeIncoming;
    lastAttentionTitle = document.title;
  }
});
document.addEventListener("visibilitychange", () => {
  if (ownsRegistrationLease()) broadcastOwnerState();
  else if (registrationStandby) requestOwnerState();
  retryConnectionWhenBrowserReturns();
  refreshIncomingAttention();
});

initHostCommands();
exposeApi();
initTabCoordination();
loadWidgetConfig();
updateNotificationButton();
updateClearNumberButton();
updateControlButtons();
applyKeypadPreference();
